import { IModule, ModuleContent, ModuleParams } from '../types';
import {
  PdfFont,
  PdfBrush,
  PdfSize,
  ETextAlign,
  PdfRect,
  PageGen,
  PdfColor,
  PdfPen,
  PdfPoint,
} from '../../pdf/pdf';
import { getPalette, getPaletteColors, Palette } from '../../pdf/palette';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';

type ChartType =
  | 'bar'
  | 'line'
  | 'pie'
  | 'column'
  | 'area'
  | 'donut'
  | 'lollipop'
  | 'step'
  | 'histogram'
  | 'funnel'
  | 'pyramid'
  | 'stacked-column'
  | 'stacked-bar'
  | 'dumbbell'
  | 'slope'
  | 'progress'
  | 'radar'
  | 'waffle'
  | 'waterfall'
  | 'concentric'
  | 'timeline'
  | 'venn'
  | 'scatter'
  | 'bubble'
  | 'heatmap'
  | 'box-plot'
  | 'population-pyramid'
  | 'treemap'
  | 'rose'
  | 'strip'
  | 'icon-array'
  | 'bullet'
  | 'parallel-coords'
  | 'gantt'
  | 'dot-map'
  | 'network'
  | 'marimekko'
  | 'streamgraph'
  | 'sunburst'
  | 'arc-diagram'
  | 'sankey'
  | 'calendar-heatmap'
  | 'chord'
  | 'spiral'
  | 'word-cloud'
  | 'pictogram'
  | 'gauge'
  | 'candlestick'
  | 'spline'
  | 'smooth-area'
  | 'number-line'
  | 'fraction-bar'
  | 'pie-grid'
  | 'violin-plot'
  | 'dot-plot'
  | 'ten-frame'
  | 'tally-chart'
  | 'place-value'
  | 'fraction-circles'
  | 'multiplication-grid';

interface DataPoint {
  label: string;
  value: number;
  rawLabel?: string; // original date/text for timeline display
}

interface MultiDataPoint {
  label: string;
  values: number[];
}

interface MultiTableData {
  headers: string[];
  rows: MultiDataPoint[];
}

const MULTI_TYPES: ChartType[] = [
  'stacked-column',
  'stacked-bar',
  'dumbbell',
  'slope',
  'progress',
  'scatter',
  'bubble',
  'heatmap',
  'box-plot',
  'population-pyramid',
  'bullet',
  'parallel-coords',
  'gantt',
  'dot-map',
  'marimekko',
  'streamgraph',
  'chord',
  'candlestick',
  'fraction-bar',
  'pie-grid',
  'violin-plot',
];

interface NetworkData {
  nodes: string[];
  edges: [number, number][];
}

interface SunburstGroup {
  label: string;
  total: number;
  children: { label: string; value: number }[];
}

interface ArcData {
  nodes: string[];
  edges: { from: number; to: number; weight: number }[];
}

export const chartModule: IModule = {
  name: 'chart',

  parse(source: string, attrs: Record<string, string>): ModuleContent {
    const type = (attrs.type || 'column') as ChartType;
    const content = MULTI_TYPES.includes(type)
      ? JSON.stringify(parseMultiChartData(source))
      : type === 'timeline'
        ? JSON.stringify(parseTimelineData(source))
        : type === 'network'
          ? JSON.stringify(parseNetworkData(source))
          : type === 'sunburst'
            ? JSON.stringify(parseSunburstData(source))
            : type === 'arc-diagram' || type === 'sankey'
              ? JSON.stringify(parseArcData(source))
              : JSON.stringify(parseChartData(source));

    return {
      type: 'chart',
      attrs: { ...attrs, chartType: type },
      content,
    };
  },

  measure(content: ModuleContent, params: ModuleParams): PdfSize {
    const width = parseFloat(content.attrs.width) || 150;
    const height = parseFloat(content.attrs.height) || 100;
    return new PdfSize(width, height);
  },

  draw(content: ModuleContent, rect: PdfRect, align: ETextAlign, params: ModuleParams): void {
    const page = params.page!;
    if (!page) {
      throw new Error('PageGen required for chart drawing');
    }

    const chartType = (content.attrs.type || 'column') as ChartType;
    const paletteName = content.attrs.palette || 'soft';
    const palette = getPalette(paletteName);

    const noData = (msg = '[No chart data]') => {
      const font = params.font || new PdfFont(PdfFont.FONT_NAME, 10);
      page.drawString(msg, font, PdfBrush.LightGray, null, rect, ETextAlign.Center);
    };

    try {
      if (MULTI_TYPES.includes(chartType)) {
        const td: MultiTableData = JSON.parse(content.content || '{"headers":[],"rows":[]}');
        if (td.rows.length === 0) {
          noData();
          return;
        }

        switch (chartType) {
          case 'stacked-column':
            drawStackedColumnChart(page, rect, td.rows, td.headers, content.attrs, palette);
            break;
          case 'stacked-bar':
            drawStackedBarChart(page, rect, td.rows, td.headers, content.attrs, palette);
            break;
          case 'dumbbell':
            drawDumbbellChart(page, rect, td.rows, td.headers, content.attrs, palette);
            break;
          case 'slope':
            drawSlopeChart(page, rect, td.rows, td.headers, content.attrs, palette);
            break;
          case 'progress':
            drawProgressChart(page, rect, td.rows, content.attrs, palette);
            break;
          case 'scatter':
            drawScatterChart(page, rect, td.rows, td.headers, content.attrs, palette);
            break;
          case 'bubble':
            drawBubbleChart(page, rect, td.rows, td.headers, content.attrs, palette);
            break;
          case 'heatmap':
            drawHeatmapChart(page, rect, td.rows, td.headers, content.attrs, palette);
            break;
          case 'box-plot':
            drawBoxPlotChart(page, rect, td.rows, td.headers, content.attrs, palette);
            break;
          case 'population-pyramid':
            drawPopulationPyramidChart(page, rect, td.rows, td.headers, content.attrs, palette);
            break;
          case 'bullet':
            drawBulletChart(page, rect, td.rows, td.headers, content.attrs, palette);
            break;
          case 'parallel-coords':
            drawParallelCoordsChart(page, rect, td.rows, td.headers, content.attrs, palette);
            break;
          case 'gantt':
            drawGanttChart(page, rect, td.rows, td.headers, content.attrs, palette);
            break;
          case 'dot-map':
            drawDotMapChart(page, rect, td.rows, td.headers, content.attrs, palette);
            break;
          case 'marimekko':
            drawMarimekkoChart(page, rect, td.rows, td.headers, content.attrs, palette);
            break;
          case 'streamgraph':
            drawStreamgraphChart(page, rect, td.rows, td.headers, content.attrs, palette);
            break;
          case 'chord':
            drawChordChart(page, rect, td.rows, td.headers, content.attrs, palette);
            break;
          case 'candlestick':
            drawCandlestickChart(page, rect, td.rows, td.headers, content.attrs, palette);
            break;
          case 'fraction-bar':
            drawFractionBarChart(page, rect, td.rows, td.headers, content.attrs, palette);
            break;
          case 'pie-grid':
            drawPieGridChart(page, rect, td.rows, td.headers, content.attrs, palette);
            break;
          case 'violin-plot':
            drawViolinPlotChart(page, rect, td.rows, td.headers, content.attrs, palette);
            break;
        }
      } else if (chartType === 'network') {
        const nd: NetworkData = JSON.parse(content.content || '{"nodes":[],"edges":[]}');
        if (nd.nodes.length === 0) {
          noData();
          return;
        }
        drawNetworkChart(page, rect, nd, content.attrs, palette);
      } else if (chartType === 'sunburst') {
        const sd: SunburstGroup[] = JSON.parse(content.content || '[]');
        if (sd.length === 0) {
          noData();
          return;
        }
        drawSunburstChart(page, rect, sd, content.attrs, palette);
      } else if (chartType === 'arc-diagram' || chartType === 'sankey') {
        const ad: ArcData = JSON.parse(content.content || '{"nodes":[],"edges":[]}');
        if (ad.nodes.length === 0) {
          noData();
          return;
        }
        if (chartType === 'arc-diagram')
          drawArcDiagramChart(page, rect, ad, content.attrs, palette);
        else drawSankeyChart(page, rect, ad, content.attrs, palette);
      } else if (chartType === 'multiplication-grid') {
        drawMultiplicationGridChart(page, rect, [], content.attrs, palette);
      } else {
        const data: DataPoint[] = JSON.parse(content.content || '[]');
        if (data.length === 0) {
          noData();
          return;
        }

        switch (chartType) {
          case 'bar':
            drawBarChart(page, rect, data, content.attrs, palette);
            break;
          case 'column':
            drawColumnChart(page, rect, data, content.attrs, palette);
            break;
          case 'line':
            drawLineChart(page, rect, data, content.attrs, palette);
            break;
          case 'area':
            drawAreaChart(page, rect, data, content.attrs, palette);
            break;
          case 'pie':
            drawPieChart(page, rect, data, content.attrs, palette);
            break;
          case 'donut':
            drawDonutChart(page, rect, data, content.attrs, palette);
            break;
          case 'lollipop':
            drawLollipopChart(page, rect, data, content.attrs, palette);
            break;
          case 'step':
            drawStepChart(page, rect, data, content.attrs, palette);
            break;
          case 'histogram':
            drawHistogramChart(page, rect, data, content.attrs, palette);
            break;
          case 'funnel':
            drawFunnelChart(page, rect, data, content.attrs, palette);
            break;
          case 'pyramid':
            drawPyramidChart(page, rect, data, content.attrs, palette);
            break;
          case 'radar':
            drawRadarChart(page, rect, data, content.attrs, palette);
            break;
          case 'waffle':
            drawWaffleChart(page, rect, data, content.attrs, palette);
            break;
          case 'waterfall':
            drawWaterfallChart(page, rect, data, content.attrs, palette);
            break;
          case 'concentric':
            drawConcentricChart(page, rect, data, content.attrs, palette);
            break;
          case 'timeline':
            drawTimelineChart(page, rect, data, content.attrs, palette);
            break;
          case 'venn':
            drawVennChart(page, rect, data, content.attrs, palette);
            break;
          case 'treemap':
            drawTreemapChart(page, rect, data, content.attrs, palette);
            break;
          case 'rose':
            drawRoseChart(page, rect, data, content.attrs, palette);
            break;
          case 'strip':
            drawStripChart(page, rect, data, content.attrs, palette);
            break;
          case 'icon-array':
            drawIconArrayChart(page, rect, data, content.attrs, palette);
            break;
          case 'calendar-heatmap':
            drawCalendarHeatmapChart(page, rect, data, content.attrs, palette);
            break;
          case 'spiral':
            drawSpiralChart(page, rect, data, content.attrs, palette);
            break;
          case 'word-cloud':
            drawWordCloudChart(page, rect, data, content.attrs, palette);
            break;
          case 'pictogram':
            drawPictogramChart(page, rect, data, content.attrs, palette);
            break;
          case 'gauge':
            drawGaugeChart(page, rect, data, content.attrs, palette);
            break;
          case 'spline':
            drawSplineChart(page, rect, data, content.attrs, palette);
            break;
          case 'smooth-area':
            drawSmoothAreaChart(page, rect, data, content.attrs, palette);
            break;
          case 'number-line':
            drawNumberLineChart(page, rect, data, content.attrs, palette);
            break;
          case 'dot-plot':
            drawDotPlotChart(page, rect, data, content.attrs, palette);
            break;
          case 'ten-frame':
            drawTenFrameChart(page, rect, data, content.attrs, palette);
            break;
          case 'tally-chart':
            drawTallyChart(page, rect, data, content.attrs, palette);
            break;
          case 'place-value':
            drawPlaceValueChart(page, rect, data, content.attrs, palette);
            break;
          case 'fraction-circles':
            drawFractionCirclesChart(page, rect, data, content.attrs, palette);
            break;
          default:
            drawColumnChart(page, rect, data, content.attrs, palette);
        }
      }
    } catch (e) {
      const font = params.font || new PdfFont(PdfFont.FONT_NAME, 10);
      page.drawString(
        `Chart error: ${(e as Error).message}`,
        font,
        PdfBrush.Red,
        null,
        rect,
        ETextAlign.CenterLeft
      );
    }
  },
};

// ─── Parsers ──────────────────────────────────────────────────────────────────

function parseChartData(source: string): DataPoint[] {
  const data: DataPoint[] = [];
  const lines = source.trim().split('\n');

  let headerSkipped = false;
  let separatorFound = false;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    if (/^\|[\s\-:|]+\|$/.test(trimmed)) {
      separatorFound = true;
      continue;
    }

    if (trimmed.startsWith('|')) {
      const cells = trimmed
        .split('|')
        .map((c) => c.trim())
        .filter((c) => c !== '');
      if (cells.length >= 2) {
        if (!headerSkipped && !separatorFound) {
          headerSkipped = true;
          continue;
        }
        const label = cells[0];
        const value = parseCzechFloat(cells[cells.length - 1]);
        if (!isNaN(value)) data.push({ label, value });
      }
    } else {
      if (!headerSkipped) {
        headerSkipped = true;
        continue;
      }
      const parts = trimmed.split(/[,\t]+/);
      if (parts.length >= 2) {
        const label = parts[0].trim();
        const value = parseCzechFloat(parts[1].trim());
        if (!isNaN(value)) data.push({ label, value });
      }
    }
  }

  return data;
}

function parseTimelineData(source: string): DataPoint[] {
  const lines = source.trim().split('\n');
  let headerSkipped = false,
    separatorFound = false;
  const rawRows: { label: string; dateStr: string }[] = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    if (/^\|[\s\-:|]+\|$/.test(trimmed)) {
      separatorFound = true;
      continue;
    }
    if (trimmed.startsWith('|')) {
      const cells = trimmed
        .split('|')
        .map((c) => c.trim())
        .filter((c) => c !== '');
      if (cells.length >= 2) {
        if (!headerSkipped && !separatorFound) {
          headerSkipped = true;
          continue;
        }
        rawRows.push({ label: cells[0], dateStr: cells[cells.length - 1] });
      }
    }
  }

  // Convert dateStr to numeric positions (number → direct, date string → timestamp, else ordinal)
  const positions = rawRows.map((r, i) => {
    const num = parseCzechFloat(r.dateStr);
    if (!isNaN(num)) return num;
    const d = new Date(r.dateStr);
    if (!isNaN(d.getTime())) return d.getFullYear() + d.getMonth() / 12;
    return i;
  });

  return rawRows.map((r, i) => ({
    label: r.label,
    value: positions[i],
    rawLabel: r.dateStr,
  }));
}

function parseMultiChartData(source: string): MultiTableData {
  const rows: MultiDataPoint[] = [];
  const lines = source.trim().split('\n');
  let headers: string[] = [];
  let headerSkipped = false;
  let separatorFound = false;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    if (/^\|[\s\-:|]+\|$/.test(trimmed)) {
      separatorFound = true;
      continue;
    }

    if (trimmed.startsWith('|')) {
      // Use slice(1,-1) instead of filter to preserve empty cells (e.g. blank label header in heatmap)
      const allCells = trimmed.split('|').map((c) => c.trim());
      const cells = allCells.slice(1, allCells.length - 1);
      if (cells.length >= 2) {
        if (!headerSkipped && !separatorFound) {
          headerSkipped = true;
          headers = cells.slice(1); // skip label column header (may be empty)
          continue;
        }
        const label = cells[0];
        const values = cells.slice(1).map((v) => parseCzechFloat(v));
        rows.push({ label, values });
      }
    } else {
      if (!headerSkipped) {
        headerSkipped = true;
        continue;
      }
      const parts = trimmed.split(/[,\t]+/);
      if (parts.length >= 2) {
        const label = parts[0].trim();
        const values = parts.slice(1).map((v) => parseCzechFloat(v.trim()));
        rows.push({ label, values });
      }
    }
  }

  return { headers, rows };
}

function parseNetworkData(source: string): NetworkData {
  const nodes: string[] = [];
  const edges: [number, number][] = [];
  const lines = source.trim().split('\n');
  let headerSkipped = false,
    separatorFound = false;

  const nodeIndex = (name: string): number => {
    let idx = nodes.indexOf(name);
    if (idx === -1) {
      idx = nodes.length;
      nodes.push(name);
    }
    return idx;
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    if (/^\|[\s\-:|]+\|$/.test(trimmed)) {
      separatorFound = true;
      continue;
    }
    if (trimmed.startsWith('|')) {
      const cells = trimmed
        .split('|')
        .map((c) => c.trim())
        .filter((c) => c !== '');
      if (cells.length >= 2) {
        if (!headerSkipped && !separatorFound) {
          headerSkipped = true;
          continue;
        }
        const from = nodeIndex(cells[0]);
        const to = nodeIndex(cells[1]);
        edges.push([from, to]);
      }
    }
  }

  return { nodes, edges };
}

function parseCzechFloat(str: string): number {
  let cleaned = str.trim();
  if (!cleaned) return NaN;

  const isNegative = cleaned.startsWith('-');
  if (isNegative) cleaned = cleaned.substring(1);

  cleaned = cleaned.replace(/[$€£¥₽\sKč€]/g, '');

  if (cleaned.includes(',') && !cleaned.includes('.')) {
    cleaned = cleaned.replace(',', '.');
  } else if (cleaned.includes(',') && cleaned.includes('.')) {
    const commaIndex = cleaned.lastIndexOf(',');
    const dotIndex = cleaned.lastIndexOf('.');
    if (commaIndex > dotIndex) cleaned = cleaned.replace(',', '.');
  }

  const result = parseFloat(cleaned);
  return isNegative && !isNaN(result) ? -result : result;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isLightColor(hex: string): boolean {
  const color = hex.replace('#', '');
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5;
}

function hexToRgb(hex: string) {
  const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return r
    ? { r: parseInt(r[1], 16), g: parseInt(r[2], 16), b: parseInt(r[3], 16) }
    : { r: 74, g: 144, b: 217 };
}

function getArcPoints(
  cx: number,
  cy: number,
  radius: number,
  startAngle: number,
  endAngle: number
): PdfPoint[] {
  const points: PdfPoint[] = [];
  const steps = Math.max(10, Math.ceil((endAngle - startAngle) / 10));
  for (let i = 0; i <= steps; i++) {
    const angle = startAngle + (endAngle - startAngle) * (i / steps);
    const rad = (angle * Math.PI) / 180;
    points.push(new PdfPoint(cx + radius * Math.cos(rad), cy + radius * Math.sin(rad)));
  }
  return points;
}

// Standard axis layout used by column/lollipop/histogram
function columnAxisLayout(rect: PdfRect) {
  return {
    axisX: rect.left + 15,
    baselineY: rect.bottom - 20,
    chartHeight: rect.height - 30,
    colWidth: rect.width - 25, // total bar area width (divide by n inside)
  };
}

// ─── Single-value chart draw functions ────────────────────────────────────────

function drawBarChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const textColor = PdfColor.fromHex(palette.textColor);
  const outlineColor = palette.outlineColor ? PdfColor.fromHex(palette.outlineColor) : null;

  const maxValue = Math.max(...data.map((d) => Math.abs(d.value))) || 1;

  const baseFont = new PdfFont(PdfFont.FONT_NAME, 9);
  const labelTexts = data.map((d) => d.label);
  const labelPad = 6;
  const measuredMaxW = Math.max(...labelTexts.map((l) => page.measureString(l, baseFont).width));
  const maxAllowed = rect.width * 0.2;
  const labelAreaW = Math.min(measuredMaxW + labelPad, maxAllowed);
  const labelFont = page.adjustFont(labelAreaW - labelPad, labelTexts, baseFont);

  const rightPad = 5;
  const topPad = 5;
  const bottomPad = 5;

  const barAreaX = rect.left + labelAreaW;
  const barAreaW = rect.width - labelAreaW - rightPad;
  const barAreaH = rect.height - topPad - bottomPad;

  const barSlotH = barAreaH / data.length;
  const barH = Math.max(4, barSlotH * 0.65);
  const barGap = (barSlotH - barH) / 2;

  const axisPen = outlineColor
    ? new PdfPen(outlineColor, 1)
    : new PdfPen(PdfColor.fromRgb(0, 0, 0), 1);
  page.drawLine(
    axisPen,
    new PdfPoint(barAreaX, rect.top + topPad),
    new PdfPoint(barAreaX, rect.bottom - bottomPad)
  );

  const valueFont = new PdfFont(PdfFont.FONT_NAME, 7);

  for (let i = 0; i < data.length; i++) {
    const barW = (Math.abs(data[i].value) / maxValue) * barAreaW;
    const barY = rect.top + topPad + i * barSlotH + barGap;

    const color = PdfColor.fromHex(colors[i % colors.length]);
    const brush = new PdfBrush(color);

    if (barW > 0) {
      page.drawRectangle(
        outlineColor ? new PdfPen(outlineColor, 0.5) : null,
        brush,
        new PdfRect(barAreaX, barY, barW, barH)
      );
    }

    const labelRect = new PdfRect(rect.left, barY, labelAreaW - labelPad, barH);
    page.drawString(
      data[i].label,
      labelFont,
      new PdfBrush(textColor),
      null,
      labelRect,
      ETextAlign.CenterLeft
    );

    if (barW > 20) {
      const valueRect = new PdfRect(barAreaX + barW / 2 - 10, barY, 20, barH);
      const valueTextColor = isLightColor(colors[i % colors.length])
        ? PdfBrush.Black
        : PdfBrush.White;
      page.drawString(
        String(data[i].value),
        valueFont,
        valueTextColor,
        null,
        valueRect,
        ETextAlign.Center
      );
    }
  }
}

function drawColumnChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const textColor = PdfColor.fromHex(palette.textColor);
  const outlineColor = palette.outlineColor ? PdfColor.fromHex(palette.outlineColor) : null;

  const maxValue = Math.max(...data.map((d) => Math.abs(d.value))) || 1;

  const colWidth = (rect.width - 25) / data.length;
  const chartHeight = rect.height - 30;

  const axisPen = outlineColor
    ? new PdfPen(outlineColor, 1)
    : new PdfPen(PdfColor.fromRgb(0, 0, 0), 1);

  const baselineY = rect.bottom - 20;
  const axisX = rect.left + 15;

  page.drawLine(axisPen, new PdfPoint(axisX, baselineY), new PdfPoint(rect.right - 5, baselineY));
  page.drawLine(axisPen, new PdfPoint(axisX, rect.top + 5), new PdfPoint(axisX, baselineY));

  for (let i = 0; i < data.length; i++) {
    const barHeight = (Math.abs(data[i].value) / maxValue) * chartHeight;
    const x = axisX + 3 + i * colWidth;
    const y = baselineY - barHeight;

    const color = PdfColor.fromHex(colors[i % colors.length]);
    const brush = new PdfBrush(color);

    page.drawRectangle(
      outlineColor ? new PdfPen(outlineColor, 0.5) : null,
      brush,
      new PdfRect(x, y, colWidth - 3, barHeight)
    );

    const labelFont = new PdfFont(PdfFont.FONT_NAME, 7);
    page.drawString(
      data[i].label,
      labelFont,
      new PdfBrush(textColor),
      null,
      new PdfRect(x, baselineY + 1, colWidth - 3, 8),
      ETextAlign.Center
    );

    if (barHeight > 12) {
      const valueRect = new PdfRect(x, y + barHeight / 2 - 3, colWidth - 3, 6);
      const valueTextColor = isLightColor(colors[i % colors.length])
        ? PdfBrush.Black
        : PdfBrush.White;
      page.drawString(
        String(data[i].value),
        labelFont,
        valueTextColor,
        null,
        valueRect,
        ETextAlign.Center
      );
    }
  }
}

function drawAreaChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const textColor = PdfColor.fromHex(palette.textColor);
  const outlineColor = palette.outlineColor ? PdfColor.fromHex(palette.outlineColor) : null;

  const maxValue = Math.max(...data.map((d) => d.value));
  const minValue = Math.min(...data.map((d) => d.value));
  const valueRange = maxValue - minValue || 1;

  const chartWidth = rect.width - 25;
  const chartHeight = rect.height - 30;

  const axisPen = outlineColor
    ? new PdfPen(outlineColor, 1)
    : new PdfPen(PdfColor.fromRgb(0, 0, 0), 1);
  const baselineY = rect.bottom - 20;
  const axisX = rect.left + 15;

  page.drawLine(axisPen, new PdfPoint(axisX, baselineY), new PdfPoint(rect.right - 5, baselineY));
  page.drawLine(axisPen, new PdfPoint(axisX, rect.top + 5), new PdfPoint(axisX, baselineY));

  const points: PdfPoint[] = [];
  for (let i = 0; i < data.length; i++) {
    const x = axisX + 5 + (i / (data.length - 1 || 1)) * chartWidth;
    const y = baselineY - ((data[i].value - minValue) / valueRange) * chartHeight;
    points.push(new PdfPoint(x, y));
  }

  const areaPoints: PdfPoint[] = [
    new PdfPoint(axisX + 5, baselineY),
    ...points,
    new PdfPoint(axisX + 5 + chartWidth, baselineY),
  ];

  const primaryRgb = hexToRgb(colors[0]);
  const areaBrush = new PdfBrush(PdfColor.fromRgb(primaryRgb.r, primaryRgb.g, primaryRgb.b, 0.3));
  page.drawPolygon(null, areaBrush, areaPoints);

  const linePen = new PdfPen(PdfColor.fromRgb(primaryRgb.r, primaryRgb.g, primaryRgb.b), 2);
  for (let i = 0; i < points.length - 1; i++) {
    page.drawLine(linePen, points[i], points[i + 1]);
  }

  for (let i = 0; i < data.length; i++) {
    const dotBrush = new PdfBrush(PdfColor.fromRgb(primaryRgb.r, primaryRgb.g, primaryRgb.b));
    page.drawEllipse(null, dotBrush, new PdfRect(points[i].x - 2.5, points[i].y - 2.5, 5, 5));
  }

  const labelFont = new PdfFont(PdfFont.FONT_NAME, 7);
  for (let i = 0; i < data.length; i++) {
    const x = axisX + 5 + (i / (data.length - 1 || 1)) * chartWidth;
    page.drawString(
      data[i].label,
      labelFont,
      new PdfBrush(textColor),
      null,
      new PdfRect(x - 12, baselineY + 1, 24, 8),
      ETextAlign.Center
    );
  }
}

function drawLineChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const textColor = PdfColor.fromHex(palette.textColor);
  const outlineColor = palette.outlineColor ? PdfColor.fromHex(palette.outlineColor) : null;

  const maxValue = Math.max(...data.map((d) => d.value));
  const minValue = Math.min(...data.map((d) => d.value));
  const valueRange = maxValue - minValue || 1;

  const chartWidth = rect.width - 25;
  const chartHeight = rect.height - 30;

  const axisPen = outlineColor
    ? new PdfPen(outlineColor, 1)
    : new PdfPen(PdfColor.fromRgb(0, 0, 0), 1);
  const baselineY = rect.bottom - 20;
  const axisX = rect.left + 15;

  page.drawLine(axisPen, new PdfPoint(axisX, baselineY), new PdfPoint(rect.right - 5, baselineY));
  page.drawLine(axisPen, new PdfPoint(axisX, rect.top + 5), new PdfPoint(axisX, baselineY));

  const points: PdfPoint[] = [];
  for (let i = 0; i < data.length; i++) {
    const x = axisX + 5 + (i / (data.length - 1 || 1)) * chartWidth;
    const y = baselineY - ((data[i].value - minValue) / valueRange) * chartHeight;
    points.push(new PdfPoint(x, y));
  }

  const primaryRgb = hexToRgb(colors[0]);
  const linePen = new PdfPen(PdfColor.fromRgb(primaryRgb.r, primaryRgb.g, primaryRgb.b), 2);
  for (let i = 0; i < points.length - 1; i++) {
    page.drawLine(linePen, points[i], points[i + 1]);
  }

  const dotBrush = new PdfBrush(PdfColor.fromRgb(primaryRgb.r, primaryRgb.g, primaryRgb.b));
  for (const pt of points) {
    page.drawEllipse(null, dotBrush, new PdfRect(pt.x - 2.5, pt.y - 2.5, 5, 5));
  }

  const labelFont = new PdfFont(PdfFont.FONT_NAME, 7);
  for (let i = 0; i < data.length; i++) {
    const x = axisX + 5 + (i / (data.length - 1 || 1)) * chartWidth;
    page.drawString(
      data[i].label,
      labelFont,
      new PdfBrush(textColor),
      null,
      new PdfRect(x - 12, baselineY + 1, 24, 8),
      ETextAlign.Center
    );
  }
}

function drawPieChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const outlineColor = palette.outlineColor ? PdfColor.fromHex(palette.outlineColor) : null;
  const pen = outlineColor ? new PdfPen(outlineColor, 0.8) : null;

  const total = data.reduce((sum, d) => sum + d.value, 0);
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;
  const radius = Math.min(rect.width, rect.height) / 2 - 8;

  interface LabelInfo {
    x: number;
    y: number;
    text: string;
    colorHex: string;
  }
  const labels: LabelInfo[] = [];
  let startAngle = 0;

  for (let i = 0; i < data.length; i++) {
    const sweepAngle = (data[i].value / total) * 360;
    const color = PdfColor.fromHex(colors[i % colors.length]);
    const brush = new PdfBrush(color);

    const arcPoints = getArcPoints(centerX, centerY, radius, startAngle, startAngle + sweepAngle);
    if (arcPoints.length > 2) {
      page.drawPolygon(pen, brush, [new PdfPoint(centerX, centerY), ...arcPoints]);
    }

    if (sweepAngle > 12) {
      const midAngle = startAngle + sweepAngle / 2;
      const labelRadius = radius * 0.65;
      labels.push({
        x: centerX + labelRadius * Math.cos((midAngle * Math.PI) / 180),
        y: centerY + labelRadius * Math.sin((midAngle * Math.PI) / 180),
        text: `${Math.round((data[i].value / total) * 100)}%`,
        colorHex: colors[i % colors.length],
      });
    }

    startAngle += sweepAngle;
  }

  const labelFont = new PdfFont(PdfFont.FONT_NAME, 8);
  for (const lbl of labels) {
    const labelTextColor = isLightColor(lbl.colorHex) ? PdfBrush.Black : PdfBrush.White;
    page.drawString(
      lbl.text,
      labelFont,
      labelTextColor,
      null,
      new PdfRect(lbl.x - 14, lbl.y - 5, 28, 10),
      ETextAlign.Center
    );
  }
}

// ─── New Tier-1 single-value chart types ─────────────────────────────────────

function drawDonutChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const outlineColor = palette.outlineColor ? PdfColor.fromHex(palette.outlineColor) : null;
  const pen = outlineColor ? new PdfPen(outlineColor, 0.8) : null;

  const total = data.reduce((sum, d) => sum + d.value, 0);
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;
  const outerRadius = Math.min(rect.width, rect.height) / 2 - 8;
  const innerRadius = outerRadius * 0.45;

  interface LabelInfo {
    x: number;
    y: number;
    text: string;
    colorHex: string;
  }
  const labels: LabelInfo[] = [];
  let startAngle = 0;

  // Pass 1: slices
  for (let i = 0; i < data.length; i++) {
    const sweepAngle = (data[i].value / total) * 360;
    const brush = new PdfBrush(PdfColor.fromHex(colors[i % colors.length]));
    const arcPoints = getArcPoints(
      centerX,
      centerY,
      outerRadius,
      startAngle,
      startAngle + sweepAngle
    );
    if (arcPoints.length > 2) {
      page.drawPolygon(pen, brush, [new PdfPoint(centerX, centerY), ...arcPoints]);
    }

    if (sweepAngle > 12) {
      const midAngle = startAngle + sweepAngle / 2;
      const labelR = (outerRadius + innerRadius) / 2;
      labels.push({
        x: centerX + labelR * Math.cos((midAngle * Math.PI) / 180),
        y: centerY + labelR * Math.sin((midAngle * Math.PI) / 180),
        text: `${Math.round((data[i].value / total) * 100)}%`,
        colorHex: colors[i % colors.length],
      });
    }

    startAngle += sweepAngle;
  }

  // Punch out centre
  page.drawEllipse(
    null,
    PdfBrush.White,
    new PdfRect(centerX - innerRadius, centerY - innerRadius, innerRadius * 2, innerRadius * 2)
  );

  // Pass 2: labels on the ring
  const labelFont = new PdfFont(PdfFont.FONT_NAME, 8);
  for (const lbl of labels) {
    const labelTextColor = isLightColor(lbl.colorHex) ? PdfBrush.Black : PdfBrush.White;
    page.drawString(
      lbl.text,
      labelFont,
      labelTextColor,
      null,
      new PdfRect(lbl.x - 14, lbl.y - 5, 28, 10),
      ETextAlign.Center
    );
  }
}

function drawLollipopChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const textColor = PdfColor.fromHex(palette.textColor);
  const outlineColor = palette.outlineColor ? PdfColor.fromHex(palette.outlineColor) : null;

  const maxValue = Math.max(...data.map((d) => Math.abs(d.value))) || 1;
  const colWidth = (rect.width - 25) / data.length;
  const chartHeight = rect.height - 30;
  const baselineY = rect.bottom - 20;
  const axisX = rect.left + 15;
  const dotR = 4;

  const axisPen = outlineColor
    ? new PdfPen(outlineColor, 1)
    : new PdfPen(PdfColor.fromRgb(0, 0, 0), 1);
  page.drawLine(axisPen, new PdfPoint(axisX, baselineY), new PdfPoint(rect.right - 5, baselineY));
  page.drawLine(axisPen, new PdfPoint(axisX, rect.top + 5), new PdfPoint(axisX, baselineY));

  const labelFont = new PdfFont(PdfFont.FONT_NAME, 7);

  for (let i = 0; i < data.length; i++) {
    const barHeight = (Math.abs(data[i].value) / maxValue) * chartHeight;
    const cx = axisX + 3 + i * colWidth + (colWidth - 3) / 2;
    const topY = baselineY - barHeight;
    const color = PdfColor.fromHex(colors[i % colors.length]);

    page.drawLine(new PdfPen(color, 1.5), new PdfPoint(cx, baselineY), new PdfPoint(cx, topY));
    page.drawEllipse(
      outlineColor ? new PdfPen(outlineColor, 0.5) : null,
      new PdfBrush(color),
      new PdfRect(cx - dotR, topY - dotR, dotR * 2, dotR * 2)
    );

    page.drawString(
      data[i].label,
      labelFont,
      new PdfBrush(textColor),
      null,
      new PdfRect(cx - (colWidth - 3) / 2, baselineY + 1, colWidth - 3, 8),
      ETextAlign.Center
    );
  }
}

function drawStepChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const textColor = PdfColor.fromHex(palette.textColor);
  const outlineColor = palette.outlineColor ? PdfColor.fromHex(palette.outlineColor) : null;

  const maxValue = Math.max(...data.map((d) => d.value));
  const minValue = Math.min(...data.map((d) => d.value));
  const valueRange = maxValue - minValue || 1;

  const chartWidth = rect.width - 25;
  const chartHeight = rect.height - 30;
  const baselineY = rect.bottom - 20;
  const axisX = rect.left + 15;

  const axisPen = outlineColor
    ? new PdfPen(outlineColor, 1)
    : new PdfPen(PdfColor.fromRgb(0, 0, 0), 1);
  page.drawLine(axisPen, new PdfPoint(axisX, baselineY), new PdfPoint(rect.right - 5, baselineY));
  page.drawLine(axisPen, new PdfPoint(axisX, rect.top + 5), new PdfPoint(axisX, baselineY));

  const points: PdfPoint[] = data.map(
    (d, i) =>
      new PdfPoint(
        axisX + 5 + (i / (data.length - 1 || 1)) * chartWidth,
        baselineY - ((d.value - minValue) / valueRange) * chartHeight
      )
  );

  const primaryRgb = hexToRgb(colors[0]);
  const linePen = new PdfPen(PdfColor.fromRgb(primaryRgb.r, primaryRgb.g, primaryRgb.b), 2);
  const dotBrush = new PdfBrush(PdfColor.fromRgb(primaryRgb.r, primaryRgb.g, primaryRgb.b));

  // Step connections: horizontal then vertical
  for (let i = 0; i < points.length - 1; i++) {
    const corner = new PdfPoint(points[i + 1].x, points[i].y);
    page.drawLine(linePen, points[i], corner);
    page.drawLine(linePen, corner, points[i + 1]);
  }

  for (const pt of points) {
    page.drawEllipse(null, dotBrush, new PdfRect(pt.x - 2.5, pt.y - 2.5, 5, 5));
  }

  const labelFont = new PdfFont(PdfFont.FONT_NAME, 7);
  for (let i = 0; i < data.length; i++) {
    page.drawString(
      data[i].label,
      labelFont,
      new PdfBrush(textColor),
      null,
      new PdfRect(points[i].x - 12, baselineY + 1, 24, 8),
      ETextAlign.Center
    );
  }
}

function drawHistogramChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const textColor = PdfColor.fromHex(palette.textColor);
  const outlineColor = palette.outlineColor ? PdfColor.fromHex(palette.outlineColor) : null;

  const maxValue = Math.max(...data.map((d) => Math.abs(d.value))) || 1;
  const colWidth = (rect.width - 25) / data.length;
  const chartHeight = rect.height - 30;
  const baselineY = rect.bottom - 20;
  const axisX = rect.left + 15;

  const axisPen = outlineColor
    ? new PdfPen(outlineColor, 1)
    : new PdfPen(PdfColor.fromRgb(0, 0, 0), 1);
  page.drawLine(axisPen, new PdfPoint(axisX, baselineY), new PdfPoint(rect.right - 5, baselineY));
  page.drawLine(axisPen, new PdfPoint(axisX, rect.top + 5), new PdfPoint(axisX, baselineY));

  const primaryColor = PdfColor.fromHex(colors[0]);
  const brush = new PdfBrush(primaryColor);
  const barPen = new PdfPen(outlineColor ?? PdfColor.fromRgb(0, 0, 0), 0.5);
  const labelFont = new PdfFont(PdfFont.FONT_NAME, 6);

  for (let i = 0; i < data.length; i++) {
    const barHeight = (Math.abs(data[i].value) / maxValue) * chartHeight;
    const x = axisX + i * colWidth; // no gap between bins
    page.drawRectangle(barPen, brush, new PdfRect(x, baselineY - barHeight, colWidth, barHeight));
    page.drawString(
      data[i].label,
      labelFont,
      new PdfBrush(textColor),
      null,
      new PdfRect(x, baselineY + 1, colWidth, 8),
      ETextAlign.Center
    );
  }
}

function drawFunnelChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const textColor = PdfColor.fromHex(palette.textColor);
  const outlineColor = palette.outlineColor ? PdfColor.fromHex(palette.outlineColor) : null;

  const maxValue = Math.max(...data.map((d) => d.value)) || 1;
  const n = data.length;
  const bandH = rect.height / n;
  const maxWidth = rect.width * 0.85;
  const cx = rect.left + rect.width / 2;
  const labelFont = new PdfFont(PdfFont.FONT_NAME, 8);
  const valueFont = new PdfFont(PdfFont.FONT_NAME, 7);

  for (let i = 0; i < n; i++) {
    const topW = (data[i].value / maxValue) * maxWidth;
    const bottomW = i + 1 < n ? (data[i + 1].value / maxValue) * maxWidth : topW * 0.65;
    const y = rect.top + i * bandH;

    const color = PdfColor.fromHex(colors[i % colors.length]);
    page.drawPolygon(outlineColor ? new PdfPen(outlineColor, 0.5) : null, new PdfBrush(color), [
      new PdfPoint(cx - topW / 2, y),
      new PdfPoint(cx + topW / 2, y),
      new PdfPoint(cx + bottomW / 2, y + bandH),
      new PdfPoint(cx - bottomW / 2, y + bandH),
    ]);

    const bandMidY = y + bandH / 2;
    const labelColor = isLightColor(colors[i % colors.length]) ? PdfBrush.Black : PdfBrush.White;
    // Category label centred inside the band
    page.drawString(
      data[i].label,
      labelFont,
      labelColor,
      null,
      new PdfRect(cx - topW / 2, bandMidY - 5, topW, 10),
      ETextAlign.Center
    );
    // Value label to the right of the band
    const rightX = cx + topW / 2 + 2;
    if (rightX < rect.right) {
      page.drawString(
        String(data[i].value),
        valueFont,
        new PdfBrush(textColor),
        null,
        new PdfRect(rightX, bandMidY - 4, rect.right - rightX, 8),
        ETextAlign.CenterLeft
      );
    }
  }
}

function drawPyramidChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  // data[0] = bottom (widest) band, data[n-1] = top (narrowest) band
  const colors = getPaletteColors(palette.name);
  const outlineColor = palette.outlineColor ? PdfColor.fromHex(palette.outlineColor) : null;

  const n = data.length;
  const bandH = rect.height / n;
  const maxWidth = rect.width * 0.9;
  const cx = rect.left + rect.width / 2;
  const totalH = rect.height;
  const labelFont = new PdfFont(PdfFont.FONT_NAME, 8);

  // Visual band i (0=top, n-1=bottom) → data index (n-1-i)
  for (let i = 0; i < n; i++) {
    const d = data[n - 1 - i];
    const topY = rect.top + i * bandH;
    const botY = topY + bandH;

    // Linearly taper: 0 at apex (rect.top) → maxWidth at base (rect.bottom)
    const topW = ((topY - rect.top) / totalH) * maxWidth;
    const botW = ((botY - rect.top) / totalH) * maxWidth;

    const color = PdfColor.fromHex(colors[i % colors.length]);
    page.drawPolygon(outlineColor ? new PdfPen(outlineColor, 0.5) : null, new PdfBrush(color), [
      new PdfPoint(cx - topW / 2, topY),
      new PdfPoint(cx + topW / 2, topY),
      new PdfPoint(cx + botW / 2, botY),
      new PdfPoint(cx - botW / 2, botY),
    ]);

    const midW = (topW + botW) / 2;
    const labelColor = isLightColor(colors[i % colors.length]) ? PdfBrush.Black : PdfBrush.White;
    page.drawString(
      d.label,
      labelFont,
      labelColor,
      null,
      new PdfRect(cx - midW / 2, topY + bandH / 2 - 5, midW, 10),
      ETextAlign.Center
    );
  }
}

// ─── New Tier-1 multi-column chart types ─────────────────────────────────────

function drawStackedColumnChart(
  page: PageGen,
  rect: PdfRect,
  data: MultiDataPoint[],
  headers: string[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const textColor = PdfColor.fromHex(palette.textColor);
  const outlineColor = palette.outlineColor ? PdfColor.fromHex(palette.outlineColor) : null;

  const totals = data.map((d) => d.values.reduce((s, v) => s + Math.max(0, v), 0));
  const maxTotal = Math.max(...totals) || 1;
  const numSeries = Math.max(...data.map((d) => d.values.length));

  const colWidth = (rect.width - 25) / data.length;
  const chartHeight = rect.height - 30;
  const baselineY = rect.bottom - 20;
  const axisX = rect.left + 15;

  const axisPen = outlineColor
    ? new PdfPen(outlineColor, 1)
    : new PdfPen(PdfColor.fromRgb(0, 0, 0), 1);
  page.drawLine(axisPen, new PdfPoint(axisX, baselineY), new PdfPoint(rect.right - 5, baselineY));
  page.drawLine(axisPen, new PdfPoint(axisX, rect.top + 5), new PdfPoint(axisX, baselineY));

  const labelFont = new PdfFont(PdfFont.FONT_NAME, 7);

  for (let i = 0; i < data.length; i++) {
    const x = axisX + 3 + i * colWidth;
    let cumY = baselineY;

    for (let s = 0; s < numSeries; s++) {
      const v = Math.max(0, data[i].values[s] ?? 0);
      if (v === 0) continue;
      const segH = (v / maxTotal) * chartHeight;
      const color = PdfColor.fromHex(colors[s % colors.length]);
      page.drawRectangle(
        outlineColor ? new PdfPen(outlineColor, 0.5) : null,
        new PdfBrush(color),
        new PdfRect(x, cumY - segH, colWidth - 3, segH)
      );
      cumY -= segH;
    }

    page.drawString(
      data[i].label,
      labelFont,
      new PdfBrush(textColor),
      null,
      new PdfRect(x, baselineY + 1, colWidth - 3, 8),
      ETextAlign.Center
    );
  }
}

function drawStackedBarChart(
  page: PageGen,
  rect: PdfRect,
  data: MultiDataPoint[],
  headers: string[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const textColor = PdfColor.fromHex(palette.textColor);
  const outlineColor = palette.outlineColor ? PdfColor.fromHex(palette.outlineColor) : null;

  const totals = data.map((d) => d.values.reduce((s, v) => s + Math.max(0, v), 0));
  const maxTotal = Math.max(...totals) || 1;
  const numSeries = Math.max(...data.map((d) => d.values.length));

  const baseFont = new PdfFont(PdfFont.FONT_NAME, 9);
  const labelTexts = data.map((d) => d.label);
  const labelPad = 6;
  const measuredMaxW = Math.max(...labelTexts.map((l) => page.measureString(l, baseFont).width));
  const labelAreaW = Math.min(measuredMaxW + labelPad, rect.width * 0.25);
  const labelFont = page.adjustFont(labelAreaW - labelPad, labelTexts, baseFont);

  const barAreaX = rect.left + labelAreaW;
  const barAreaW = rect.width - labelAreaW - 5;
  const barAreaH = rect.height - 10;
  const barSlotH = barAreaH / data.length;
  const barH = Math.max(4, barSlotH * 0.65);
  const barGap = (barSlotH - barH) / 2;

  const axisPen = outlineColor
    ? new PdfPen(outlineColor, 1)
    : new PdfPen(PdfColor.fromRgb(0, 0, 0), 1);
  page.drawLine(
    axisPen,
    new PdfPoint(barAreaX, rect.top + 5),
    new PdfPoint(barAreaX, rect.bottom - 5)
  );

  for (let i = 0; i < data.length; i++) {
    const barY = rect.top + 5 + i * barSlotH + barGap;
    let cumX = barAreaX;

    for (let s = 0; s < numSeries; s++) {
      const v = Math.max(0, data[i].values[s] ?? 0);
      if (v === 0) continue;
      const segW = (v / maxTotal) * barAreaW;
      const color = PdfColor.fromHex(colors[s % colors.length]);
      page.drawRectangle(
        outlineColor ? new PdfPen(outlineColor, 0.5) : null,
        new PdfBrush(color),
        new PdfRect(cumX, barY, segW, barH)
      );
      cumX += segW;
    }

    page.drawString(
      data[i].label,
      labelFont,
      new PdfBrush(textColor),
      null,
      new PdfRect(rect.left, barY, labelAreaW - labelPad, barH),
      ETextAlign.CenterLeft
    );
  }
}

function drawDumbbellChart(
  page: PageGen,
  rect: PdfRect,
  data: MultiDataPoint[],
  headers: string[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const textColor = PdfColor.fromHex(palette.textColor);
  const outlineColor = palette.outlineColor ? PdfColor.fromHex(palette.outlineColor) : null;

  const allValues = data.flatMap((d) => d.values.filter((v) => !isNaN(v)));
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);
  const valueRange = maxValue - minValue || 1;

  const labelW = rect.width * 0.25;
  const pad = 10;
  const trackX = rect.left + labelW + pad;
  const trackW = rect.width - labelW - pad * 2;
  const rowH = rect.height / data.length;
  const dotR = 4;

  const startColor = PdfColor.fromHex(colors[0 % colors.length]);
  const endColor = PdfColor.fromHex(colors[1 % colors.length]);
  const connectorPen = new PdfPen(PdfColor.fromRgb(180, 180, 180), 1.5);
  const labelFont = new PdfFont(PdfFont.FONT_NAME, 8);

  // Column header labels at top
  const headerFont = new PdfFont(PdfFont.FONT_NAME, 7);
  if (headers.length >= 2) {
    page.drawString(
      `● ${  headers[0] || 'Start'}`,
      headerFont,
      new PdfBrush(startColor),
      null,
      new PdfRect(trackX, rect.top, trackW / 2, 10),
      ETextAlign.CenterLeft
    );
    page.drawString(
      `● ${  headers[1] || 'End'}`,
      headerFont,
      new PdfBrush(endColor),
      null,
      new PdfRect(trackX + trackW / 2, rect.top, trackW / 2, 10),
      ETextAlign.CenterLeft
    );
  }
  const dataTop = rect.top + (headers.length >= 2 ? 12 : 0);

  for (let i = 0; i < data.length; i++) {
    const start = data[i].values[0] ?? 0;
    const end = data[i].values[1] ?? 0;
    const rowCount = data.length;
    const availH = rect.bottom - dataTop;
    const rowHAdj = availH / rowCount;
    const cy = dataTop + rowHAdj * (i + 0.5);

    const x1 = trackX + ((start - minValue) / valueRange) * trackW;
    const x2 = trackX + ((end - minValue) / valueRange) * trackW;

    page.drawLine(connectorPen, new PdfPoint(x1, cy), new PdfPoint(x2, cy));
    page.drawEllipse(
      null,
      new PdfBrush(startColor),
      new PdfRect(x1 - dotR, cy - dotR, dotR * 2, dotR * 2)
    );
    page.drawEllipse(
      null,
      new PdfBrush(endColor),
      new PdfRect(x2 - dotR, cy - dotR, dotR * 2, dotR * 2)
    );

    page.drawString(
      data[i].label,
      labelFont,
      new PdfBrush(textColor),
      null,
      new PdfRect(rect.left, cy - rowHAdj / 2, labelW - 4, rowHAdj),
      ETextAlign.CenterRight
    );
  }
}

function drawSlopeChart(
  page: PageGen,
  rect: PdfRect,
  data: MultiDataPoint[],
  headers: string[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const textColor = PdfColor.fromHex(palette.textColor);
  const outlineColor = palette.outlineColor ? PdfColor.fromHex(palette.outlineColor) : null;

  const allValues = data.flatMap((d) => d.values.filter((v) => !isNaN(v)));
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);
  const valueRange = maxValue - minValue || 1;

  const dotR = 3;
  const labelW = 28;
  const topPad = 18;
  const bottomPad = 5;
  const chartH = rect.height - topPad - bottomPad;
  const leftAxisX = rect.left + labelW;
  const rightAxisX = rect.right - labelW;

  const axisPen = outlineColor
    ? new PdfPen(outlineColor, 1)
    : new PdfPen(PdfColor.fromRgb(150, 150, 150), 1);
  page.drawLine(
    axisPen,
    new PdfPoint(leftAxisX, rect.top + topPad),
    new PdfPoint(leftAxisX, rect.bottom - bottomPad)
  );
  page.drawLine(
    axisPen,
    new PdfPoint(rightAxisX, rect.top + topPad),
    new PdfPoint(rightAxisX, rect.bottom - bottomPad)
  );

  const headerFont = new PdfFont(PdfFont.FONT_NAME, 8);
  page.drawString(
    headers[0] || 'Before',
    headerFont,
    new PdfBrush(textColor),
    null,
    new PdfRect(leftAxisX - labelW, rect.top, labelW * 2, topPad),
    ETextAlign.Center
  );
  page.drawString(
    headers[1] || 'After',
    headerFont,
    new PdfBrush(textColor),
    null,
    new PdfRect(rightAxisX - labelW, rect.top, labelW * 2, topPad),
    ETextAlign.Center
  );

  const valueToY = (v: number) => rect.top + topPad + (1 - (v - minValue) / valueRange) * chartH;

  const itemFont = new PdfFont(PdfFont.FONT_NAME, 7);
  for (let i = 0; i < data.length; i++) {
    const before = data[i].values[0] ?? 0;
    const after = data[i].values[1] ?? 0;
    const y1 = valueToY(before);
    const y2 = valueToY(after);
    const color = PdfColor.fromHex(colors[i % colors.length]);

    page.drawLine(
      new PdfPen(color, 1.5),
      new PdfPoint(leftAxisX, y1),
      new PdfPoint(rightAxisX, y2)
    );
    page.drawEllipse(
      null,
      new PdfBrush(color),
      new PdfRect(leftAxisX - dotR, y1 - dotR, dotR * 2, dotR * 2)
    );
    page.drawEllipse(
      null,
      new PdfBrush(color),
      new PdfRect(rightAxisX - dotR, y2 - dotR, dotR * 2, dotR * 2)
    );

    page.drawString(
      data[i].label,
      itemFont,
      new PdfBrush(textColor),
      null,
      new PdfRect(rect.left, y1 - 5, labelW - 4, 10),
      ETextAlign.CenterRight
    );
  }
}

// ─── Tier-2 single-value chart types ─────────────────────────────────────────

function drawRadarChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  if (data.length < 3) return;
  const colors = getPaletteColors(palette.name);
  const textColor = PdfColor.fromHex(palette.textColor);
  const outlineColor = palette.outlineColor ? PdfColor.fromHex(palette.outlineColor) : null;

  const maxValue = Math.max(...data.map((d) => d.value)) || 1;
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const labelPad = 14;
  const r = Math.min(rect.width, rect.height) / 2 - labelPad;
  const n = data.length;
  const angles = data.map((_, i) => -Math.PI / 2 + (2 * Math.PI * i) / n);

  // Background concentric polygon grid
  const gridPen = new PdfPen(PdfColor.fromRgb(210, 210, 210), 0.5);
  for (const frac of [0.2, 0.4, 0.6, 0.8, 1.0]) {
    const gridPts = angles.map(
      (a) => new PdfPoint(cx + r * frac * Math.cos(a), cy + r * frac * Math.sin(a))
    );
    page.drawPolygon(gridPen, null, gridPts);
  }

  // Axis lines
  const axisPen = new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5);
  for (const a of angles) {
    page.drawLine(
      axisPen,
      new PdfPoint(cx, cy),
      new PdfPoint(cx + r * Math.cos(a), cy + r * Math.sin(a))
    );
  }

  // Data polygon
  const dataPoints = data.map((d, i) => {
    const frac = d.value / maxValue;
    return new PdfPoint(cx + r * frac * Math.cos(angles[i]), cy + r * frac * Math.sin(angles[i]));
  });

  const primaryRgb = hexToRgb(colors[0]);
  page.drawPolygon(
    new PdfPen(PdfColor.fromRgb(primaryRgb.r, primaryRgb.g, primaryRgb.b), 1.5),
    new PdfBrush(PdfColor.fromRgb(primaryRgb.r, primaryRgb.g, primaryRgb.b, 0.25)),
    dataPoints
  );

  const dotBrush = new PdfBrush(PdfColor.fromRgb(primaryRgb.r, primaryRgb.g, primaryRgb.b));
  for (const pt of dataPoints) {
    page.drawEllipse(null, dotBrush, new PdfRect(pt.x - 2.5, pt.y - 2.5, 5, 5));
  }

  // Value labels on data points
  const showValues = attrs.values !== 'false';
  if (showValues) {
    const valFont = new PdfFont(PdfFont.FONT_NAME, 6);
    for (let i = 0; i < n; i++) {
      const pt = dataPoints[i];
      const a = angles[i];
      // Offset label slightly outward from the dot
      const ox = Math.cos(a) * 7;
      const oy = Math.sin(a) * 7;
      page.drawString(
        String(data[i].value),
        valFont,
        new PdfBrush(textColor),
        null,
        new PdfRect(pt.x + ox - 12, pt.y + oy - 4, 24, 8),
        ETextAlign.Center
      );
    }
  }

  // Axis labels
  const labelFont = new PdfFont(PdfFont.FONT_NAME, 7);
  for (let i = 0; i < n; i++) {
    const a = angles[i];
    const lx = cx + (r + labelPad - 2) * Math.cos(a);
    const ly = cy + (r + labelPad - 2) * Math.sin(a);
    page.drawString(
      data[i].label,
      labelFont,
      new PdfBrush(textColor),
      null,
      new PdfRect(lx - 20, ly - 5, 40, 10),
      ETextAlign.Center
    );
  }
}

function drawWaffleChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const textColor = PdfColor.fromHex(palette.textColor);
  const outlineColor = palette.outlineColor ? PdfColor.fromHex(palette.outlineColor) : null;

  const total = data.reduce((s, d) => s + Math.abs(d.value), 0);
  const legendLineH = 9;
  const legendH = data.length * legendLineH + 3;
  const gridSize = Math.min(rect.width, rect.height - legendH);
  const gridX = rect.left + (rect.width - gridSize) / 2;
  const gridY = rect.top;
  const cell = gridSize / 10;
  const gap = cell * 0.08;

  // Build cell colour map: 100 cells
  const cells: number[] = [];
  for (let i = 0; i < data.length; i++) {
    const count = Math.round((Math.abs(data[i].value) / total) * 100);
    for (let j = 0; j < count && cells.length < 100; j++) cells.push(i);
  }
  while (cells.length < 100) cells.push(-1);

  const pen = outlineColor ? new PdfPen(outlineColor, 0.3) : null;
  for (let row = 0; row < 10; row++) {
    for (let col = 0; col < 10; col++) {
      const idx = row * 10 + col;
      const cat = cells[idx];
      const x = gridX + col * cell + gap / 2;
      const y = gridY + row * cell + gap / 2;
      const w = cell - gap;
      const color =
        cat >= 0 ? PdfColor.fromHex(colors[cat % colors.length]) : PdfColor.fromRgb(230, 230, 230);
      page.drawRectangle(pen, new PdfBrush(color), new PdfRect(x, y, w, w));
    }
  }

  // Legend
  const legendFont = new PdfFont(PdfFont.FONT_NAME, 7);
  for (let i = 0; i < data.length; i++) {
    const ly = gridY + gridSize + 3 + i * legendLineH;
    const pct = Math.round((Math.abs(data[i].value) / total) * 100);
    page.drawRectangle(
      null,
      new PdfBrush(PdfColor.fromHex(colors[i % colors.length])),
      new PdfRect(rect.left, ly + 1, 7, 7)
    );
    page.drawString(
      `${data[i].label} (${pct}%)`,
      legendFont,
      new PdfBrush(textColor),
      null,
      new PdfRect(rect.left + 10, ly, rect.width - 10, legendLineH),
      ETextAlign.CenterLeft
    );
  }
}

function drawWaterfallChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const textColor = PdfColor.fromHex(palette.textColor);
  const outlineColor = palette.outlineColor ? PdfColor.fromHex(palette.outlineColor) : null;

  const totalLabels = ['total', 'sum', 'subtotal', 'gesamt'];
  let running = 0;
  const segs = data.map((d) => {
    const isTotal = totalLabels.includes(d.label.toLowerCase());
    if (isTotal) {
      return { bottom: 0, top: running, isPositive: running >= 0, isTotal: true };
    }
    const prev = running;
    running += d.value;
    return {
      bottom: Math.min(prev, running),
      top: Math.max(prev, running),
      isPositive: d.value >= 0,
      isTotal: false,
    };
  });

  const allY = segs.flatMap((s) => [s.bottom, s.top]);
  const minY = Math.min(0, ...allY);
  const maxY = Math.max(...allY) || 1;
  const yRange = maxY - minY;

  const colWidth = (rect.width - 25) / data.length;
  const chartH = rect.height - 30;
  const baselineY = rect.bottom - 20;
  const axisX = rect.left + 15;
  const toY = (v: number) => baselineY - ((v - minY) / yRange) * chartH;

  const axisPen = outlineColor
    ? new PdfPen(outlineColor, 1)
    : new PdfPen(PdfColor.fromRgb(0, 0, 0), 1);
  page.drawLine(axisPen, new PdfPoint(axisX, rect.top + 5), new PdfPoint(axisX, baselineY));
  page.drawLine(axisPen, new PdfPoint(axisX, baselineY), new PdfPoint(rect.right - 5, baselineY));

  if (minY < 0) {
    const zeroY = toY(0);
    page.drawLine(
      new PdfPen(PdfColor.fromRgb(150, 150, 150), 0.5),
      new PdfPoint(axisX, zeroY),
      new PdfPoint(rect.right - 5, zeroY)
    );
  }

  const posColor = PdfColor.fromHex(colors[0]);
  const negColor = PdfColor.fromHex(colors[1] ?? colors[0]);
  const totalColor = PdfColor.fromHex(colors[2] ?? colors[0]);
  const labelFont = new PdfFont(PdfFont.FONT_NAME, 7);
  const connPen = new PdfPen(PdfColor.fromRgb(160, 160, 160), 0.5);

  for (let i = 0; i < data.length; i++) {
    const s = segs[i];
    const x = axisX + 3 + i * colWidth;
    const barTop = toY(s.top);
    const barH = Math.max(1, toY(s.bottom) - barTop);
    const color = s.isTotal ? totalColor : s.isPositive ? posColor : negColor;

    page.drawRectangle(
      outlineColor ? new PdfPen(outlineColor, 0.5) : null,
      new PdfBrush(color),
      new PdfRect(x, barTop, colWidth - 3, barH)
    );

    // Connector to next bar
    if (i < data.length - 1 && !segs[i + 1].isTotal) {
      const connY = s.isPositive ? barTop : barTop + barH;
      page.drawLine(
        connPen,
        new PdfPoint(x + colWidth - 3, connY),
        new PdfPoint(x + colWidth, connY)
      );
    }

    page.drawString(
      data[i].label,
      labelFont,
      new PdfBrush(textColor),
      null,
      new PdfRect(x, baselineY + 1, colWidth - 3, 8),
      ETextAlign.Center
    );
  }
}

function drawConcentricChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  // data[0] = innermost ring, data[n-1] = outermost
  const colors = getPaletteColors(palette.name);
  const showValues = attrs.values !== 'false';
  const n = data.length;
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const maxR = Math.min(rect.width, rect.height) / 2 - 4;

  // Pass 1: draw all ellipses (outermost first) so inner rings paint over outer ones
  for (let i = n - 1; i >= 0; i--) {
    const r = maxR * ((i + 1) / n);
    const colorHex = colors[(n - 1 - i) % colors.length];
    page.drawEllipse(
      null,
      new PdfBrush(PdfColor.fromHex(colorHex)),
      new PdfRect(cx - r, cy - r, r * 2, r * 2)
    );
  }

  // Pass 2: draw all labels on top so they are never covered
  for (let i = n - 1; i >= 0; i--) {
    const r = maxR * ((i + 1) / n);
    const innerR = i > 0 ? maxR * (i / n) : 0;
    const midR = (r + innerR) / 2;
    const bandH = r - innerR;
    const colorHex = colors[(n - 1 - i) % colors.length];
    const labelColor = isLightColor(colorHex) ? PdfBrush.Black : PdfBrush.White;

    // Place text at the top-center of each ring band: (cx, cy - midR)
    // Width uses chord of outer circle at that y-level
    const chordHW = i === 0 ? r * 0.75 : Math.sqrt(Math.max(0, r * r - midR * midR)) * 0.85;
    const tw = Math.max(22, chordHW * 2);
    const ty = cy - midR; // top-center y of ring band

    if (showValues && bandH >= 14) {
      const lf = new PdfFont(PdfFont.FONT_NAME, Math.min(8, bandH * 0.35));
      const vf = new PdfFont(PdfFont.FONT_NAME, Math.min(7, bandH * 0.28));
      page.drawString(
        data[i].label,
        lf,
        labelColor,
        null,
        new PdfRect(cx - tw / 2, ty - 6, tw, 8),
        ETextAlign.Center
      );
      page.drawString(
        String(data[i].value),
        vf,
        labelColor,
        null,
        new PdfRect(cx - tw / 2, ty + 1, tw, 7),
        ETextAlign.Center
      );
    } else {
      const lf = new PdfFont(PdfFont.FONT_NAME, Math.min(8, bandH * 0.45));
      page.drawString(
        data[i].label,
        lf,
        labelColor,
        null,
        new PdfRect(cx - tw / 2, ty - 4, tw, 8),
        ETextAlign.Center
      );
    }
  }
}

function drawTimelineChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const textColor = PdfColor.fromHex(palette.textColor);
  const outlineColor = palette.outlineColor ? PdfColor.fromHex(palette.outlineColor) : null;

  const sorted = [...data].sort((a, b) => a.value - b.value);
  const minVal = sorted[0].value;
  const maxVal = sorted[sorted.length - 1].value;
  const valRange = maxVal - minVal || 1;

  const hPad = 20;
  const lineY = rect.top + rect.height / 2;
  const lineX1 = rect.left + hPad;
  const lineX2 = rect.right - hPad;
  const lineW = lineX2 - lineX1;

  const axisPen = outlineColor
    ? new PdfPen(outlineColor, 2)
    : new PdfPen(PdfColor.fromRgb(100, 100, 100), 2);
  page.drawLine(axisPen, new PdfPoint(lineX1, lineY), new PdfPoint(lineX2, lineY));

  // Axis end arrows
  page.drawLine(axisPen, new PdfPoint(lineX2 - 4, lineY - 3), new PdfPoint(lineX2, lineY));
  page.drawLine(axisPen, new PdfPoint(lineX2 - 4, lineY + 3), new PdfPoint(lineX2, lineY));

  const showValues = attrs.values !== 'false';
  const dotR = 5;
  const labelFont = new PdfFont(PdfFont.FONT_NAME, 7);
  const dateFont = new PdfFont(PdfFont.FONT_NAME, 6);
  const dateZoneH = showValues ? 9 : 0; // space below/above axis for date labels
  const connLen = Math.min(22, rect.height / 2 - dotR - dateZoneH - 10);
  const connPen = new PdfPen(PdfColor.fromRgb(160, 160, 160), 0.5);

  for (let i = 0; i < sorted.length; i++) {
    const x = lineX1 + ((sorted[i].value - minVal) / valRange) * lineW;
    const above = i % 2 === 0;
    const color = PdfColor.fromHex(colors[i % colors.length]);

    page.drawEllipse(
      null,
      new PdfBrush(color),
      new PdfRect(x - dotR, lineY - dotR, dotR * 2, dotR * 2)
    );

    const connStartY = lineY + (above ? -dotR : dotR);
    const connEndY = lineY + (above ? -(dotR + connLen) : dotR + connLen);
    page.drawLine(connPen, new PdfPoint(x, connStartY), new PdfPoint(x, connEndY));

    // Event label at the end of the connector
    const lh = 9;
    const ly = above ? connEndY - lh : connEndY;
    page.drawString(
      sorted[i].label,
      labelFont,
      new PdfBrush(textColor),
      null,
      new PdfRect(x - 25, ly, 50, lh),
      ETextAlign.Center
    );

    // Date/value label on the opposite side of the axis from the event label
    if (showValues) {
      const dateText = sorted[i].rawLabel ?? String(Math.round(sorted[i].value));
      const dateY = above ? lineY + dotR + 1 : lineY - dotR - 8;
      page.drawString(
        dateText,
        dateFont,
        new PdfBrush(textColor),
        null,
        new PdfRect(x - 18, dateY, 36, 8),
        ETextAlign.Center
      );
    }
  }
}

function drawVennChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const showValues = attrs.values !== 'false';
  // Separate individual sets from intersections using single-char detection
  const singleChars = new Set(
    data.filter((d) => d.label.trim().length === 1).map((d) => d.label.trim().toUpperCase())
  );
  const sets =
    singleChars.size >= 2
      ? data.filter((d) => d.label.trim().length === 1)
      : data.slice(0, Math.min(3, data.length)); // fallback: first entries are sets
  const n = Math.min(sets.length, 3);
  if (n < 2) return;

  // Find intersection entries: label is a combination of the individual set chars
  const findIntersection = (...ids: number[]) => {
    const keys = ids.map((i) => sets[i].label.trim().toUpperCase());
    return data.find((d) => {
      const u = d.label.trim().toUpperCase().replace(/\s/g, '');
      return u.length === keys.join('').length && keys.every((k) => u.includes(k));
    });
  };

  const labelFont = new PdfFont(PdfFont.FONT_NAME, 8);
  const valFont = new PdfFont(PdfFont.FONT_NAME, 7);
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const r = Math.min(rect.width, rect.height) * 0.32;
  const overlap = r * 0.45;

  // Circle centres
  let centres: { x: number; y: number }[];
  if (n === 2) {
    centres = [
      { x: cx - overlap / 2, y: cy },
      { x: cx + overlap / 2, y: cy },
    ];
  } else {
    const vOff = r * 0.3;
    centres = [
      { x: cx - overlap / 2, y: cy - vOff },
      { x: cx + overlap / 2, y: cy - vOff },
      { x: cx, y: cy + vOff * 1.5 },
    ];
  }

  // Draw circles with semi-transparent fills
  for (let i = 0; i < n; i++) {
    const rgb = hexToRgb(colors[i % colors.length]);
    page.drawEllipse(
      new PdfPen(PdfColor.fromRgb(rgb.r, rgb.g, rgb.b), 1),
      new PdfBrush(PdfColor.fromRgb(rgb.r, rgb.g, rgb.b, 0.3)),
      new PdfRect(centres[i].x - r, centres[i].y - r, r * 2, r * 2)
    );
  }

  // Helper to draw label + value at a position
  const drawRegion = (
    label: string,
    value: number | undefined,
    px: number,
    py: number,
    colorIdx: number
  ) => {
    const textBrush = new PdfBrush(PdfColor.fromHex(colors[colorIdx % colors.length]));
    page.drawString(
      label,
      labelFont,
      textBrush,
      null,
      new PdfRect(px - 25, py - (showValues ? 6 : 4), 50, 9),
      ETextAlign.Center
    );
    if (showValues && value !== undefined) {
      page.drawString(
        String(value),
        valFont,
        PdfBrush.Black,
        null,
        new PdfRect(px - 20, py + 3, 40, 8),
        ETextAlign.Center
      );
    }
  };

  // Set labels at outer arcs (exclusive region direction)
  const labelOffsets =
    n === 2
      ? [
          { dx: -r * 0.55, dy: -r * 0.4 },
          { dx: r * 0.55, dy: -r * 0.4 },
        ]
      : [
          { dx: -r * 0.5, dy: -r * 0.55 },
          { dx: r * 0.5, dy: -r * 0.55 },
          { dx: 0, dy: r * 0.75 },
        ];

  for (let i = 0; i < n; i++) {
    const lx = centres[i].x + labelOffsets[i].dx;
    const ly = centres[i].y + labelOffsets[i].dy;
    drawRegion(sets[i].label, sets[i].value, lx, ly, i);
  }

  // Intersection values
  if (showValues) {
    if (n === 2) {
      const ab = findIntersection(0, 1);
      if (ab) {
        page.drawString(
          String(ab.value),
          valFont,
          PdfBrush.Black,
          null,
          new PdfRect(cx - 18, cy - 4, 36, 8),
          ETextAlign.Center
        );
      }
    } else {
      const ab = findIntersection(0, 1);
      const ac = findIntersection(0, 2);
      const bc = findIntersection(1, 2);
      const abc = findIntersection(0, 1, 2);
      const midAB = { x: (centres[0].x + centres[1].x) / 2, y: (centres[0].y + centres[1].y) / 2 };
      const midAC = { x: (centres[0].x + centres[2].x) / 2, y: (centres[0].y + centres[2].y) / 2 };
      const midBC = { x: (centres[1].x + centres[2].x) / 2, y: (centres[1].y + centres[2].y) / 2 };
      const centroid = {
        x: (centres[0].x + centres[1].x + centres[2].x) / 3,
        y: (centres[0].y + centres[1].y + centres[2].y) / 3,
      };
      // Push pairwise midpoints slightly toward the overlapping region (away from the 3rd circle)
      const push = (
        mid: { x: number; y: number },
        away: { x: number; y: number },
        dist: number
      ) => {
        const dx = mid.x - away.x,
          dy = mid.y - away.y;
        const len = Math.hypot(dx, dy) || 1;
        return { x: mid.x + (dx / len) * dist, y: mid.y + (dy / len) * dist };
      };
      const posAB = push(midAB, centres[2], r * 0.2);
      const posAC = push(midAC, centres[1], r * 0.2);
      const posBC = push(midBC, centres[0], r * 0.2);
      if (ab)
        page.drawString(
          String(ab.value),
          valFont,
          PdfBrush.Black,
          null,
          new PdfRect(posAB.x - 18, posAB.y - 4, 36, 8),
          ETextAlign.Center
        );
      if (ac)
        page.drawString(
          String(ac.value),
          valFont,
          PdfBrush.Black,
          null,
          new PdfRect(posAC.x - 18, posAC.y - 4, 36, 8),
          ETextAlign.Center
        );
      if (bc)
        page.drawString(
          String(bc.value),
          valFont,
          PdfBrush.Black,
          null,
          new PdfRect(posBC.x - 18, posBC.y - 4, 36, 8),
          ETextAlign.Center
        );
      if (abc)
        page.drawString(
          String(abc.value),
          valFont,
          PdfBrush.Black,
          null,
          new PdfRect(centroid.x - 18, centroid.y - 4, 36, 8),
          ETextAlign.Center
        );
    }
  }
}

// ─── Tier-2 multi-column chart types ─────────────────────────────────────────

function drawScatterChart(
  page: PageGen,
  rect: PdfRect,
  data: MultiDataPoint[],
  headers: string[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const textColor = PdfColor.fromHex(palette.textColor);
  const outlineColor = palette.outlineColor ? PdfColor.fromHex(palette.outlineColor) : null;

  const xVals = data.map((d) => d.values[0] ?? 0);
  const yVals = data.map((d) => d.values[1] ?? 0);
  const minX = Math.min(...xVals),
    maxX = Math.max(...xVals);
  const minY = Math.min(...yVals),
    maxY = Math.max(...yVals);
  const xRange = maxX - minX || 1;
  const yRange = maxY - minY || 1;

  const axisX = rect.left + 20;
  const baselineY = rect.bottom - 20;
  const chartW = rect.width - 28;
  const chartH = rect.height - 30;

  const axisPen = outlineColor
    ? new PdfPen(outlineColor, 1)
    : new PdfPen(PdfColor.fromRgb(0, 0, 0), 1);
  page.drawLine(axisPen, new PdfPoint(axisX, rect.top + 5), new PdfPoint(axisX, baselineY));
  page.drawLine(axisPen, new PdfPoint(axisX, baselineY), new PdfPoint(rect.right - 5, baselineY));

  // Light grid lines
  const gridPen = new PdfPen(PdfColor.fromRgb(220, 220, 220), 0.5);
  for (const f of [0.25, 0.5, 0.75]) {
    const gx = axisX + f * chartW;
    const gy = baselineY - f * chartH;
    page.drawLine(gridPen, new PdfPoint(gx, rect.top + 5), new PdfPoint(gx, baselineY));
    page.drawLine(gridPen, new PdfPoint(axisX, gy), new PdfPoint(rect.right - 5, gy));
  }

  const toX = (v: number) => axisX + ((v - minX) / xRange) * chartW;
  const toY = (v: number) => baselineY - ((v - minY) / yRange) * chartH;

  const dotR = 3;
  for (let i = 0; i < data.length; i++) {
    const px = toX(data[i].values[0] ?? 0);
    const py = toY(data[i].values[1] ?? 0);
    const color = PdfColor.fromHex(colors[i % colors.length]);
    page.drawEllipse(
      outlineColor ? new PdfPen(outlineColor, 0.5) : null,
      new PdfBrush(color),
      new PdfRect(px - dotR, py - dotR, dotR * 2, dotR * 2)
    );
  }

  // Axis min/max labels
  const axFont = new PdfFont(PdfFont.FONT_NAME, 6);
  page.drawString(
    String(Math.round(minX * 10) / 10),
    axFont,
    new PdfBrush(textColor),
    null,
    new PdfRect(axisX - 5, baselineY + 2, 20, 7),
    ETextAlign.Center
  );
  page.drawString(
    String(Math.round(maxX * 10) / 10),
    axFont,
    new PdfBrush(textColor),
    null,
    new PdfRect(rect.right - 18, baselineY + 2, 20, 7),
    ETextAlign.Center
  );
  page.drawString(
    String(Math.round(maxY * 10) / 10),
    axFont,
    new PdfBrush(textColor),
    null,
    new PdfRect(rect.left, rect.top + 3, 18, 7),
    ETextAlign.CenterRight
  );

  // Axis header labels
  if (headers.length >= 2) {
    const hFont = new PdfFont(PdfFont.FONT_NAME, 7);
    page.drawString(
      headers[0],
      hFont,
      new PdfBrush(textColor),
      null,
      new PdfRect(axisX, baselineY + 10, chartW, 9),
      ETextAlign.Center
    );
  }
}

function drawBubbleChart(
  page: PageGen,
  rect: PdfRect,
  data: MultiDataPoint[],
  headers: string[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const textColor = PdfColor.fromHex(palette.textColor);
  const outlineColor = palette.outlineColor ? PdfColor.fromHex(palette.outlineColor) : null;

  const xVals = data.map((d) => d.values[0] ?? 0);
  const yVals = data.map((d) => d.values[1] ?? 0);
  const sizeVals = data.map((d) => d.values[2] ?? 1);
  const minX = Math.min(...xVals),
    maxX = Math.max(...xVals);
  const minY = Math.min(...yVals),
    maxY = Math.max(...yVals);
  const maxSize = Math.max(...sizeVals) || 1;

  const axisX = rect.left + 15;
  const baselineY = rect.bottom - 20;
  const chartW = rect.width - 23;
  const chartH = rect.height - 28;
  const maxR = Math.min(chartW, chartH) * 0.1;
  // Shrink the plotable area by maxR on each side so bubbles stay within bounds
  const plotX1 = axisX + maxR;
  const plotX2 = axisX + chartW - maxR;
  const plotY1 = baselineY - chartH + maxR;
  const plotY2 = baselineY - maxR;
  const plotW = plotX2 - plotX1;
  const plotH = plotY2 - plotY1;

  const axisPen = outlineColor
    ? new PdfPen(outlineColor, 1)
    : new PdfPen(PdfColor.fromRgb(0, 0, 0), 1);
  page.drawLine(axisPen, new PdfPoint(axisX, rect.top + 5), new PdfPoint(axisX, baselineY));
  page.drawLine(axisPen, new PdfPoint(axisX, baselineY), new PdfPoint(rect.right - 5, baselineY));

  const toX = (v: number) => plotX1 + ((v - minX) / (maxX - minX || 1)) * plotW;
  const toY = (v: number) => plotY2 - ((v - minY) / (maxY - minY || 1)) * plotH;

  // Largest bubbles first so small ones appear on top
  const sorted = data
    .map((d, i) => ({ d, i }))
    .sort((a, b) => (b.d.values[2] ?? 0) - (a.d.values[2] ?? 0));

  for (const { d, i } of sorted) {
    const px = toX(d.values[0] ?? 0);
    const py = toY(d.values[1] ?? 0);
    const bR = Math.sqrt((d.values[2] ?? 1) / maxSize) * maxR;
    const rgb = hexToRgb(colors[i % colors.length]);
    page.drawEllipse(
      outlineColor ? new PdfPen(outlineColor, 0.5) : null,
      new PdfBrush(PdfColor.fromRgb(rgb.r, rgb.g, rgb.b, 0.7)),
      new PdfRect(px - bR, py - bR, bR * 2, bR * 2)
    );
    if (d.label) {
      const lFont = new PdfFont(PdfFont.FONT_NAME, 6);
      page.drawString(
        d.label,
        lFont,
        PdfBrush.Black,
        null,
        new PdfRect(px - 20, py - 4, 40, 8),
        ETextAlign.Center
      );
    }
  }
}

function drawHeatmapChart(
  page: PageGen,
  rect: PdfRect,
  data: MultiDataPoint[],
  headers: string[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const showValues = attrs.values !== 'false';
  const textColor = PdfColor.fromHex(palette.textColor);
  const nRows = data.length;
  const nCols = Math.max(...data.map((d) => d.values.length));
  if (nRows === 0 || nCols === 0) return;

  const rowLabelW = Math.min(rect.width * 0.25, 35);
  const colHeaderH = 12;
  const cellW = (rect.width - rowLabelW) / nCols;
  const cellH = (rect.height - colHeaderH) / nRows;

  const allVals = data.flatMap((d) => d.values.filter((v) => !isNaN(v)));
  const minVal = Math.min(...allVals);
  const maxVal = Math.max(...allVals);
  const valRange = maxVal - minVal || 1;

  const lerp = (a: number, b: number, t: number) => Math.round(a + (b - a) * t);
  const cellColor = (v: number) => {
    const t = (v - minVal) / valRange;
    return PdfColor.fromRgb(lerp(240, 20, t), lerp(240, 80, t), lerp(240, 200, t));
  };

  const labelFont = new PdfFont(PdfFont.FONT_NAME, 7);
  const cellFont = new PdfFont(PdfFont.FONT_NAME, 6);

  // Column headers
  for (let c = 0; c < nCols; c++) {
    const x = rect.left + rowLabelW + c * cellW;
    page.drawString(
      headers[c] ?? String(c + 1),
      labelFont,
      new PdfBrush(textColor),
      null,
      new PdfRect(x, rect.top, cellW, colHeaderH),
      ETextAlign.Center
    );
  }

  // Rows
  for (let r = 0; r < nRows; r++) {
    const rowY = rect.top + colHeaderH + r * cellH;
    page.drawString(
      data[r].label,
      labelFont,
      new PdfBrush(textColor),
      null,
      new PdfRect(rect.left, rowY, rowLabelW - 2, cellH),
      ETextAlign.CenterRight
    );

    for (let c = 0; c < nCols; c++) {
      const v = data[r].values[c] ?? 0;
      const x = rect.left + rowLabelW + c * cellW;
      page.drawRectangle(
        null,
        new PdfBrush(cellColor(v)),
        new PdfRect(x + 0.5, rowY + 0.5, cellW - 1, cellH - 1)
      );
      if (showValues && cellW > 8 && cellH > 6) {
        const t = (v - minVal) / valRange;
        const vBrush = t > 0.5 ? PdfBrush.White : PdfBrush.Black;
        page.drawString(
          String(Math.round(v * 10) / 10),
          cellFont,
          vBrush,
          null,
          new PdfRect(x, rowY, cellW, cellH),
          ETextAlign.Center
        );
      }
    }
  }
}

function drawBoxPlotChart(
  page: PageGen,
  rect: PdfRect,
  data: MultiDataPoint[],
  headers: string[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const showValues = attrs.values !== 'false';
  const colors = getPaletteColors(palette.name);
  const textColor = PdfColor.fromHex(palette.textColor);
  const outlineColor = palette.outlineColor ? PdfColor.fromHex(palette.outlineColor) : null;

  // values: [min, q1, median, q3, max]
  const allVals = data.flatMap((d) => d.values.filter((v) => !isNaN(v)));
  const minAll = Math.min(...allVals),
    maxAll = Math.max(...allVals);
  const yRange = maxAll - minAll || 1;

  const axisX = rect.left + 15;
  const baselineY = rect.bottom - 20;
  const chartH = rect.height - 30;
  const colW = (rect.width - 25) / data.length;

  const axisPen = outlineColor
    ? new PdfPen(outlineColor, 1)
    : new PdfPen(PdfColor.fromRgb(0, 0, 0), 1);
  page.drawLine(axisPen, new PdfPoint(axisX, rect.top + 5), new PdfPoint(axisX, baselineY));
  page.drawLine(axisPen, new PdfPoint(axisX, baselineY), new PdfPoint(rect.right - 5, baselineY));

  const toY = (v: number) => baselineY - ((v - minAll) / yRange) * chartH;
  const labelFont = new PdfFont(PdfFont.FONT_NAME, 7);

  for (let i = 0; i < data.length; i++) {
    const v = data[i].values;
    const minV = v[0] ?? 0,
      q1 = v[1] ?? 0,
      med = v[2] ?? 0,
      q3 = v[3] ?? 0,
      maxV = v[4] ?? 0;
    const cx = axisX + 5 + i * colW + colW / 2;
    const boxW = colW * 0.55;

    const yMin = toY(minV),
      yQ1 = toY(q1),
      yMed = toY(med),
      yQ3 = toY(q3),
      yMax = toY(maxV);
    const rgb = hexToRgb(colors[i % colors.length]);
    const whiskerPen = new PdfPen(PdfColor.fromRgb(80, 80, 80), 1);
    const capPen = new PdfPen(PdfColor.fromRgb(80, 80, 80), 1);
    const medPen = new PdfPen(PdfColor.fromRgb(40, 40, 40), 2);

    // Whiskers
    page.drawLine(whiskerPen, new PdfPoint(cx, yMin), new PdfPoint(cx, yQ1));
    page.drawLine(whiskerPen, new PdfPoint(cx, yQ3), new PdfPoint(cx, yMax));
    // Caps
    page.drawLine(capPen, new PdfPoint(cx - boxW / 4, yMin), new PdfPoint(cx + boxW / 4, yMin));
    page.drawLine(capPen, new PdfPoint(cx - boxW / 4, yMax), new PdfPoint(cx + boxW / 4, yMax));
    // Box (Q1–Q3)
    page.drawRectangle(
      new PdfPen(PdfColor.fromRgb(60, 60, 60), 0.8),
      new PdfBrush(PdfColor.fromRgb(rgb.r, rgb.g, rgb.b, 0.5)),
      new PdfRect(cx - boxW / 2, yQ3, boxW, yQ1 - yQ3)
    );
    // Median
    page.drawLine(medPen, new PdfPoint(cx - boxW / 2, yMed), new PdfPoint(cx + boxW / 2, yMed));
    // Label
    page.drawString(
      data[i].label,
      labelFont,
      new PdfBrush(textColor),
      null,
      new PdfRect(cx - colW / 2, baselineY + 1, colW, 8),
      ETextAlign.Center
    );
    // Value labels
    if (showValues) {
      const vf = new PdfFont(PdfFont.FONT_NAME, 6);
      const vBrush = new PdfBrush(PdfColor.fromRgb(60, 60, 60));
      const vx = cx + boxW / 2 + 1;
      page.drawString(
        String(maxV),
        vf,
        vBrush,
        null,
        new PdfRect(vx, yMax - 4, 18, 8),
        ETextAlign.TopLeft
      );
      page.drawString(
        String(med),
        vf,
        vBrush,
        null,
        new PdfRect(vx, yMed - 4, 18, 8),
        ETextAlign.TopLeft
      );
      page.drawString(
        String(minV),
        vf,
        vBrush,
        null,
        new PdfRect(vx, yMin - 4, 18, 8),
        ETextAlign.TopLeft
      );
    }
  }
}

function drawPopulationPyramidChart(
  page: PageGen,
  rect: PdfRect,
  data: MultiDataPoint[],
  headers: string[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const showValues = attrs.values !== 'false';
  const colors = getPaletteColors(palette.name);
  const textColor = PdfColor.fromHex(palette.textColor);
  const outlineColor = palette.outlineColor ? PdfColor.fromHex(palette.outlineColor) : null;

  const leftVals = data.map((d) => Math.abs(d.values[0] ?? 0));
  const rightVals = data.map((d) => Math.abs(d.values[1] ?? 0));
  const maxVal = Math.max(...leftVals, ...rightVals) || 1;

  const labelW = 28;
  const topPad = 14;
  const barAreaW = (rect.width - labelW) / 2;
  const centerX = rect.left + barAreaW;
  const availH = rect.height - topPad;
  const rowH = availH / data.length;
  const barH = Math.max(3, rowH * 0.7);

  const leftColor = PdfColor.fromHex(colors[0]);
  const rightColor = PdfColor.fromHex(colors[1] ?? colors[0]);
  const headerFont = new PdfFont(PdfFont.FONT_NAME, 7);
  const labelFont = new PdfFont(PdfFont.FONT_NAME, 8);

  // Headers
  page.drawString(
    headers[0] || 'Left',
    headerFont,
    new PdfBrush(leftColor),
    null,
    new PdfRect(rect.left, rect.top, barAreaW, topPad),
    ETextAlign.Center
  );
  page.drawString(
    headers[1] || 'Right',
    headerFont,
    new PdfBrush(rightColor),
    null,
    new PdfRect(centerX + labelW, rect.top, barAreaW, topPad),
    ETextAlign.Center
  );

  for (let i = 0; i < data.length; i++) {
    const cy = rect.top + topPad + rowH * (i + 0.5);
    const barY = cy - barH / 2;
    const lW = (leftVals[i] / maxVal) * barAreaW;
    const rW = (rightVals[i] / maxVal) * barAreaW;

    page.drawRectangle(
      outlineColor ? new PdfPen(outlineColor, 0.5) : null,
      new PdfBrush(leftColor),
      new PdfRect(centerX - lW, barY, lW, barH)
    );
    page.drawRectangle(
      outlineColor ? new PdfPen(outlineColor, 0.5) : null,
      new PdfBrush(rightColor),
      new PdfRect(centerX + labelW, barY, rW, barH)
    );
    page.drawString(
      data[i].label,
      labelFont,
      new PdfBrush(textColor),
      null,
      new PdfRect(centerX, cy - rowH / 2, labelW, rowH),
      ETextAlign.Center
    );
    if (showValues) {
      const vf = new PdfFont(PdfFont.FONT_NAME, 6);
      const vBrush = new PdfBrush(PdfColor.fromRgb(60, 60, 60));
      if (lW > 8)
        page.drawString(
          String(leftVals[i]),
          vf,
          vBrush,
          null,
          new PdfRect(centerX - lW, cy - 4, lW - 1, 8),
          ETextAlign.CenterRight
        );
      if (rW > 8)
        page.drawString(
          String(rightVals[i]),
          vf,
          vBrush,
          null,
          new PdfRect(centerX + labelW + 1, cy - 4, rW - 1, 8),
          ETextAlign.CenterLeft
        );
    }
  }
}

function drawProgressChart(
  page: PageGen,
  rect: PdfRect,
  data: MultiDataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const textColor = PdfColor.fromHex(palette.textColor);
  const outlineColor = palette.outlineColor ? PdfColor.fromHex(palette.outlineColor) : null;

  const rowH = rect.height / data.length;
  const barH = Math.max(6, Math.min(rowH * 0.45, 18));
  const labelW = rect.width * 0.28;
  const trackX = rect.left + labelW;
  const trackW = rect.width - labelW - 4;

  const labelFont = new PdfFont(PdfFont.FONT_NAME, 8);
  const valueFont = new PdfFont(PdfFont.FONT_NAME, 7);
  const trackBrush = new PdfBrush(PdfColor.fromRgb(220, 220, 220));
  const trackPen = outlineColor ? new PdfPen(outlineColor, 0.5) : null;

  for (let i = 0; i < data.length; i++) {
    const value = data[i].values[0] ?? 0;
    const max = data[i].values[1] ?? 100;
    const fraction = Math.min(1, Math.max(0, value / (max || 1)));
    const cy = rect.top + rowH * (i + 0.5);
    const barY = cy - barH / 2;
    const r = barH / 2;

    page.drawString(
      data[i].label,
      labelFont,
      new PdfBrush(textColor),
      null,
      new PdfRect(rect.left, barY, labelW - 4, barH),
      ETextAlign.CenterRight
    );

    // Track
    page.drawRoundedRectangle(trackPen, trackBrush, new PdfRect(trackX, barY, trackW, barH), r);

    // Fill
    if (fraction > 0) {
      const fillW = Math.max(barH, trackW * fraction); // at least as wide as height for round cap
      const color = PdfColor.fromHex(colors[i % colors.length]);
      page.drawRoundedRectangle(
        null,
        new PdfBrush(color),
        new PdfRect(trackX, barY, fillW, barH),
        r
      );

      if (fillW > 20) {
        const pctBrush = isLightColor(colors[i % colors.length]) ? PdfBrush.Black : PdfBrush.White;
        page.drawString(
          `${Math.round(fraction * 100)}%`,
          valueFont,
          pctBrush,
          null,
          new PdfRect(trackX, barY, fillW, barH),
          ETextAlign.Center
        );
      }
    }
  }
}

// ─── Treemap ──────────────────────────────────────────────────────────────────

function drawTreemapChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const showValues = attrs.values !== 'false';
  const colors = getPaletteColors(palette.name);

  // Sort descending by value and assign color by sorted order
  const sorted = [...data].sort((a, b) => b.value - a.value);
  const colorMap = new Map<string, string>();
  sorted.forEach((d, i) => colorMap.set(`${d.label  }_${  i}`, colors[i % colors.length]));

  const total = sorted.reduce((s, d) => s + d.value, 0) || 1;

  function drawTile(items: { d: DataPoint; hex: string }[], r: PdfRect): void {
    if (items.length === 0) return;
    if (items.length === 1) {
      const { d, hex } = items[0];
      const brush = new PdfBrush(PdfColor.fromHex(hex));
      const pen = new PdfPen(PdfColor.fromRgb(255, 255, 255), 1);
      page.drawRectangle(pen, brush, r);
      const labelBrush = isLightColor(hex) ? PdfBrush.Black : PdfBrush.White;
      const labelSize = Math.min(9, r.height * 0.3);
      const valueSize = Math.min(7, r.height * 0.22);
      const inset = 2;
      if (r.width > 10 && r.height > 8) {
        const lf = new PdfFont(PdfFont.FONT_NAME, Math.max(5, labelSize));
        page.drawString(
          d.label,
          lf,
          labelBrush,
          null,
          new PdfRect(
            r.left + inset,
            r.top + inset,
            r.width - inset * 2,
            Math.min(12, r.height * 0.4)
          ),
          ETextAlign.TopLeft
        );
        if (showValues && r.height > 16) {
          const vf = new PdfFont(PdfFont.FONT_NAME, Math.max(4, valueSize));
          page.drawString(
            String(d.value),
            vf,
            labelBrush,
            null,
            new PdfRect(
              r.left + inset,
              r.top + inset + Math.min(10, r.height * 0.35),
              r.width - inset * 2,
              Math.min(10, r.height * 0.3)
            ),
            ETextAlign.TopLeft
          );
        }
      }
      return;
    }

    // Split along longer axis at cumulative midpoint
    const sumAll = items.reduce((s, x) => s + x.d.value, 0);
    let cumSum = 0;
    let splitIdx = 0;
    for (let i = 0; i < items.length - 1; i++) {
      cumSum += items[i].d.value;
      if (cumSum >= sumAll / 2) {
        splitIdx = i + 1;
        break;
      }
      splitIdx = i + 1;
    }
    if (splitIdx === 0) splitIdx = 1;

    const firstHalf = items.slice(0, splitIdx);
    const secondHalf = items.slice(splitIdx);
    const fraction = firstHalf.reduce((s, x) => s + x.d.value, 0) / sumAll;

    let r1: PdfRect, r2: PdfRect;
    if (r.width >= r.height) {
      const w1 = r.width * fraction;
      r1 = new PdfRect(r.left, r.top, w1, r.height);
      r2 = new PdfRect(r.left + w1, r.top, r.width - w1, r.height);
    } else {
      const h1 = r.height * fraction;
      r1 = new PdfRect(r.left, r.top, r.width, h1);
      r2 = new PdfRect(r.left, r.top + h1, r.width, r.height - h1);
    }

    drawTile(firstHalf, r1);
    drawTile(secondHalf, r2);
  }

  const items = sorted.map((d, i) => ({ d, hex: colors[i % colors.length] }));
  drawTile(items, rect);
}

// ─── Rose (Nightingale) ───────────────────────────────────────────────────────

function drawRoseChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const showValues = attrs.values !== 'false';
  const colors = getPaletteColors(palette.name);

  if (data.length === 0) return;

  const n = data.length;
  const sectorAngle = 360 / n;
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const maxR = Math.min(rect.width, rect.height) / 2 - 14;
  const maxValue = Math.max(...data.map((d) => d.value)) || 1;

  const grayPen = new PdfPen(PdfColor.fromRgb(180, 180, 180), 0.5);
  const whitePen = new PdfPen(PdfColor.fromRgb(255, 255, 255), 1);
  const labelFont = new PdfFont(PdfFont.FONT_NAME, 6.5);
  const valueFont = new PdfFont(PdfFont.FONT_NAME, 5.5);

  // Draw axis lines first (at each sector boundary)
  for (let i = 0; i < n; i++) {
    const angleDeg = -90 + i * sectorAngle;
    const angleRad = (angleDeg * Math.PI) / 180;
    page.drawLine(
      grayPen,
      new PdfPoint(cx, cy),
      new PdfPoint(cx + maxR * Math.cos(angleRad), cy + maxR * Math.sin(angleRad))
    );
  }

  for (let i = 0; i < n; i++) {
    const startAngle = -90 + i * sectorAngle;
    const endAngle = startAngle + sectorAngle;
    const r = (data[i].value / maxValue) * maxR;
    const hex = colors[i % colors.length];
    const { r: cr, g: cg, b: cb } = hexToRgb(hex);
    const brush = new PdfBrush(PdfColor.fromRgb(cr, cg, cb, 0.85));
    const sectorRect = new PdfRect(cx - r, cy - r, r * 2, r * 2);

    page.drawPie(whitePen, brush, sectorRect, startAngle, endAngle);

    // Label at outside
    const midAngleDeg = -90 + (i + 0.5) * sectorAngle;
    const midAngleRad = (midAngleDeg * Math.PI) / 180;
    const labelDist = maxR + 6;
    const lx = cx + labelDist * Math.cos(midAngleRad);
    const ly = cy + labelDist * Math.sin(midAngleRad);
    const lw = 28,
      lh = 8;
    page.drawString(
      data[i].label,
      labelFont,
      PdfBrush.Black,
      null,
      new PdfRect(lx - lw / 2, ly - lh / 2, lw, lh),
      ETextAlign.Center
    );

    // Value inside slice
    if (showValues && r > 10) {
      const vDist = r * 0.6;
      const vx = cx + vDist * Math.cos(midAngleRad);
      const vy = cy + vDist * Math.sin(midAngleRad);
      const vBrush = isLightColor(hex) ? PdfBrush.Black : PdfBrush.White;
      page.drawString(
        String(data[i].value),
        valueFont,
        vBrush,
        null,
        new PdfRect(vx - 10, vy - 4, 20, 8),
        ETextAlign.Center
      );
    }
  }
}

// ─── Strip Plot ───────────────────────────────────────────────────────────────

function drawStripChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const showValues = attrs.values !== 'false';
  const colors = getPaletteColors(palette.name);

  if (data.length === 0) return;

  // Group by label preserving insertion order
  const categories: string[] = [];
  const grouped = new Map<string, number[]>();
  for (const d of data) {
    if (!grouped.has(d.label)) {
      categories.push(d.label);
      grouped.set(d.label, []);
    }
    grouped.get(d.label)!.push(d.value);
  }

  const allVals = data.map((d) => d.value);
  const minV = Math.min(...allVals);
  const maxV = Math.max(...allVals);
  const range = maxV - minV || 1;

  const labelW = 32;
  const axisX = rect.left + labelW;
  const axisW = rect.width - labelW - 4;
  const rowH = rect.height / categories.length;

  const axisLinePen = new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5);
  const labelFont = new PdfFont(PdfFont.FONT_NAME, 7);
  const tickFont = new PdfFont(PdfFont.FONT_NAME, 5.5);

  for (let ci = 0; ci < categories.length; ci++) {
    const cat = categories[ci];
    const vals = grouped.get(cat)!;
    const midY = rect.top + rowH * (ci + 0.5);
    const hex = colors[ci % colors.length];
    const { r, g, b } = hexToRgb(hex);

    // Axis line
    page.drawLine(axisLinePen, new PdfPoint(axisX, midY), new PdfPoint(axisX + axisW, midY));

    // Category label
    page.drawString(
      cat,
      labelFont,
      PdfBrush.Black,
      null,
      new PdfRect(rect.left, midY - rowH / 2, labelW - 2, rowH),
      ETextAlign.CenterRight
    );

    // Dots
    for (const v of vals) {
      const x = axisX + ((v - minV) / range) * axisW;
      const dotR = 3.5;
      const dotBrush = new PdfBrush(PdfColor.fromRgb(r, g, b, 0.7));
      page.drawEllipse(null, dotBrush, new PdfRect(x - dotR, midY - dotR, dotR * 2, dotR * 2));
      if (showValues) {
        page.drawString(
          String(v),
          tickFont,
          PdfBrush.Black,
          null,
          new PdfRect(x - 8, midY + dotR + 1, 16, 6),
          ETextAlign.Center
        );
      }
    }
  }

  // Min/max labels at bottom
  page.drawString(
    String(minV),
    tickFont,
    PdfBrush.Black,
    null,
    new PdfRect(axisX - 4, rect.bottom - 7, 20, 7),
    ETextAlign.TopLeft
  );
  page.drawString(
    String(maxV),
    tickFont,
    PdfBrush.Black,
    null,
    new PdfRect(axisX + axisW - 20, rect.bottom - 7, 20, 7),
    ETextAlign.TopRight
  );
}

// ─── Icon Array ───────────────────────────────────────────────────────────────

function drawIconArrayChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const showValues = attrs.values !== 'false';
  const colors = getPaletteColors(palette.name);

  const total = data.reduce((s, d) => s + d.value, 0) || 100;
  const cols = 10,
    rows = 10;
  const legendH = showValues ? 14 : 0;
  const gridH = rect.height - legendH;
  const cellW = rect.width / cols;
  const cellH = gridH / rows;
  const iconR = Math.min(cellW, cellH) * 0.36;

  // Build array of 100 colors
  const iconColors: string[] = [];
  let remaining = 100;
  for (let i = 0; i < data.length; i++) {
    const count = i === data.length - 1 ? remaining : Math.round((data[i].value / total) * 100);
    const c = Math.min(count, remaining);
    for (let j = 0; j < c; j++) iconColors.push(colors[i % colors.length]);
    remaining -= c;
    if (remaining <= 0) break;
  }
  while (iconColors.length < 100) iconColors.push('#E0E0E0');

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const idx = row * cols + col;
      const cx = rect.left + (col + 0.5) * cellW;
      const cy = rect.top + (row + 0.5) * cellH;
      const hex = iconColors[idx];
      const brush = new PdfBrush(PdfColor.fromHex(hex));
      page.drawEllipse(null, brush, new PdfRect(cx - iconR, cy - iconR, iconR * 2, iconR * 2));
    }
  }

  // Legend
  if (showValues) {
    const legendY = rect.top + gridH + 2;
    let lx = rect.left;
    const legendFont = new PdfFont(PdfFont.FONT_NAME, 5.5);
    const perCat = rect.width / data.length;
    for (let i = 0; i < data.length; i++) {
      const hex = colors[i % colors.length];
      const brush = new PdfBrush(PdfColor.fromHex(hex));
      const dotR = 3;
      page.drawEllipse(
        null,
        brush,
        new PdfRect(lx, legendY + legendH / 2 - dotR, dotR * 2, dotR * 2)
      );
      const pct = Math.round((data[i].value / total) * 100);
      page.drawString(
        `${data[i].label}: ${pct}%`,
        legendFont,
        PdfBrush.Black,
        null,
        new PdfRect(lx + dotR * 2 + 1, legendY, perCat - dotR * 2 - 2, legendH),
        ETextAlign.CenterLeft
      );
      lx += perCat;
    }
  }
}

// ─── Bullet Chart ─────────────────────────────────────────────────────────────

function drawBulletChart(
  page: PageGen,
  rect: PdfRect,
  data: MultiDataPoint[],
  headers: string[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const showValues = attrs.values !== 'false';
  const colors = getPaletteColors(palette.name);

  const labelW = rect.width * 0.28;
  const rowH = rect.height / data.length;
  const barAreaX = rect.left + labelW;
  const barAreaW = rect.width - labelW - 4;

  const labelFont = new PdfFont(PdfFont.FONT_NAME, 7);
  const valueFont = new PdfFont(PdfFont.FONT_NAME, 6);
  const bandColors = ['#DDDDDD', '#BBBBBB', '#999999'];

  for (let i = 0; i < data.length; i++) {
    const row = data[i];
    const value = row.values[0] ?? 0;
    const target = isNaN(row.values[1]) ? undefined : row.values[1];
    const rawBands = row.values.slice(2).filter((v) => !isNaN(v));
    // maxVal = largest of value, target, and all band values so nothing overflows
    const maxVal = Math.max(value, target ?? 0, ...rawBands, 1);
    const bands = rawBands.length > 0 ? rawBands : [maxVal * 0.6, maxVal * 0.8, maxVal];

    const cy = rect.top + rowH * (i + 0.5);
    const barH = rowH * 0.35;
    const bandH = rowH * 0.7;
    const bandY = cy - bandH / 2;
    const barY = cy - barH / 2;

    // Draw background bands (3 bands, lightest to darkest)
    const nBands = Math.min(bands.length, 3);
    for (let b = nBands - 1; b >= 0; b--) {
      const bW = (bands[b] / maxVal) * barAreaW;
      const bHex = bandColors[b];
      page.drawRectangle(
        null,
        new PdfBrush(PdfColor.fromHex(bHex)),
        new PdfRect(barAreaX, bandY, bW, bandH)
      );
    }

    // Value bar
    const vW = (value / maxVal) * barAreaW;
    const hex = colors[i % colors.length];
    page.drawRectangle(
      null,
      new PdfBrush(PdfColor.fromHex(hex)),
      new PdfRect(barAreaX, barY, vW, barH)
    );

    // Target marker (only if target defined and within range)
    const darkPen = new PdfPen(PdfColor.fromRgb(30, 30, 30), 2);
    if (target !== undefined) {
      const tx = Math.min((target / maxVal) * barAreaW + barAreaX, barAreaX + barAreaW);
      const markerH = rowH * 0.8;
      page.drawLine(
        darkPen,
        new PdfPoint(tx, cy - markerH / 2),
        new PdfPoint(tx, cy + markerH / 2)
      );
    }

    // Label
    page.drawString(
      row.label,
      labelFont,
      PdfBrush.Black,
      null,
      new PdfRect(rect.left, cy - rowH / 2, labelW - 4, rowH),
      ETextAlign.CenterRight
    );

    // Value text
    if (showValues) {
      page.drawString(
        String(value),
        valueFont,
        PdfBrush.Black,
        null,
        new PdfRect(barAreaX + vW + 2, cy - 4, 20, 8),
        ETextAlign.CenterLeft
      );
    }
  }
}

// ─── Parallel Coordinates ─────────────────────────────────────────────────────

function drawParallelCoordsChart(
  page: PageGen,
  rect: PdfRect,
  data: MultiDataPoint[],
  headers: string[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const showValues = attrs.values !== 'false';
  const colors = getPaletteColors(palette.name);

  const n = headers.length;
  if (n < 2) return;

  // Fixed vertical zones (px from top):
  //   [0..9]   axis name labels
  //  [9..17]   max-value ticks
  //  [17..-21] axis lines (drawing area)
  //  [-21..-13] min-value ticks
  //  [-13..0]  legend row
  const axisNameH = 9;
  const maxTickH = 8;
  const minTickH = 8;
  const legendH = 12;
  const axisTop = rect.top + axisNameH + maxTickH;
  const axisBot = rect.bottom - minTickH - legendH;

  const pad = 14;
  const axisXs: number[] = [];
  for (let j = 0; j < n; j++) {
    axisXs[j] = rect.left + pad + (j * (rect.width - pad * 2)) / (n - 1);
  }

  // Per-axis min/max
  const mins: number[] = Array(n).fill(Infinity);
  const maxs: number[] = Array(n).fill(-Infinity);
  for (const row of data) {
    for (let j = 0; j < n; j++) {
      const v = row.values[j] ?? 0;
      if (v < mins[j]) mins[j] = v;
      if (v > maxs[j]) maxs[j] = v;
    }
  }

  const toY = (v: number, j: number) =>
    axisTop + (1 - (v - mins[j]) / (maxs[j] - mins[j] || 1)) * (axisBot - axisTop);

  const axisFont = new PdfFont(PdfFont.FONT_NAME, 6.5);
  const tickFont = new PdfFont(PdfFont.FONT_NAME, 5.5);
  const axisLinePen = new PdfPen(PdfColor.fromRgb(80, 80, 80), 1);

  // Data lines (drawn before axes so axes appear on top)
  for (let i = 0; i < data.length; i++) {
    const hex = colors[i % colors.length];
    const { r, g, b } = hexToRgb(hex);
    const pen = new PdfPen(PdfColor.fromRgb(r, g, b, 0.65), 1.2);
    const pts: PdfPoint[] = [];
    for (let j = 0; j < n; j++) {
      pts.push(new PdfPoint(axisXs[j], toY(data[i].values[j] ?? mins[j], j)));
    }
    page.drawLines(pen, pts);

    // Crossing-point dots when showValues
    if (showValues) {
      for (let j = 0; j < n; j++) {
        const py = toY(data[i].values[j] ?? mins[j], j);
        const { r: dr, g: dg, b: db } = hexToRgb(hex);
        page.drawEllipse(
          null,
          new PdfBrush(PdfColor.fromRgb(dr, dg, db)),
          new PdfRect(axisXs[j] - 2, py - 2, 4, 4)
        );
      }
    }
  }

  // Axes + labels
  for (let j = 0; j < n; j++) {
    page.drawLine(axisLinePen, new PdfPoint(axisXs[j], axisTop), new PdfPoint(axisXs[j], axisBot));
    // Axis name — centered above the axis line
    page.drawString(
      headers[j],
      axisFont,
      PdfBrush.Black,
      null,
      new PdfRect(axisXs[j] - 18, rect.top, 36, axisNameH),
      ETextAlign.Center
    );
    // Max value just below axis name
    if (isFinite(maxs[j])) {
      page.drawString(
        String(Math.round(maxs[j] * 10) / 10),
        tickFont,
        PdfBrush.Black,
        null,
        new PdfRect(axisXs[j] - 12, rect.top + axisNameH, 24, maxTickH),
        ETextAlign.Center
      );
    }
    // Min value just below axis line
    if (isFinite(mins[j])) {
      page.drawString(
        String(Math.round(mins[j] * 10) / 10),
        tickFont,
        PdfBrush.Black,
        null,
        new PdfRect(axisXs[j] - 12, axisBot + 1, 24, minTickH),
        ETextAlign.Center
      );
    }
  }

  // Legend — always shown, sits in the reserved legendH zone at the bottom
  const legFont = new PdfFont(PdfFont.FONT_NAME, 6);
  const legY = rect.bottom - legendH + 1;
  const maxLegItems = Math.min(data.length, 6);
  const perItem = rect.width / maxLegItems;
  for (let i = 0; i < maxLegItems; i++) {
    const hex = colors[i % colors.length];
    const lx = rect.left + perItem * i;
    const dotR = 2.5;
    page.drawEllipse(
      null,
      new PdfBrush(PdfColor.fromHex(hex)),
      new PdfRect(lx, legY + (legendH - dotR * 2) / 2, dotR * 2, dotR * 2)
    );
    page.drawString(
      data[i].label,
      legFont,
      PdfBrush.Black,
      null,
      new PdfRect(lx + dotR * 2 + 2, legY, perItem - dotR * 2 - 3, legendH),
      ETextAlign.CenterLeft
    );
  }
}

// ─── Gantt Chart ──────────────────────────────────────────────────────────────

function drawGanttChart(
  page: PageGen,
  rect: PdfRect,
  data: MultiDataPoint[],
  headers: string[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const showValues = attrs.values !== 'false';
  const colors = getPaletteColors(palette.name);

  const allStarts = data.map((d) => d.values[0] ?? 0);
  const allEnds = data.map((d) => d.values[1] ?? 0);
  const minT = Math.min(...allStarts);
  const maxT = Math.max(...allEnds);
  const range = maxT - minT || 1;

  const labelW = Math.min(rect.width * 0.35, 50);
  const axisH = 12;
  const chartX = rect.left + labelW;
  const chartW = rect.width - labelW - 2;
  const rowH = (rect.height - axisH) / data.length;
  const barH = Math.max(4, rowH * 0.55);

  const toX = (t: number) => chartX + ((t - minT) / range) * chartW;

  const gridPen = new PdfPen(PdfColor.fromRgb(210, 210, 210), 0.5);
  const labelFont = new PdfFont(PdfFont.FONT_NAME, 7);
  const tickFont = new PdfFont(PdfFont.FONT_NAME, 6);
  const barTextFont = new PdfFont(PdfFont.FONT_NAME, 6);

  // Vertical grid lines at 0%, 25%, 50%, 75%, 100%
  for (const frac of [0, 0.25, 0.5, 0.75, 1]) {
    const gx = chartX + frac * chartW;
    page.drawLine(gridPen, new PdfPoint(gx, rect.top), new PdfPoint(gx, rect.bottom - axisH));
    const tickVal = minT + frac * range;
    page.drawString(
      String(Math.round(tickVal * 10) / 10),
      tickFont,
      PdfBrush.Black,
      null,
      new PdfRect(gx - 10, rect.bottom - axisH, 20, axisH),
      ETextAlign.Center
    );
  }

  for (let i = 0; i < data.length; i++) {
    const start = data[i].values[0] ?? 0;
    const end = data[i].values[1] ?? 0;
    const cy = rect.top + rowH * (i + 0.5);
    const barY = cy - barH / 2;
    const bx = toX(start);
    const bw = Math.max(2, toX(end) - bx);
    const hex = colors[i % colors.length];

    page.drawRoundedRectangle(
      null,
      new PdfBrush(PdfColor.fromHex(hex)),
      new PdfRect(bx, barY, bw, barH),
      barH / 2
    );

    page.drawString(
      data[i].label,
      labelFont,
      PdfBrush.Black,
      null,
      new PdfRect(rect.left, cy - rowH / 2, labelW - 2, rowH),
      ETextAlign.CenterRight
    );

    if (showValues && bw > 14) {
      const valBrush = isLightColor(hex) ? PdfBrush.Black : PdfBrush.White;
      page.drawString(
        `${start}–${end}`,
        barTextFont,
        valBrush,
        null,
        new PdfRect(bx + 2, barY, bw - 4, barH),
        ETextAlign.CenterLeft
      );
    }
  }
}

// ─── Dot Map ──────────────────────────────────────────────────────────────────

function drawDotMapChart(
  page: PageGen,
  rect: PdfRect,
  data: MultiDataPoint[],
  headers: string[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const showValues = attrs.values !== 'false';
  const colors = getPaletteColors(palette.name);

  const allSizes = data.map((d) => d.values[2] ?? 0);
  const maxSize = Math.max(...allSizes) || 1;
  const maxR = Math.min(rect.width, rect.height) * 0.08;

  const gridPen = new PdfPen(PdfColor.fromRgb(220, 220, 220), 0.5);
  const labelFont = new PdfFont(PdfFont.FONT_NAME, 6);
  const valueFont = new PdfFont(PdfFont.FONT_NAME, 5.5);

  // Grid lines
  for (const frac of [0.25, 0.5, 0.75]) {
    const gx = rect.left + frac * rect.width;
    const gy = rect.top + frac * rect.height;
    page.drawLine(gridPen, new PdfPoint(gx, rect.top), new PdfPoint(gx, rect.bottom));
    page.drawLine(gridPen, new PdfPoint(rect.left, gy), new PdfPoint(rect.right, gy));
  }

  // Sort by size descending (draw largest first = behind)
  const sorted = [...data].sort((a, b) => (b.values[2] ?? 0) - (a.values[2] ?? 0));

  for (let i = 0; i < sorted.length; i++) {
    const row = sorted[i];
    const origIdx = data.indexOf(row);
    const px = rect.left + ((row.values[0] ?? 50) / 100) * rect.width;
    const py = rect.top + ((row.values[1] ?? 50) / 100) * rect.height;
    const sz = row.values[2] ?? 0;
    const bR = Math.sqrt(sz / maxSize) * maxR;
    const hex = colors[origIdx % colors.length];
    const { r, g, b } = hexToRgb(hex);
    const brush = new PdfBrush(PdfColor.fromRgb(r, g, b, 0.75));

    page.drawEllipse(null, brush, new PdfRect(px - bR, py - bR, bR * 2, bR * 2));
    page.drawString(
      row.label,
      labelFont,
      PdfBrush.Black,
      null,
      new PdfRect(px - 15, py - 4, 30, 8),
      ETextAlign.Center
    );

    if (showValues) {
      page.drawString(
        String(sz),
        valueFont,
        PdfBrush.Black,
        null,
        new PdfRect(px - 15, py + 4, 30, 7),
        ETextAlign.Center
      );
    }
  }
}

// ─── Network Chart ────────────────────────────────────────────────────────────

function drawNetworkChart(
  page: PageGen,
  rect: PdfRect,
  data: NetworkData,
  attrs: Record<string, string>,
  palette: Palette
): void {
  const showValues = attrs.values !== 'false';
  const colors = getPaletteColors(palette.name);

  const n = data.nodes.length;
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const nodeR = Math.min(rect.width, rect.height) / 2 - 20;
  const dotR = Math.min(8, nodeR * 0.12);

  // Compute node positions
  const nodePos: PdfPoint[] = data.nodes.map((_, i) => {
    const angle = -Math.PI / 2 + (2 * Math.PI * i) / n;
    return new PdfPoint(cx + nodeR * Math.cos(angle), cy + nodeR * Math.sin(angle));
  });

  const edgePen = new PdfPen(PdfColor.fromRgb(160, 160, 160), 0.8);

  // Draw edges first
  for (const [from, to] of data.edges) {
    if (from < nodePos.length && to < nodePos.length) {
      page.drawLine(edgePen, nodePos[from], nodePos[to]);
    }
  }

  // Draw nodes
  const nodePen = new PdfPen(PdfColor.fromRgb(255, 255, 255), 1);
  const labelFont = new PdfFont(PdfFont.FONT_NAME, 6.5);

  for (let i = 0; i < n; i++) {
    const pos = nodePos[i];
    const hex = colors[i % colors.length];
    const brush = new PdfBrush(PdfColor.fromHex(hex));
    page.drawEllipse(nodePen, brush, new PdfRect(pos.x - dotR, pos.y - dotR, dotR * 2, dotR * 2));

    if (showValues) {
      const angle = -Math.PI / 2 + (2 * Math.PI * i) / n;
      const labelDist = nodeR + dotR + 7;
      const lx = cx + labelDist * Math.cos(angle);
      const ly = cy + labelDist * Math.sin(angle);
      page.drawString(
        data.nodes[i],
        labelFont,
        PdfBrush.Black,
        null,
        new PdfRect(lx - 20, ly - 4, 40, 8),
        ETextAlign.Center
      );
    }
  }
}

// ─── Sunburst Parser ──────────────────────────────────────────────────────────

function parseSunburstData(source: string): SunburstGroup[] {
  const lines = source.trim().split('\n');
  let headerSkipped = false,
    separatorFound = false;
  const rows: { group: string; sub: string; value: number }[] = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    if (/^\|[\s\-:|]+\|$/.test(trimmed)) {
      separatorFound = true;
      continue;
    }
    if (trimmed.startsWith('|')) {
      const cells = trimmed
        .split('|')
        .map((c) => c.trim())
        .filter((c) => c !== '');
      if (cells.length >= 2) {
        if (!headerSkipped && !separatorFound) {
          headerSkipped = true;
          continue;
        }
        const value = parseCzechFloat(cells[cells.length - 1]);
        if (!isNaN(value)) {
          const group = cells[0];
          const sub = cells.length >= 3 ? cells[1] : cells[0];
          rows.push({ group, sub, value });
        }
      }
    }
  }

  const groupMap = new Map<string, SunburstGroup>();
  for (const row of rows) {
    if (!groupMap.has(row.group)) {
      groupMap.set(row.group, { label: row.group, total: 0, children: [] });
    }
    const g = groupMap.get(row.group)!;
    g.children.push({ label: row.sub, value: row.value });
    g.total += row.value;
  }

  return Array.from(groupMap.values());
}

// ─── Arc/Sankey Parser ────────────────────────────────────────────────────────

function parseArcData(source: string): ArcData {
  const lines = source.trim().split('\n');
  let headerSkipped = false,
    separatorFound = false;
  const rawEdges: { from: string; to: string; weight: number }[] = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    if (/^\|[\s\-:|]+\|$/.test(trimmed)) {
      separatorFound = true;
      continue;
    }
    if (trimmed.startsWith('|')) {
      const cells = trimmed
        .split('|')
        .map((c) => c.trim())
        .filter((c) => c !== '');
      if (cells.length >= 2) {
        if (!headerSkipped && !separatorFound) {
          headerSkipped = true;
          continue;
        }
        const from = cells[0];
        const to = cells[1];
        const weight = cells.length >= 3 ? parseCzechFloat(cells[2]) || 1 : 1;
        rawEdges.push({ from, to, weight });
      }
    }
  }

  const nodeSet = new Map<string, number>();
  const addNode = (name: string) => {
    if (!nodeSet.has(name)) nodeSet.set(name, nodeSet.size);
  };
  for (const e of rawEdges) {
    addNode(e.from);
    addNode(e.to);
  }

  const nodes = Array.from(nodeSet.keys());
  const edges = rawEdges.map((e) => ({
    from: nodeSet.get(e.from)!,
    to: nodeSet.get(e.to)!,
    weight: e.weight,
  }));

  return { nodes, edges };
}

// ─── Marimekko Chart ─────────────────────────────────────────────────────────

function drawMarimekkoChart(
  page: PageGen,
  rect: PdfRect,
  data: MultiDataPoint[],
  headers: string[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const showValues = attrs.values !== 'false';
  const colors = getPaletteColors(palette.name);

  // headers[0] = weight column, headers[1..] = segment names
  const segNames = headers.slice(1);
  const segCount = segNames.length;
  if (segCount === 0) return;

  const labelH = 14;
  const legendH = 12;
  const chartH = rect.height - labelH - legendH;

  const totalWeight = data.reduce((s, d) => s + (d.values[0] ?? 0), 0) || 1;
  const borderPen = new PdfPen(PdfColor.fromRgb(255, 255, 255), 0.8);
  const labelFont = new PdfFont(PdfFont.FONT_NAME, 6.5);
  const valueFont = new PdfFont(PdfFont.FONT_NAME, 5.5);

  let xCursor = rect.left;
  for (const row of data) {
    const colW = ((row.values[0] ?? 0) / totalWeight) * rect.width;
    const segSum = row.values.slice(1).reduce((s, v) => s + (v ?? 0), 0) || 1;

    let yCursor = rect.top;
    for (let s = 0; s < segCount; s++) {
      const segVal = row.values[s + 1] ?? 0;
      const segH = (segVal / segSum) * chartH;
      const hex = colors[s % colors.length];
      const brush = new PdfBrush(PdfColor.fromHex(hex));
      const segRect = new PdfRect(xCursor, yCursor, colW, segH);
      page.drawRectangle(borderPen, brush, segRect);

      if (showValues && segH > 8 && colW > 14) {
        const lb = isLightColor(hex) ? PdfBrush.Black : PdfBrush.White;
        page.drawString(
          String(Math.round(segVal)),
          valueFont,
          lb,
          null,
          new PdfRect(xCursor + 1, yCursor + 1, colW - 2, segH - 2),
          ETextAlign.Center
        );
      }
      yCursor += segH;
    }

    page.drawString(
      row.label,
      labelFont,
      PdfBrush.Black,
      null,
      new PdfRect(xCursor, rect.top + chartH, colW, labelH),
      ETextAlign.Center
    );

    xCursor += colW;
  }

  // Legend
  const legFont = new PdfFont(PdfFont.FONT_NAME, 6);
  const legY = rect.bottom - legendH + 1;
  const maxLeg = Math.min(segCount, 6);
  const perItem = rect.width / maxLeg;
  for (let s = 0; s < maxLeg; s++) {
    const hex = colors[s % colors.length];
    const lx = rect.left + perItem * s;
    page.drawRectangle(
      null,
      new PdfBrush(PdfColor.fromHex(hex)),
      new PdfRect(lx, legY + 3, 5, legendH - 6)
    );
    page.drawString(
      segNames[s],
      legFont,
      PdfBrush.Black,
      null,
      new PdfRect(lx + 7, legY, perItem - 8, legendH),
      ETextAlign.CenterLeft
    );
  }
}

// ─── Streamgraph Chart ────────────────────────────────────────────────────────

function drawStreamgraphChart(
  page: PageGen,
  rect: PdfRect,
  data: MultiDataPoint[],
  headers: string[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const showValues = attrs.values !== 'false';
  const colors = getPaletteColors(palette.name);

  const n = data.length;
  const m = headers.length;
  if (n < 2 || m === 0) return;

  const legendH = 10;
  const xLabelH = 10;
  const plotTop = rect.top + legendH;
  const plotBottom = rect.bottom - xLabelH;
  const plotH = plotBottom - plotTop;

  const totals = data.map((d) => d.values.slice(0, m).reduce((s, v) => s + (v ?? 0), 0));
  const maxTotal = Math.max(...totals) || 1;
  const scaleH = plotH / maxTotal;

  // Draw series as filled polygons (top edge forward, bottom edge backward)
  for (let s = 0; s < m; s++) {
    const pts: PdfPoint[] = [];

    for (let i = 0; i < n; i++) {
      const baseline = ((maxTotal - totals[i]) / 2) * scaleH;
      const cumBelow = data[i].values.slice(0, s).reduce((a, b) => a + (b ?? 0), 0);
      const y = plotTop + baseline + cumBelow * scaleH;
      const x = rect.left + (i / (n - 1)) * rect.width;
      pts.push(new PdfPoint(x, y));
    }

    for (let i = n - 1; i >= 0; i--) {
      const baseline = ((maxTotal - totals[i]) / 2) * scaleH;
      const cumBelow = data[i].values.slice(0, s).reduce((a, b) => a + (b ?? 0), 0);
      const bandH = (data[i].values[s] ?? 0) * scaleH;
      const y = plotTop + baseline + cumBelow * scaleH + bandH;
      const x = rect.left + (i / (n - 1)) * rect.width;
      pts.push(new PdfPoint(x, y));
    }

    const hex = colors[s % colors.length];
    const { r, g, b } = hexToRgb(hex);
    page.drawPolygon(null, new PdfBrush(PdfColor.fromRgb(r, g, b, 0.8)), pts);
  }

  // X-axis labels
  const labelFont = new PdfFont(PdfFont.FONT_NAME, 6);
  for (let i = 0; i < n; i++) {
    const x = rect.left + (i / (n - 1)) * rect.width;
    page.drawString(
      data[i].label,
      labelFont,
      PdfBrush.Black,
      null,
      new PdfRect(x - 15, plotBottom + 1, 30, xLabelH),
      ETextAlign.Center
    );
  }

  // Legend (top)
  const legFont = new PdfFont(PdfFont.FONT_NAME, 6);
  const maxLeg = Math.min(m, 6);
  const perItem = rect.width / maxLeg;
  for (let s = 0; s < maxLeg; s++) {
    const hex = colors[s % colors.length];
    const { r, g, b } = hexToRgb(hex);
    const lx = rect.left + perItem * s;
    page.drawEllipse(
      null,
      new PdfBrush(PdfColor.fromRgb(r, g, b)),
      new PdfRect(lx, rect.top + 2, 5, 5)
    );
    page.drawString(
      headers[s],
      legFont,
      PdfBrush.Black,
      null,
      new PdfRect(lx + 7, rect.top, perItem - 8, legendH),
      ETextAlign.CenterLeft
    );
  }
}

// ─── Sunburst Chart ───────────────────────────────────────────────────────────

function drawSunburstChart(
  page: PageGen,
  rect: PdfRect,
  data: SunburstGroup[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const showValues = attrs.values !== 'false';
  const colors = getPaletteColors(palette.name);

  if (data.length === 0) return;

  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const outerR = Math.min(rect.width, rect.height) / 2 - 6;
  const innerR = outerR * 0.55;
  const holeR = outerR * 0.25;

  const grand = data.reduce((s, g) => s + g.total, 0) || 1;
  const outerRect = (r: number) => new PdfRect(cx - r, cy - r, r * 2, r * 2);

  const labelFont = new PdfFont(PdfFont.FONT_NAME, 6);
  const whiteSep = new PdfPen(PdfColor.fromRgb(255, 255, 255), 0.8);

  let groupAngle = -90;

  // Phase 1: draw outer (child) slices
  let childColorIdx = 0;
  for (let gi = 0; gi < data.length; gi++) {
    const group = data[gi];
    const groupSpan = (group.total / grand) * 360;
    let childAngle = groupAngle;
    for (const child of group.children) {
      const childSpan = (child.value / grand) * 360;
      const hex = colors[childColorIdx % colors.length];
      const { r, g, b } = hexToRgb(hex);
      page.drawPie(
        whiteSep,
        new PdfBrush(PdfColor.fromRgb(r, g, b, 0.75)),
        outerRect(outerR),
        childAngle,
        childAngle + childSpan
      );
      childColorIdx++;
      childAngle += childSpan;
    }
    groupAngle += groupSpan;
  }

  // Phase 2: draw inner (group) slices — covers inner part of children
  groupAngle = -90;
  for (let gi = 0; gi < data.length; gi++) {
    const group = data[gi];
    const groupSpan = (group.total / grand) * 360;
    const hex = colors[gi % colors.length];
    const { r, g, b } = hexToRgb(hex);
    page.drawPie(
      whiteSep,
      new PdfBrush(PdfColor.fromRgb(r, g, b, 0.9)),
      outerRect(innerR),
      groupAngle,
      groupAngle + groupSpan
    );
    groupAngle += groupSpan;
  }

  // Phase 3: white center hole
  page.drawEllipse(null, PdfBrush.White, outerRect(holeR));

  // Phase 4: labels
  groupAngle = -90;
  childColorIdx = 0;
  for (let gi = 0; gi < data.length; gi++) {
    const group = data[gi];
    const groupSpan = (group.total / grand) * 360;
    const groupHex = colors[gi % colors.length];
    const groupMidRad = ((groupAngle + groupSpan / 2) * Math.PI) / 180;
    const gLabelR = (innerR + holeR) / 2;

    // Group label
    if (groupSpan > 14) {
      const lx = cx + gLabelR * Math.cos(groupMidRad);
      const ly = cy + gLabelR * Math.sin(groupMidRad);
      const lb = isLightColor(groupHex) ? PdfBrush.Black : PdfBrush.White;
      page.drawString(
        group.label,
        labelFont,
        lb,
        null,
        new PdfRect(lx - 15, ly - 4, 30, 8),
        ETextAlign.Center
      );
    }

    // Child labels
    if (showValues) {
      let childAngle = groupAngle;
      for (const child of group.children) {
        const childSpan = (child.value / grand) * 360;
        if (childSpan > 10) {
          const midRad = ((childAngle + childSpan / 2) * Math.PI) / 180;
          const cLabelR = (innerR + outerR) / 2;
          const lx = cx + cLabelR * Math.cos(midRad);
          const ly = cy + cLabelR * Math.sin(midRad);
          const childHex = colors[childColorIdx % colors.length];
          const lb = isLightColor(childHex) ? PdfBrush.Black : PdfBrush.White;
          page.drawString(
            child.label,
            labelFont,
            lb,
            null,
            new PdfRect(lx - 15, ly - 4, 30, 8),
            ETextAlign.Center
          );
        }
        childAngle += childSpan;
        childColorIdx++;
      }
    } else {
      childColorIdx += group.children.length;
    }

    groupAngle += groupSpan;
  }
}

// ─── Arc Diagram ─────────────────────────────────────────────────────────────

function drawArcDiagramChart(
  page: PageGen,
  rect: PdfRect,
  data: ArcData,
  attrs: Record<string, string>,
  palette: Palette
): void {
  const showValues = attrs.values !== 'false';
  const colors = getPaletteColors(palette.name);

  const n = data.nodes.length;
  if (n === 0) return;

  const nodeAreaH = Math.min(18, rect.height * 0.22);
  const baseline = rect.bottom - nodeAreaH;
  const spacing = rect.width / (n + 1);
  const nodeXs = data.nodes.map((_, i) => rect.left + spacing * (i + 1));
  const dotR = Math.min(4, spacing * 0.12);

  const maxWeight = Math.max(...data.edges.map((e) => e.weight), 1);
  const labelFont = new PdfFont(PdfFont.FONT_NAME, 6.5);

  // Draw arcs
  for (let idx = 0; idx < data.edges.length; idx++) {
    const { from, to, weight } = data.edges[idx];
    if (from >= n || to >= n || from === to) continue;

    const x1 = nodeXs[Math.min(from, to)];
    const x2 = nodeXs[Math.max(from, to)];
    const midX = (x1 + x2) / 2;
    const radius = (x2 - x1) / 2;
    const thickness = Math.max(0.5, (weight / maxWeight) * 3);
    const hex = colors[idx % colors.length];
    const { r, g, b } = hexToRgb(hex);
    const arcPen = new PdfPen(PdfColor.fromRgb(r, g, b, 0.7), thickness);

    // Upper semicircle above baseline: center=(midX, baseline), start=180°, end=360°
    const arcRect = new PdfRect(midX - radius, baseline - radius, radius * 2, radius * 2);
    page.drawArc(arcPen, null, arcRect, 180, 360);
  }

  // Draw nodes
  const nodePen = new PdfPen(PdfColor.fromRgb(255, 255, 255), 0.8);
  for (let i = 0; i < n; i++) {
    const hex = colors[i % colors.length];
    page.drawEllipse(
      nodePen,
      new PdfBrush(PdfColor.fromHex(hex)),
      new PdfRect(nodeXs[i] - dotR, baseline - dotR, dotR * 2, dotR * 2)
    );

    page.drawString(
      data.nodes[i],
      labelFont,
      PdfBrush.Black,
      null,
      new PdfRect(nodeXs[i] - 15, baseline + dotR + 1, 30, nodeAreaH - dotR - 2),
      ETextAlign.Center
    );
  }
}

// ─── Sankey Chart ─────────────────────────────────────────────────────────────

function drawSankeyChart(
  page: PageGen,
  rect: PdfRect,
  data: ArcData,
  attrs: Record<string, string>,
  palette: Palette
): void {
  const showValues = attrs.values !== 'false';
  const colors = getPaletteColors(palette.name);

  const n = data.nodes.length;
  if (n === 0) return;

  // Compute per-node inflow and outflow
  const outFlow = new Array(n).fill(0);
  const inFlow = new Array(n).fill(0);
  for (const e of data.edges) {
    outFlow[e.from] = (outFlow[e.from] ?? 0) + e.weight;
    inFlow[e.to] = (inFlow[e.to] ?? 0) + e.weight;
  }

  // Left column = nodes with outFlow > 0 (source-side)
  // Right column = nodes with inFlow > 0 (sink-side)
  // Collect in order of first appearance
  const leftNames: string[] = [];
  const rightNames: string[] = [];
  const leftIdxMap = new Map<string, number>();
  const rightIdxMap = new Map<string, number>();

  for (const e of data.edges) {
    const fn = data.nodes[e.from];
    const tn = data.nodes[e.to];
    if (!leftIdxMap.has(fn)) {
      leftIdxMap.set(fn, leftNames.length);
      leftNames.push(fn);
    }
    if (!rightIdxMap.has(tn)) {
      rightIdxMap.set(tn, rightNames.length);
      rightNames.push(tn);
    }
  }

  const nodeBarW = Math.min(10, rect.width * 0.06);
  const labelAreaW = Math.min(40, rect.width * 0.22);
  const barAreaX = rect.left + labelAreaW;
  const barAreaRight = rect.right - labelAreaW;
  const leftBarX = barAreaX;
  const rightBarX = barAreaRight - nodeBarW;
  const pad = 6;

  // Compute left node y positions
  const totalLeftOut =
    leftNames.reduce((s, n2) => s + (outFlow[data.nodes.indexOf(n2)] ?? 0), 0) || 1;
  const totalRightIn =
    rightNames.reduce((s, n2) => s + (inFlow[data.nodes.indexOf(n2)] ?? 0), 0) || 1;
  const leftGapTotal = pad * Math.max(leftNames.length - 1, 0);
  const rightGapTotal = pad * Math.max(rightNames.length - 1, 0);
  const leftBarAreaH = rect.height - leftGapTotal;
  const rightBarAreaH = rect.height - rightGapTotal;

  const leftNodeY = new Map<string, { top: number; h: number; cur: number }>();
  let yCur = rect.top;
  for (const nm of leftNames) {
    const nIdx = data.nodes.indexOf(nm);
    const h = Math.max(4, ((outFlow[nIdx] ?? 0) / totalLeftOut) * leftBarAreaH);
    leftNodeY.set(nm, { top: yCur, h, cur: yCur });
    yCur += h + pad;
  }

  const rightNodeY = new Map<string, { top: number; h: number; cur: number }>();
  yCur = rect.top;
  for (const nm of rightNames) {
    const nIdx = data.nodes.indexOf(nm);
    const h = Math.max(4, ((inFlow[nIdx] ?? 0) / totalRightIn) * rightBarAreaH);
    rightNodeY.set(nm, { top: yCur, h, cur: yCur });
    yCur += h + pad;
  }

  const midX = (leftBarX + nodeBarW + rightBarX) / 2;
  const valueFont = new PdfFont(PdfFont.FONT_NAME, 6);
  const labelFont = new PdfFont(PdfFont.FONT_NAME, 7);

  // Draw flow bands
  for (let ei = 0; ei < data.edges.length; ei++) {
    const { from, to, weight } = data.edges[ei];
    const fromName = data.nodes[from];
    const toName = data.nodes[to];
    const lPos = leftNodeY.get(fromName);
    const rPos = rightNodeY.get(toName);
    if (!lPos || !rPos) continue;

    const totalOut = outFlow[from] || 1;
    const totalIn = inFlow[to] || 1;
    const lBandH = Math.max(1, (weight / totalOut) * lPos.h);
    const rBandH = Math.max(1, (weight / totalIn) * rPos.h);

    const y0 = lPos.cur;
    const y1 = y0 + lBandH;
    lPos.cur = y1;

    const dy0 = rPos.cur;
    const dy1 = dy0 + rBandH;
    rPos.cur = dy1;

    // Bezier ribbon: approximate with polygon
    const pts: PdfPoint[] = [];
    const srcX = leftBarX + nodeBarW;
    const dstX = rightBarX;
    const sampleN = 16;

    // Top edge: src(y0) → dst(dy0)
    for (let t = 0; t <= sampleN; t++) {
      const u = t / sampleN;
      const x =
        (1 - u) * (1 - u) * (1 - u) * srcX +
        3 * (1 - u) * (1 - u) * u * midX +
        3 * (1 - u) * u * u * midX +
        u * u * u * dstX;
      const y =
        (1 - u) * (1 - u) * (1 - u) * y0 +
        3 * (1 - u) * (1 - u) * u * y0 +
        3 * (1 - u) * u * u * dy0 +
        u * u * u * dy0;
      pts.push(new PdfPoint(x, y));
    }
    // Bottom edge: dst(dy1) → src(y1) [reverse]
    for (let t = sampleN; t >= 0; t--) {
      const u = t / sampleN;
      const x =
        (1 - u) * (1 - u) * (1 - u) * srcX +
        3 * (1 - u) * (1 - u) * u * midX +
        3 * (1 - u) * u * u * midX +
        u * u * u * dstX;
      const y =
        (1 - u) * (1 - u) * (1 - u) * y1 +
        3 * (1 - u) * (1 - u) * u * y1 +
        3 * (1 - u) * u * u * dy1 +
        u * u * u * dy1;
      pts.push(new PdfPoint(x, y));
    }

    const hex = colors[ei % colors.length];
    const { r, g, b } = hexToRgb(hex);
    page.drawPolygon(null, new PdfBrush(PdfColor.fromRgb(r, g, b, 0.5)), pts);
  }

  // Draw node bars + labels
  for (const nm of leftNames) {
    const pos = leftNodeY.get(nm)!;
    const nIdx = data.nodes.indexOf(nm);
    const hex = colors[nIdx % colors.length];
    page.drawRectangle(
      null,
      new PdfBrush(PdfColor.fromHex(hex)),
      new PdfRect(leftBarX, pos.top, nodeBarW, pos.h)
    );
    page.drawString(
      nm,
      labelFont,
      PdfBrush.Black,
      null,
      new PdfRect(rect.left, pos.top, labelAreaW - 3, pos.h),
      ETextAlign.CenterRight
    );
    if (showValues) {
      page.drawString(
        String(Math.round(outFlow[nIdx] * 10) / 10),
        valueFont,
        PdfBrush.Black,
        null,
        new PdfRect(rect.left, pos.top + pos.h, labelAreaW - 3, 8),
        ETextAlign.BottomRight
      );
    }
  }

  for (const nm of rightNames) {
    const pos = rightNodeY.get(nm)!;
    const nIdx = data.nodes.indexOf(nm);
    const hex = colors[nIdx % colors.length];
    page.drawRectangle(
      null,
      new PdfBrush(PdfColor.fromHex(hex)),
      new PdfRect(rightBarX, pos.top, nodeBarW, pos.h)
    );
    page.drawString(
      nm,
      labelFont,
      PdfBrush.Black,
      null,
      new PdfRect(rightBarX + nodeBarW + 2, pos.top, labelAreaW - 3, pos.h),
      ETextAlign.CenterLeft
    );
    if (showValues) {
      page.drawString(
        String(Math.round(inFlow[nIdx] * 10) / 10),
        valueFont,
        PdfBrush.Black,
        null,
        new PdfRect(rightBarX + nodeBarW + 2, pos.top + pos.h, labelAreaW - 3, 8),
        ETextAlign.BottomLeft
      );
    }
  }
}

// ─── Calendar Heatmap ─────────────────────────────────────────────────────────

function drawCalendarHeatmapChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const showValues = attrs.values !== 'false';
  const colors = getPaletteColors(palette.name);

  const items = data
    .map((d) => ({ date: new Date(d.label), value: d.value }))
    .filter((x) => !isNaN(x.date.getTime()))
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  if (items.length === 0) return;

  const minDate = items[0].date;
  const maxDate = items[items.length - 1].date;

  const startDay = new Date(minDate);
  startDay.setDate(startDay.getDate() - startDay.getDay()); // back to Sunday

  const endDay = new Date(maxDate);
  endDay.setDate(endDay.getDate() + (6 - endDay.getDay())); // forward to Saturday

  const totalWeeks =
    Math.ceil((endDay.getTime() - startDay.getTime()) / (7 * 24 * 3600 * 1000)) + 1;

  const dateMap = new Map<string, number>();
  for (const item of items) {
    const key = item.date.toISOString().split('T')[0];
    dateMap.set(key, (dateMap.get(key) ?? 0) + item.value);
  }
  const maxValue = Math.max(...Array.from(dateMap.values())) || 1;

  const dayLabelW = 12;
  const monthLabelH = 10;
  const availW = rect.width - dayLabelW;
  const availH = rect.height - monthLabelH;
  const cellW = availW / totalWeeks;
  const cellH = availH / 7;

  const gridLeft = rect.left + dayLabelW;
  const gridTop = rect.top + monthLabelH;

  const baseHex = colors[0];
  const { r: br, g: bg, b: bb } = hexToRgb(baseHex);
  const labelFont = new PdfFont(PdfFont.FONT_NAME, 5.5);

  const dayLetters = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  for (let d = 0; d < 7; d++) {
    page.drawString(
      dayLetters[d],
      labelFont,
      PdfBrush.Black,
      null,
      new PdfRect(rect.left, gridTop + d * cellH, dayLabelW - 1, cellH),
      ETextAlign.CenterRight
    );
  }

  const MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  let lastMonthShown = -1;

  for (let w = 0; w < totalWeeks; w++) {
    const weekStart = new Date(startDay.getTime() + w * 7 * 24 * 3600 * 1000);
    const month = weekStart.getMonth();
    if (month !== lastMonthShown) {
      lastMonthShown = month;
      page.drawString(
        MONTHS[month],
        labelFont,
        PdfBrush.Black,
        null,
        new PdfRect(gridLeft + w * cellW, rect.top, cellW * 4, monthLabelH),
        ETextAlign.CenterLeft
      );
    }

    for (let d = 0; d < 7; d++) {
      const cellDate = new Date(startDay.getTime() + (w * 7 + d) * 24 * 3600 * 1000);
      const key = cellDate.toISOString().split('T')[0];
      const val = dateMap.get(key);
      const px = gridLeft + w * cellW + 0.5;
      const py = gridTop + d * cellH + 0.5;
      const cw = cellW - 1;
      const ch = cellH - 1;

      if (val !== undefined) {
        const intensity = val / maxValue;
        const fr = Math.round(235 + (br - 235) * intensity);
        const fg = Math.round(235 + (bg - 235) * intensity);
        const fb = Math.round(235 + (bb - 235) * intensity);
        page.drawRectangle(
          null,
          new PdfBrush(
            PdfColor.fromRgb(
              Math.max(0, Math.min(255, fr)),
              Math.max(0, Math.min(255, fg)),
              Math.max(0, Math.min(255, fb))
            )
          ),
          new PdfRect(px, py, cw, ch)
        );
        if (showValues && ch > 7) {
          const tb = intensity > 0.55 ? PdfBrush.White : PdfBrush.Black;
          page.drawString(
            String(val),
            new PdfFont(PdfFont.FONT_NAME, Math.min(5, ch * 0.55)),
            tb,
            null,
            new PdfRect(px, py, cw, ch),
            ETextAlign.Center
          );
        }
      } else {
        page.drawRectangle(
          null,
          new PdfBrush(PdfColor.fromRgb(238, 238, 238)),
          new PdfRect(px, py, cw, ch)
        );
      }
    }
  }
}

// ─── Chord Diagram ────────────────────────────────────────────────────────────

function drawChordChart(
  page: PageGen,
  rect: PdfRect,
  data: MultiDataPoint[],
  headers: string[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const showValues = attrs.values !== 'false';
  const colors = getPaletteColors(palette.name);

  const m = data.length;
  if (m < 2) return;

  const nodeNames = data.map((r) => r.label);
  const flow: number[][] = data.map((row) =>
    Array.from({ length: m }, (_, j) => Math.max(0, row.values[j] ?? 0))
  );
  const nodeTotals = flow.map((row) => row.reduce((s, v) => s + v, 0));
  const grandTotal = nodeTotals.reduce((s, t) => s + t, 0) || 1;

  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const outerR = Math.min(rect.width, rect.height) / 2 - 14;
  const innerR = outerR * 0.87;

  const gap = 0.07;
  const totalGap = m * gap;
  const availAngle = 2 * Math.PI - totalGap;

  const nodeSpan = nodeTotals.map((t) => (t / grandTotal) * availAngle);
  const nodeStart: number[] = [];
  let curAngle = -Math.PI / 2;
  for (let i = 0; i < m; i++) {
    nodeStart.push(curAngle);
    curAngle += nodeSpan[i] + gap;
  }

  // Sub-arc positions within each node's arc
  const subStart: number[][] = [];
  for (let i = 0; i < m; i++) {
    subStart.push([]);
    let cur = nodeStart[i];
    for (let j = 0; j < m; j++) {
      subStart[i].push(cur);
      if (nodeTotals[i] > 0) cur += (flow[i][j] / nodeTotals[i]) * nodeSpan[i];
    }
  }

  const pt = (angle: number, r: number) =>
    new PdfPoint(cx + r * Math.cos(angle), cy + r * Math.sin(angle));

  const sampleBezier = (
    x0: number,
    y0: number,
    cpX: number,
    cpY: number,
    x1: number,
    y1: number,
    N = 14
  ): PdfPoint[] => {
    const pts: PdfPoint[] = [];
    for (let t = 0; t <= N; t++) {
      const u = t / N;
      pts.push(
        new PdfPoint(
          (1 - u) * (1 - u) * (1 - u) * x0 +
            3 * (1 - u) * (1 - u) * u * cpX +
            3 * (1 - u) * u * u * cpX +
            u * u * u * x1,
          (1 - u) * (1 - u) * (1 - u) * y0 +
            3 * (1 - u) * (1 - u) * u * cpY +
            3 * (1 - u) * u * u * cpY +
            u * u * u * y1
        )
      );
    }
    return pts;
  };

  const sampleArc = (r: number, fromA: number, toA: number, N = 6): PdfPoint[] => {
    const pts: PdfPoint[] = [];
    for (let t = 0; t <= N; t++) {
      const a = fromA + ((toA - fromA) * t) / N;
      pts.push(new PdfPoint(cx + r * Math.cos(a), cy + r * Math.sin(a)));
    }
    return pts;
  };

  // Draw chords first
  for (let i = 0; i < m; i++) {
    for (let j = i + 1; j < m; j++) {
      const vij = flow[i][j];
      const vji = flow[j][i];
      if (vij === 0 && vji === 0) continue;

      const a1s = subStart[i][j];
      const a1e = a1s + (nodeTotals[i] > 0 ? (vij / nodeTotals[i]) * nodeSpan[i] : 0);
      const a2s = subStart[j][i];
      const a2e = a2s + (nodeTotals[j] > 0 ? (vji / nodeTotals[j]) * nodeSpan[j] : 0);

      const p1s = pt(a1s, innerR);
      const p1e = pt(a1e, innerR);
      const p2s = pt(a2s, innerR);
      const p2e = pt(a2e, innerR);

      // d3-style ribbon: forward arc on i, bezier end-of-i→start-of-j, forward arc on j, bezier back
      const ribbonPts: PdfPoint[] = [
        ...sampleArc(innerR, a1s, a1e, 6),
        ...sampleBezier(p1e.x, p1e.y, cx, cy, p2s.x, p2s.y),
        ...sampleArc(innerR, a2s, a2e, 6),
        ...sampleBezier(p2e.x, p2e.y, cx, cy, p1s.x, p1s.y),
      ];

      const hex = colors[i % colors.length];
      const { r, g, b } = hexToRgb(hex);
      page.drawPolygon(null, new PdfBrush(PdfColor.fromRgb(r, g, b, 0.35)), ribbonPts);
    }
  }

  // Draw outer arc segments
  const labelFont = new PdfFont(PdfFont.FONT_NAME, 6.5);
  for (let i = 0; i < m; i++) {
    if (nodeSpan[i] <= 0) continue;
    const hex = colors[i % colors.length];
    const { r, g, b } = hexToRgb(hex);
    const arcSamples = Math.max(8, Math.round(nodeSpan[i] * 18));

    const arcPts: PdfPoint[] = [
      ...sampleArc(outerR, nodeStart[i], nodeStart[i] + nodeSpan[i], arcSamples),
      ...sampleArc(innerR, nodeStart[i] + nodeSpan[i], nodeStart[i], arcSamples),
    ];
    page.drawPolygon(null, new PdfBrush(PdfColor.fromRgb(r, g, b)), arcPts);

    if (showValues && nodeSpan[i] > 0.15) {
      const midAngle = nodeStart[i] + nodeSpan[i] / 2;
      const labelR = outerR + 9;
      const lx = cx + labelR * Math.cos(midAngle);
      const ly = cy + labelR * Math.sin(midAngle);
      page.drawString(
        nodeNames[i],
        labelFont,
        PdfBrush.Black,
        null,
        new PdfRect(lx - 18, ly - 4, 36, 8),
        ETextAlign.Center
      );
    }
  }
}

// ─── Spiral Chart ─────────────────────────────────────────────────────────────

function drawSpiralChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const showValues = attrs.values !== 'false';
  const colors = getPaletteColors(palette.name);

  const n = data.length;
  if (n === 0) return;

  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const outerR = Math.min(rect.width, rect.height) / 2 - 12;
  const innerR = outerR * 0.18;

  // Auto-detect loop size: if n <= 24 use 1 loop, else 12 per loop
  const loopSize = n <= 12 ? n : n <= 24 ? Math.ceil(n / 2) : 12;
  const loops = Math.ceil(n / loopSize);

  const maxValue = Math.max(...data.map((d) => d.value)) || 1;
  const labelFont = new PdfFont(PdfFont.FONT_NAME, 6);

  // Draw concentric guide rings
  const gridPen = new PdfPen(PdfColor.fromRgb(220, 220, 220), 0.3);
  for (let l = 0; l <= loops; l++) {
    const r = innerR + ((outerR - innerR) * l) / loops;
    page.drawEllipse(gridPen, null, new PdfRect(cx - r, cy - r, r * 2, r * 2));
  }

  for (let i = 0; i < n; i++) {
    const loop = Math.floor(i / loopSize);
    const posInLoop = i % loopSize;
    const angle = -Math.PI / 2 + (posInLoop / loopSize) * 2 * Math.PI;

    const baseR = innerR + ((outerR - innerR) * loop) / loops;
    const nextR = innerR + ((outerR - innerR) * (loop + 1)) / loops;
    const maxBarLen = (nextR - baseR) * 0.8;
    const barLen = (data[i].value / maxValue) * maxBarLen;

    const cosA = Math.cos(angle);
    const sinA = Math.sin(angle);
    const p1 = new PdfPoint(cx + baseR * cosA, cy + baseR * sinA);
    const p2 = new PdfPoint(cx + (baseR + barLen) * cosA, cy + (baseR + barLen) * sinA);

    const hex = colors[i % colors.length];
    const { r, g, b } = hexToRgb(hex);
    const thickness = Math.min(8, Math.max(1.5, ((2 * Math.PI * baseR) / loopSize) * 0.6));
    page.drawLine(new PdfPen(PdfColor.fromRgb(r, g, b), thickness), p1, p2);

    if (showValues) {
      const step = Math.max(1, Math.floor(loopSize / 8));
      if (posInLoop % step === 0) {
        const labelR = baseR - 5;
        const lx = cx + labelR * cosA;
        const ly = cy + labelR * sinA;
        page.drawString(
          data[i].label,
          labelFont,
          PdfBrush.Black,
          null,
          new PdfRect(lx - 12, ly - 4, 24, 8),
          ETextAlign.Center
        );
      }
    }
  }
}

// ─── Word Cloud ───────────────────────────────────────────────────────────────

function drawWordCloudChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);

  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const maxR = Math.min(rect.width, rect.height) / 2 - 8;

  const maxWeight = Math.max(...data.map((d) => d.value)) || 1;
  const minFontSize = 6;
  const maxFontSize = 22;

  // Sort by weight descending (largest placed nearest center)
  const sorted = [...data].sort((a, b) => b.value - a.value);

  const goldenAngle = (137.508 * Math.PI) / 180;

  for (let i = 0; i < sorted.length; i++) {
    const word = sorted[i];
    const t = word.value / maxWeight;
    const size = minFontSize + t * (maxFontSize - minFontSize);
    const font = new PdfFont(PdfFont.FONT_NAME, size);
    const hex = colors[i % colors.length];
    const { r, g, b } = hexToRgb(hex);

    // Golden-angle spiral placement
    const distance = Math.sqrt((i + 0.5) / sorted.length) * maxR * 0.9;
    const angle = i * goldenAngle;
    const wx = cx + distance * Math.cos(angle);
    const wy = cy + distance * Math.sin(angle);

    // Approximate text bounding box
    const approxW = word.label.length * size * 0.52;
    const approxH = size * 1.2;

    page.drawString(
      word.label,
      font,
      new PdfBrush(PdfColor.fromRgb(r, g, b)),
      null,
      new PdfRect(wx - approxW / 2, wy - approxH / 2, approxW, approxH),
      ETextAlign.Center
    );
  }
}

// ─── Pictogram ────────────────────────────────────────────────────────────────

function drawPictogramChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const showValues = attrs.values !== 'false';
  const colors = getPaletteColors(palette.name);

  const n = data.length;
  if (n === 0) return;

  const labelW = Math.min(40, rect.width * 0.28);
  const valueW = showValues ? 18 : 0;
  const iconAreaW = rect.width - labelW - valueW;
  const rowH = rect.height / n;
  const maxValue = Math.max(...data.map((d) => d.value)) || 1;

  const iconR = Math.max(2, Math.min(rowH * 0.32, 5));
  const iconSpacing = iconR * 2 + 1.5;
  const maxIconsPerRow = Math.max(1, Math.floor(iconAreaW / iconSpacing));

  // Scale so the max-value row fits in maxIconsPerRow icons
  const iconUnit = maxValue > maxIconsPerRow ? Math.ceil(maxValue / maxIconsPerRow) : 1;
  const totalIcons = Math.ceil(maxValue / iconUnit);

  const labelFont = new PdfFont(PdfFont.FONT_NAME, Math.min(8, rowH * 0.55));
  const valueFont = new PdfFont(PdfFont.FONT_NAME, 6);
  const scaleFont = new PdfFont(PdfFont.FONT_NAME, 5.5);
  const iconAreaLeft = rect.left + labelW;

  for (let i = 0; i < n; i++) {
    const rowY = rect.top + i * rowH;
    const rowMidY = rowY + rowH / 2;
    const iconsToFill = Math.round(data[i].value / iconUnit);
    const hex = colors[i % colors.length];
    const { r, g, b } = hexToRgb(hex);

    page.drawString(
      data[i].label,
      labelFont,
      PdfBrush.Black,
      null,
      new PdfRect(rect.left, rowY, labelW - 2, rowH),
      ETextAlign.CenterRight
    );

    for (let j = 0; j < totalIcons; j++) {
      const ix = iconAreaLeft + j * iconSpacing + iconR;
      const filled = j < iconsToFill;
      const brush = filled
        ? new PdfBrush(PdfColor.fromRgb(r, g, b, 0.9))
        : new PdfBrush(PdfColor.fromRgb(220, 220, 220));
      page.drawEllipse(null, brush, new PdfRect(ix - iconR, rowMidY - iconR, iconR * 2, iconR * 2));
    }

    if (showValues) {
      page.drawString(
        String(data[i].value),
        valueFont,
        PdfBrush.Black,
        null,
        new PdfRect(iconAreaLeft + totalIcons * iconSpacing + 2, rowY, valueW, rowH),
        ETextAlign.CenterLeft
      );
    }
  }

  if (iconUnit > 1) {
    page.drawString(
      `● = ${iconUnit} units`,
      scaleFont,
      PdfBrush.LightGray,
      null,
      new PdfRect(rect.left, rect.bottom - 8, rect.width, 8),
      ETextAlign.BottomRight
    );
  }
}

// ─── Gauge (Speedometer) ─────────────────────────────────────────────────────

function drawGaugeChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const n = data.length;
  if (n === 0) return;

  const cols = n <= 1 ? 1 : 2;
  const rowCount = Math.ceil(n / cols);
  const cellW = rect.width / cols;
  const cellH = rect.height / rowCount;

  for (let i = 0; i < n; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    drawSingleGauge(
      page,
      new PdfRect(rect.left + col * cellW, rect.top + row * cellH, cellW, cellH),
      data[i],
      attrs,
      colors[i % colors.length]
    );
  }
}

function drawSingleGauge(
  page: PageGen,
  rect: PdfRect,
  dp: DataPoint,
  attrs: Record<string, string>,
  colorHex: string
): void {
  const min = parseFloat(attrs.min) || 0;
  const max = parseFloat(attrs.max) || 100;
  const value = Math.min(max, Math.max(min, dp.value));
  const fraction = (value - min) / (max - min || 1);

  const pad = 5;
  const labelH = 12;
  const cx = rect.left + rect.width / 2;
  const cy = rect.bottom - labelH - pad;
  const outerR = Math.min((rect.width - pad * 2) / 2, rect.height - labelH - pad * 2);
  const innerR = outerR * 0.5;

  const gaugeRect = new PdfRect(cx - outerR, cy - outerR, outerR * 2, outerR * 2);
  const innerRect = new PdfRect(cx - innerR, cy - innerR, innerR * 2, innerR * 2);

  // Background semicircle (180°→360°)
  page.drawPie(null, new PdfBrush(PdfColor.fromRgb(220, 220, 220)), gaugeRect, 180, 360);

  // Value fill arc
  const { r, g, b } = hexToRgb(colorHex);
  if (fraction > 0.005) {
    page.drawPie(
      null,
      new PdfBrush(PdfColor.fromRgb(r, g, b, 0.85)),
      gaugeRect,
      180,
      180 + fraction * 180
    );
  }

  // White inner circle to create ring effect
  page.drawEllipse(null, new PdfBrush(PdfColor.fromRgb(255, 255, 255)), innerRect);

  // Tick marks at 0%, 25%, 50%, 75%, 100%
  const tickPen = new PdfPen(PdfColor.fromRgb(100, 100, 100), 0.7);
  for (let t = 0; t <= 4; t++) {
    const ang = ((180 + (t / 4) * 180) * Math.PI) / 180;
    const r1 = outerR * 0.87,
      r2 = outerR * 0.97;
    page.drawLine(
      tickPen,
      new PdfPoint(cx + r1 * Math.cos(ang), cy + r1 * Math.sin(ang)),
      new PdfPoint(cx + r2 * Math.cos(ang), cy + r2 * Math.sin(ang))
    );
  }

  // Needle
  const needleAng = ((180 + fraction * 180) * Math.PI) / 180;
  page.drawLine(
    new PdfPen(PdfColor.fromRgb(50, 50, 50), 1.5),
    new PdfPoint(cx, cy),
    new PdfPoint(cx + outerR * 0.82 * Math.cos(needleAng), cy + outerR * 0.82 * Math.sin(needleAng))
  );

  // Pivot dot
  const pR = Math.max(2.5, innerR * 0.12);
  page.drawEllipse(
    null,
    new PdfBrush(PdfColor.fromRgb(50, 50, 50)),
    new PdfRect(cx - pR, cy - pR, pR * 2, pR * 2)
  );

  // Min/max labels at arc ends
  const lf = new PdfFont(PdfFont.FONT_NAME, 6);
  page.drawString(
    String(min),
    lf,
    PdfBrush.Black,
    null,
    new PdfRect(cx - outerR - 1, cy - 7, 14, 7),
    ETextAlign.CenterLeft
  );
  page.drawString(
    String(max),
    lf,
    PdfBrush.Black,
    null,
    new PdfRect(cx + outerR - 13, cy - 7, 14, 7),
    ETextAlign.CenterRight
  );

  // Value text inside ring
  const vf = new PdfFont(PdfFont.FONT_NAME, Math.min(8, innerR * 0.42));
  page.drawString(
    String(dp.value),
    vf,
    PdfBrush.Black,
    null,
    new PdfRect(cx - innerR * 0.65, cy - innerR * 0.6, innerR * 1.3, innerR * 0.5),
    ETextAlign.Center
  );

  // Name label below gauge
  if (dp.label) {
    page.drawString(
      dp.label,
      new PdfFont(PdfFont.FONT_NAME, 7),
      PdfBrush.Black,
      null,
      new PdfRect(rect.left + pad, cy + 2, rect.width - pad * 2, labelH),
      ETextAlign.Center
    );
  }
}

// ─── Candlestick (OHLC) ──────────────────────────────────────────────────────

function drawCandlestickChart(
  page: PageGen,
  rect: PdfRect,
  rows: MultiDataPoint[],
  headers: string[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const n = rows.length;
  if (n === 0) return;

  const allLows = rows.map((r) => r.values[2] ?? r.values[0] ?? 0);
  const allHighs = rows.map((r) => r.values[1] ?? r.values[0] ?? 0);
  const minY = Math.min(...allLows);
  const maxY = Math.max(...allHighs);
  const range = maxY - minY || 1;

  const axisX = rect.left + 22;
  const axisRight = rect.right - 5;
  const baselineY = rect.bottom - 14;
  const topY = rect.top + 6;
  const chartH = baselineY - topY;
  const chartW = axisRight - axisX;

  const paddedMin = minY - range * 0.1;
  const paddedMax = maxY + range * 0.1;
  const paddedRange = paddedMax - paddedMin;
  const toY = (v: number) => baselineY - ((v - paddedMin) / paddedRange) * chartH;

  // Axes
  const axisPen = new PdfPen(PdfColor.fromRgb(0, 0, 0), 1);
  page.drawLine(axisPen, new PdfPoint(axisX, topY), new PdfPoint(axisX, baselineY));
  page.drawLine(axisPen, new PdfPoint(axisX, baselineY), new PdfPoint(axisRight, baselineY));

  // Y-axis grid + labels (4 ticks)
  const labelFont = new PdfFont(PdfFont.FONT_NAME, 6);
  for (let t = 0; t <= 3; t++) {
    const v = paddedMin + (t / 3) * paddedRange;
    const ly = toY(v);
    page.drawLine(
      new PdfPen(PdfColor.fromRgb(180, 180, 180), 0.5),
      new PdfPoint(axisX, ly),
      new PdfPoint(axisRight, ly)
    );
    page.drawString(
      v.toFixed(1),
      labelFont,
      PdfBrush.Black,
      null,
      new PdfRect(rect.left, ly - 4, 20, 8),
      ETextAlign.CenterRight
    );
  }

  const spacingX = chartW / n;
  const candleW = spacingX * 0.6;
  const wickPen = new PdfPen(PdfColor.fromRgb(100, 100, 100), 0.8);

  for (let i = 0; i < n; i++) {
    const open = rows[i].values[0] ?? 0;
    const high = rows[i].values[1] ?? open;
    const low = rows[i].values[2] ?? open;
    const close = rows[i].values[3] ?? open;

    const cx = axisX + (i + 0.5) * spacingX;
    const highY = toY(high);
    const lowY = toY(low);
    const openY = toY(open);
    const closeY = toY(close);

    // Wick (high–low)
    page.drawLine(wickPen, new PdfPoint(cx, highY), new PdfPoint(cx, lowY));

    // Body
    const bodyTop = Math.min(openY, closeY);
    const bodyH = Math.max(1, Math.abs(closeY - openY));
    const {
      r: br,
      g: bg,
      b: bb,
    } = close > open
      ? { r: 34, g: 139, b: 34 } // green
      : close < open
        ? { r: 200, g: 40, b: 40 } // red
        : { r: 120, g: 120, b: 120 }; // gray (unchanged)
    page.drawRectangle(
      null,
      new PdfBrush(PdfColor.fromRgb(br, bg, bb)),
      new PdfRect(cx - candleW / 2, bodyTop, candleW, bodyH)
    );

    // X-axis date label
    if (rows[i].label) {
      page.drawString(
        rows[i].label,
        labelFont,
        PdfBrush.Black,
        null,
        new PdfRect(cx - spacingX / 2, baselineY + 1, spacingX, 8),
        ETextAlign.Center
      );
    }
  }
}

// ─── Catmull-Rom spline helpers ───────────────────────────────────────────────

function mirrorPoint(anchor: PdfPoint, pt: PdfPoint): PdfPoint {
  return new PdfPoint(2 * anchor.x - pt.x, 2 * anchor.y - pt.y);
}

function catmullRomToBezier(points: PdfPoint[]): PdfPoint[] {
  if (points.length < 2) return points;
  const result: PdfPoint[] = [points[0]];
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = i === 0 ? mirrorPoint(points[0], points[1]) : points[i - 1];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = i + 2 >= points.length ? mirrorPoint(points[i + 1], points[i]) : points[i + 2];
    result.push(
      new PdfPoint(p1.x + (p2.x - p0.x) / 6, p1.y + (p2.y - p0.y) / 6),
      new PdfPoint(p2.x - (p3.x - p1.x) / 6, p2.y - (p3.y - p1.y) / 6),
      new PdfPoint(p2.x, p2.y)
    );
  }
  return result;
}

function sampleBezierCurve(bezPts: PdfPoint[], stepsPerSeg: number): PdfPoint[] {
  // Format: [P0, C1, C2, P1, C1, C2, P2, ...]
  const result: PdfPoint[] = [];
  const segCount = (bezPts.length - 1) / 3;
  for (let seg = 0; seg < segCount; seg++) {
    const p0 = bezPts[seg * 3];
    const c1 = bezPts[seg * 3 + 1];
    const c2 = bezPts[seg * 3 + 2];
    const p1 = bezPts[seg * 3 + 3];
    for (let s = 0; s < stepsPerSeg; s++) {
      const t = s / stepsPerSeg,
        mt = 1 - t;
      result.push(
        new PdfPoint(
          mt * mt * mt * p0.x + 3 * mt * mt * t * c1.x + 3 * mt * t * t * c2.x + t * t * t * p1.x,
          mt * mt * mt * p0.y + 3 * mt * mt * t * c1.y + 3 * mt * t * t * c2.y + t * t * t * p1.y
        )
      );
    }
  }
  if (bezPts.length >= 1) result.push(bezPts[bezPts.length - 1]);
  return result;
}

function buildLinePoints(
  rect: PdfRect,
  data: DataPoint[]
): { pts: PdfPoint[]; axisX: number; baselineY: number; chartWidth: number } {
  const maxValue = Math.max(...data.map((d) => d.value));
  const minValue = Math.min(...data.map((d) => d.value));
  const valueRange = maxValue - minValue || 1;
  const chartWidth = rect.width - 25;
  const chartHeight = rect.height - 30;
  const baselineY = rect.bottom - 20;
  const axisX = rect.left + 15;
  const pts = data.map(
    (d, i) =>
      new PdfPoint(
        axisX + 5 + (i / (data.length - 1 || 1)) * chartWidth,
        baselineY - ((d.value - minValue) / valueRange) * chartHeight
      )
  );
  return { pts, axisX, baselineY, chartWidth };
}

// ─── Spline ───────────────────────────────────────────────────────────────────

function drawSplineChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const textColor = PdfColor.fromHex(palette.textColor);
  const outlineColor = palette.outlineColor ? PdfColor.fromHex(palette.outlineColor) : null;

  const { pts, axisX, baselineY, chartWidth } = buildLinePoints(rect, data);
  const axisPen = outlineColor
    ? new PdfPen(outlineColor, 1)
    : new PdfPen(PdfColor.fromRgb(0, 0, 0), 1);
  page.drawLine(axisPen, new PdfPoint(axisX, baselineY), new PdfPoint(rect.right - 5, baselineY));
  page.drawLine(axisPen, new PdfPoint(axisX, rect.top + 5), new PdfPoint(axisX, baselineY));

  if (pts.length >= 2) {
    const bezPts = catmullRomToBezier(pts);
    const { r, g, b } = hexToRgb(colors[0]);
    page.drawBeziers(new PdfPen(PdfColor.fromRgb(r, g, b), 2), null, bezPts);
    const dotBrush = new PdfBrush(PdfColor.fromRgb(r, g, b));
    for (const pt of pts) {
      page.drawEllipse(null, dotBrush, new PdfRect(pt.x - 2.5, pt.y - 2.5, 5, 5));
    }
  }

  const labelFont = new PdfFont(PdfFont.FONT_NAME, 7);
  for (let i = 0; i < data.length; i++) {
    const x = axisX + 5 + (i / (data.length - 1 || 1)) * chartWidth;
    page.drawString(
      data[i].label,
      labelFont,
      new PdfBrush(textColor),
      null,
      new PdfRect(x - 12, baselineY + 1, 24, 8),
      ETextAlign.Center
    );
  }
}

// ─── Smooth Area ──────────────────────────────────────────────────────────────

function drawSmoothAreaChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const textColor = PdfColor.fromHex(palette.textColor);
  const outlineColor = palette.outlineColor ? PdfColor.fromHex(palette.outlineColor) : null;

  const { pts, axisX, baselineY, chartWidth } = buildLinePoints(rect, data);
  const axisPen = outlineColor
    ? new PdfPen(outlineColor, 1)
    : new PdfPen(PdfColor.fromRgb(0, 0, 0), 1);
  page.drawLine(axisPen, new PdfPoint(axisX, baselineY), new PdfPoint(rect.right - 5, baselineY));
  page.drawLine(axisPen, new PdfPoint(axisX, rect.top + 5), new PdfPoint(axisX, baselineY));

  if (pts.length >= 2) {
    const bezPts = catmullRomToBezier(pts);
    const { r, g, b } = hexToRgb(colors[0]);

    // Sampled polygon for fill
    const sampled = sampleBezierCurve(bezPts, 10);
    const areaPoints: PdfPoint[] = [
      new PdfPoint(pts[0].x, baselineY),
      ...sampled,
      new PdfPoint(pts[pts.length - 1].x, baselineY),
    ];
    page.drawPolygon(null, new PdfBrush(PdfColor.fromRgb(r, g, b, 0.3)), areaPoints);

    // Spline outline on top
    page.drawBeziers(new PdfPen(PdfColor.fromRgb(r, g, b), 2), null, bezPts);

    const dotBrush = new PdfBrush(PdfColor.fromRgb(r, g, b));
    for (const pt of pts) {
      page.drawEllipse(null, dotBrush, new PdfRect(pt.x - 2.5, pt.y - 2.5, 5, 5));
    }
  }

  const labelFont = new PdfFont(PdfFont.FONT_NAME, 7);
  for (let i = 0; i < data.length; i++) {
    const x = axisX + 5 + (i / (data.length - 1 || 1)) * chartWidth;
    page.drawString(
      data[i].label,
      labelFont,
      new PdfBrush(textColor),
      null,
      new PdfRect(x - 12, baselineY + 1, 24, 8),
      ETextAlign.Center
    );
  }
}

// ─── Number Line ─────────────────────────────────────────────────────────────

function drawNumberLineChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const minVal =
    attrs.min !== undefined
      ? parseFloat(attrs.min)
      : Math.floor(Math.min(...data.map((d) => d.value)));
  const maxVal =
    attrs.max !== undefined
      ? parseFloat(attrs.max)
      : Math.ceil(Math.max(...data.map((d) => d.value)));
  const step = parseFloat(attrs.step) || 1;
  const range = maxVal - minVal || 1;

  const pad = 14;
  const lineY = rect.top + rect.height / 2;
  const lineX0 = rect.left + pad;
  const lineX1 = rect.right - pad;
  const lineW = lineX1 - lineX0;
  const toX = (v: number) => lineX0 + ((v - minVal) / range) * lineW;

  // Main axis line
  page.drawLine(
    new PdfPen(PdfColor.fromRgb(0, 0, 0), 1.2),
    new PdfPoint(lineX0, lineY),
    new PdfPoint(lineX1, lineY)
  );

  // Arrowheads at both ends
  const arr = 4;
  page.drawPolygon(null, PdfBrush.Black, [
    new PdfPoint(lineX1 + arr, lineY),
    new PdfPoint(lineX1 - arr / 2, lineY - arr / 2),
    new PdfPoint(lineX1 - arr / 2, lineY + arr / 2),
  ]);
  page.drawPolygon(null, PdfBrush.Black, [
    new PdfPoint(lineX0 - arr, lineY),
    new PdfPoint(lineX0 + arr / 2, lineY - arr / 2),
    new PdfPoint(lineX0 + arr / 2, lineY + arr / 2),
  ]);

  // Tick marks and integer labels
  const tickFont = new PdfFont(PdfFont.FONT_NAME, 6.5);
  for (let v = minVal; v <= maxVal + step * 0.01; v += step) {
    const tx = toX(v);
    if (tx < lineX0 - 1 || tx > lineX1 + 1) continue;
    const isInteger = Math.abs(v - Math.round(v)) < 0.001;
    const tickH = isInteger ? 5 : 3;
    page.drawLine(
      new PdfPen(PdfColor.fromRgb(0, 0, 0), 0.8),
      new PdfPoint(tx, lineY - tickH),
      new PdfPoint(tx, lineY + tickH)
    );
    if (isInteger) {
      page.drawString(
        String(Math.round(v)),
        tickFont,
        PdfBrush.Black,
        null,
        new PdfRect(tx - 8, lineY + 6, 16, 8),
        ETextAlign.Center
      );
    }
  }

  // Data point dots with labels (alternating above/below)
  const dotR = 4;
  const dotFont = new PdfFont(PdfFont.FONT_NAME, 7);
  for (let i = 0; i < data.length; i++) {
    const dx = toX(data[i].value);
    const { r, g, b } = hexToRgb(colors[i % colors.length]);
    page.drawEllipse(
      new PdfPen(
        PdfColor.fromRgb(Math.max(0, r - 40), Math.max(0, g - 40), Math.max(0, b - 40)),
        0.8
      ),
      new PdfBrush(PdfColor.fromRgb(r, g, b)),
      new PdfRect(dx - dotR, lineY - dotR, dotR * 2, dotR * 2)
    );
    if (data[i].label) {
      const above = i % 2 === 0;
      const labelY = above ? lineY - dotR - 10 : lineY + dotR + 2;
      page.drawString(
        data[i].label,
        dotFont,
        new PdfBrush(PdfColor.fromRgb(r, g, b)),
        null,
        new PdfRect(dx - 15, labelY, 30, 8),
        ETextAlign.Center
      );
    }
  }
}

// ─── Fraction Bar ─────────────────────────────────────────────────────────────

function drawFractionBarChart(
  page: PageGen,
  rect: PdfRect,
  rows: MultiDataPoint[],
  headers: string[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const n = rows.length;
  if (n === 0) return;

  const labelW = Math.min(35, rect.width * 0.25);
  const valueW = 18;
  const barAreaW = rect.width - labelW - valueW;
  const rowH = rect.height / n;
  const barH = Math.max(4, rowH * 0.6);
  const barGapY = (rowH - barH) / 2;

  const labelFont = new PdfFont(PdfFont.FONT_NAME, Math.min(8, rowH * 0.5));
  const valueFont = new PdfFont(PdfFont.FONT_NAME, 6.5);
  const borderPen = new PdfPen(PdfColor.fromRgb(150, 150, 150), 0.5);
  const emptyBrush = new PdfBrush(PdfColor.fromRgb(235, 235, 235));

  for (let i = 0; i < n; i++) {
    const num = Math.max(0, Math.round(rows[i].values[0] ?? 0));
    const den = Math.max(1, Math.round(rows[i].values[1] ?? 1));
    const rowY = rect.top + i * rowH;
    const barY = rowY + barGapY;

    page.drawString(
      rows[i].label,
      labelFont,
      PdfBrush.Black,
      null,
      new PdfRect(rect.left, rowY, labelW - 3, rowH),
      ETextAlign.CenterRight
    );

    const cellW = barAreaW / den;
    const barLeft = rect.left + labelW;
    const { r, g, b } = hexToRgb(colors[i % colors.length]);

    for (let j = 0; j < den; j++) {
      const cellX = barLeft + j * cellW;
      const filled = j < num;
      const brush = filled ? new PdfBrush(PdfColor.fromRgb(r, g, b, 0.85)) : emptyBrush;
      page.drawRectangle(borderPen, brush, new PdfRect(cellX + 0.5, barY, cellW - 1, barH));
    }

    page.drawString(
      `${num}/${den}`,
      valueFont,
      PdfBrush.Black,
      null,
      new PdfRect(barLeft + barAreaW + 2, rowY, valueW - 3, rowH),
      ETextAlign.CenterLeft
    );
  }
}

// ─── Pie Grid (Small Multiple Pies) ──────────────────────────────────────────

function drawPieGridChart(
  page: PageGen,
  rect: PdfRect,
  rows: MultiDataPoint[],
  headers: string[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const n = rows.length;
  if (n === 0) return;

  const legendH = 12;
  const chartAreaH = rect.height - legendH - 4;

  const cols = Math.min(n, 3);
  const rowCount = Math.ceil(n / cols);
  const cellW = rect.width / cols;
  const cellH = chartAreaH / rowCount;
  const titleH = 10;

  const titleFont = new PdfFont(PdfFont.FONT_NAME, 7);
  const pctFont = new PdfFont(PdfFont.FONT_NAME, 6);
  const outlineColor = palette.outlineColor ? PdfColor.fromHex(palette.outlineColor) : null;
  const pen = outlineColor ? new PdfPen(outlineColor, 0.5) : null;

  for (let i = 0; i < n; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const cellX = rect.left + col * cellW;
    const cellY = rect.top + row * cellH;

    const total = rows[i].values.reduce((s, v) => s + (v || 0), 0) || 1;
    const pieAreaH = cellH - titleH;
    const pieR = Math.min(cellW, pieAreaH) / 2 - 4;
    const cx = cellX + cellW / 2;
    const cy = cellY + titleH + pieAreaH / 2;

    // Title above pie
    page.drawString(
      rows[i].label,
      titleFont,
      PdfBrush.Black,
      null,
      new PdfRect(cellX, cellY, cellW, titleH),
      ETextAlign.Center
    );

    // Pie slices
    let startAngle = 0;
    for (let j = 0; j < rows[i].values.length; j++) {
      const v = rows[i].values[j] || 0;
      const sweep = (v / total) * 360;
      const brush = new PdfBrush(PdfColor.fromHex(colors[j % colors.length]));
      const arcPts = getArcPoints(cx, cy, pieR, startAngle, startAngle + sweep);
      if (arcPts.length > 2) {
        page.drawPolygon(pen, brush, [new PdfPoint(cx, cy), ...arcPts]);
      }
      if (sweep > 20) {
        const midAng = ((startAngle + sweep / 2) * Math.PI) / 180;
        const lx = cx + pieR * 0.6 * Math.cos(midAng);
        const ly = cy + pieR * 0.6 * Math.sin(midAng);
        const txtColor = isLightColor(colors[j % colors.length]) ? PdfBrush.Black : PdfBrush.White;
        page.drawString(
          `${Math.round((v / total) * 100)}%`,
          pctFont,
          txtColor,
          null,
          new PdfRect(lx - 8, ly - 4, 16, 8),
          ETextAlign.Center
        );
      }
      startAngle += sweep;
    }
  }

  // Shared legend
  const legendFont = new PdfFont(PdfFont.FONT_NAME, 7);
  const legendAreaY = rect.bottom - legendH;
  const dotR = 3;
  const slotW = rect.width / Math.max(headers.length, 1);
  for (let j = 0; j < headers.length; j++) {
    const lx = rect.left + j * slotW;
    const { r, g, b } = hexToRgb(colors[j % colors.length]);
    page.drawEllipse(
      null,
      new PdfBrush(PdfColor.fromRgb(r, g, b)),
      new PdfRect(lx + 1, legendAreaY + legendH / 2 - dotR, dotR * 2, dotR * 2)
    );
    page.drawString(
      headers[j],
      legendFont,
      PdfBrush.Black,
      null,
      new PdfRect(lx + dotR * 2 + 3, legendAreaY, slotW - dotR * 2 - 4, legendH),
      ETextAlign.CenterLeft
    );
  }
}

// ─── Violin Plot ──────────────────────────────────────────────────────────────

function drawViolinPlotChart(
  page: PageGen,
  rect: PdfRect,
  data: MultiDataPoint[],
  headers: string[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const textColor = PdfColor.fromHex(palette.textColor);
  const outlineColor = palette.outlineColor ? PdfColor.fromHex(palette.outlineColor) : null;

  // values: [min, q1, median, q3, max]
  const allVals = data.flatMap((d) => d.values.filter((v) => !isNaN(v)));
  const rawMin = Math.min(...allVals),
    rawMax = Math.max(...allVals);
  const pad10 = (rawMax - rawMin) * 0.1 || 1;
  const minAll = rawMin - pad10,
    maxAll = rawMax + pad10;
  const yRange = maxAll - minAll || 1;

  const axisX = rect.left + 15;
  const baselineY = rect.bottom - 18;
  const chartH = rect.height - 28;
  const colW = (rect.width - 20) / data.length;

  const axisPen = outlineColor
    ? new PdfPen(outlineColor, 1)
    : new PdfPen(PdfColor.fromRgb(0, 0, 0), 1);
  page.drawLine(axisPen, new PdfPoint(axisX, rect.top + 5), new PdfPoint(axisX, baselineY));
  page.drawLine(axisPen, new PdfPoint(axisX, baselineY), new PdfPoint(rect.right - 5, baselineY));

  const toY = (v: number) => baselineY - ((v - minAll) / yRange) * chartH;
  const labelFont = new PdfFont(PdfFont.FONT_NAME, 7);

  // Y-axis ticks
  const tickFont = new PdfFont(PdfFont.FONT_NAME, 6);
  const nTicks = 4;
  for (let t = 0; t <= nTicks; t++) {
    const tv = rawMin + (rawMax - rawMin) * (t / nTicks);
    const ty = toY(tv);
    page.drawLine(
      new PdfPen(PdfColor.fromRgb(180, 180, 180), 0.5),
      new PdfPoint(axisX, ty),
      new PdfPoint(rect.right - 5, ty)
    );
    page.drawString(
      String(Math.round(tv)),
      tickFont,
      PdfBrush.Black,
      null,
      new PdfRect(rect.left, ty - 4, 14, 8),
      ETextAlign.CenterRight
    );
  }

  for (let i = 0; i < data.length; i++) {
    const v = data[i].values;
    const minV = v[0] ?? 0,
      q1 = v[1] ?? 0,
      med = v[2] ?? 0,
      q3 = v[3] ?? 0,
      maxV = v[4] ?? 0;
    const iqr = q3 - q1 || 1;
    const cx = axisX + 5 + i * colW + colW / 2;
    const slotW = colW * 0.8;

    // Build violin shape points (Y-levels top to bottom = max … min)
    const spread = iqr * 0.2;
    const yLevels = [maxV, q3 + spread, q3, med, q1, q1 - spread, minV];
    const widths = [0, iqr * 0.25, iqr * 0.4, iqr * 0.5, iqr * 0.4, iqr * 0.25, 0];
    const maxW = slotW / 2;

    // Scale widths relative to iqr; clamp to slotW/2
    const wScale = maxW / (iqr * 0.5 || 1);
    const scaledW = widths.map((w) => Math.min(w * wScale, maxW));

    // Left-side then right-side points (top-to-bottom then bottom-to-top)
    const leftPts = yLevels.map((y, k) => new PdfPoint(cx - scaledW[k], toY(y)));
    const rightPts = [...yLevels.map((y, k) => new PdfPoint(cx + scaledW[k], toY(y)))].reverse();
    const allPts = [...leftPts, ...rightPts];

    const { r, g, b } = hexToRgb(colors[i % colors.length]);
    page.drawPolygon(
      new PdfPen(
        PdfColor.fromRgb(Math.max(0, r - 40), Math.max(0, g - 40), Math.max(0, b - 40)),
        0.8
      ),
      new PdfBrush(PdfColor.fromRgb(r, g, b, 0.4)),
      allPts
    );

    // Box-plot overlay
    const yMin = toY(minV),
      yQ1 = toY(q1),
      yMed = toY(med),
      yQ3 = toY(q3),
      yMax = toY(maxV);
    const boxW = slotW * 0.25;
    const whiskerPen = new PdfPen(PdfColor.fromRgb(60, 60, 60), 0.8);
    const medPen = new PdfPen(PdfColor.fromRgb(40, 40, 40), 1.5);

    page.drawLine(whiskerPen, new PdfPoint(cx, yMin), new PdfPoint(cx, yQ1));
    page.drawLine(whiskerPen, new PdfPoint(cx, yQ3), new PdfPoint(cx, yMax));
    page.drawLine(whiskerPen, new PdfPoint(cx - boxW / 2, yMin), new PdfPoint(cx + boxW / 2, yMin));
    page.drawLine(whiskerPen, new PdfPoint(cx - boxW / 2, yMax), new PdfPoint(cx + boxW / 2, yMax));
    page.drawRectangle(
      new PdfPen(PdfColor.fromRgb(60, 60, 60), 0.6),
      new PdfBrush(PdfColor.fromRgb(r, g, b, 0.6)),
      new PdfRect(cx - boxW / 2, yQ3, boxW, yQ1 - yQ3)
    );
    page.drawLine(medPen, new PdfPoint(cx - boxW / 2, yMed), new PdfPoint(cx + boxW / 2, yMed));

    page.drawString(
      data[i].label,
      labelFont,
      new PdfBrush(textColor),
      null,
      new PdfRect(cx - colW / 2, baselineY + 1, colW, 8),
      ETextAlign.Center
    );
  }
}

// ─── Dot Plot (Cleveland) ─────────────────────────────────────────────────────

function drawDotPlotChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const n = data.length;
  if (n === 0) return;

  const labelW = Math.min(40, rect.width * 0.3);
  const pad = 8;
  const plotX0 = rect.left + labelW + pad;
  const plotX1 = rect.right - pad;
  const plotW = plotX1 - plotX0;
  const axisY = rect.bottom - 14;
  const plotH = axisY - rect.top - 4;
  const rowH = plotH / n;

  const minVal = Math.min(...data.map((d) => d.value));
  const maxVal = Math.max(...data.map((d) => d.value));
  const rawRange = maxVal - minVal || 1;
  const xPad = rawRange * 0.1;
  const xMin = minVal - xPad,
    xMax = maxVal + xPad;
  const xRange = xMax - xMin;

  const toX = (v: number) => plotX0 + ((v - xMin) / xRange) * plotW;

  // Axis line
  const axisPen = new PdfPen(PdfColor.fromRgb(0, 0, 0), 1);
  page.drawLine(axisPen, new PdfPoint(plotX0, axisY), new PdfPoint(plotX1, axisY));

  // Axis ticks + labels
  const tickFont = new PdfFont(PdfFont.FONT_NAME, 6.5);
  const nTicks = Math.min(6, Math.ceil(xRange));
  for (let t = 0; t <= nTicks; t++) {
    const tv = xMin + (xRange * t) / nTicks;
    const tx = toX(tv);
    page.drawLine(
      new PdfPen(PdfColor.fromRgb(0, 0, 0), 0.8),
      new PdfPoint(tx, axisY),
      new PdfPoint(tx, axisY + 3)
    );
    page.drawString(
      String(Math.round(tv * 10) / 10),
      tickFont,
      PdfBrush.Black,
      null,
      new PdfRect(tx - 10, axisY + 3, 20, 8),
      ETextAlign.Center
    );
  }

  const labelFont = new PdfFont(PdfFont.FONT_NAME, 7.5);
  const dotR = 3.5;

  for (let i = 0; i < n; i++) {
    const rowY = rect.top + 4 + i * rowH + rowH / 2;
    const dx = toX(data[i].value);
    const { r, g, b } = hexToRgb(colors[i % colors.length]);

    // Guide line
    page.drawLine(
      new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.6),
      new PdfPoint(plotX0, rowY),
      new PdfPoint(plotX1, rowY)
    );

    // Category label
    page.drawString(
      data[i].label,
      labelFont,
      PdfBrush.Black,
      null,
      new PdfRect(rect.left, rowY - rowH / 2, labelW, rowH),
      ETextAlign.CenterRight
    );

    // Dot
    page.drawEllipse(
      new PdfPen(
        PdfColor.fromRgb(Math.max(0, r - 40), Math.max(0, g - 40), Math.max(0, b - 40)),
        0.8
      ),
      new PdfBrush(PdfColor.fromRgb(r, g, b)),
      new PdfRect(dx - dotR, rowY - dotR, dotR * 2, dotR * 2)
    );
  }
}

// ─── Ten Frame ────────────────────────────────────────────────────────────────

function drawTenFrameChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const n = data.length;
  if (n === 0) return;

  const labelH = 10;
  const gridCols = Math.min(n, 3);
  const gridRows = Math.ceil(n / gridCols);
  const cellW = rect.width / gridCols;
  const cellH = rect.height / gridRows;

  for (let i = 0; i < n; i++) {
    const col = i % gridCols;
    const row = Math.floor(i / gridCols);
    const fx = rect.left + col * cellW;
    const fy = rect.top + row * cellH;
    const count = Math.max(0, Math.min(10, Math.round(data[i].value)));
    const { r, g, b } = hexToRgb(colors[i % colors.length]);

    // Frame border area
    const frameW = cellW - 6;
    const frameH = cellH - labelH - 8;
    const frameX = fx + 3;
    const frameY = fy + 3;
    const frameR = new PdfRect(frameX, frameY, frameW, frameH);

    page.drawRectangle(
      new PdfPen(PdfColor.fromRgb(100, 100, 100), 1),
      new PdfBrush(PdfColor.fromRgb(255, 255, 255)),
      frameR
    );

    // 5×2 cells inside frame
    const cols5 = 5;
    const tcW = frameW / cols5;
    const tcH = frameH / 2;
    const ellipseMargin = 2;

    for (let cell = 0; cell < 10; cell++) {
      const tc = cell % cols5;
      const tr = Math.floor(cell / cols5);
      const ex = frameX + tc * tcW + ellipseMargin;
      const ey = frameY + tr * tcH + ellipseMargin;
      const ew = tcW - ellipseMargin * 2;
      const eh = tcH - ellipseMargin * 2;

      // Cell border
      page.drawRectangle(
        new PdfPen(PdfColor.fromRgb(180, 180, 180), 0.5),
        null,
        new PdfRect(frameX + tc * tcW, frameY + tr * tcH, tcW, tcH)
      );

      const filled = cell < count;
      if (filled) {
        page.drawEllipse(
          new PdfPen(
            PdfColor.fromRgb(Math.max(0, r - 40), Math.max(0, g - 40), Math.max(0, b - 40)),
            0.6
          ),
          new PdfBrush(PdfColor.fromRgb(r, g, b)),
          new PdfRect(ex, ey, ew, eh)
        );
      } else {
        page.drawEllipse(
          new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5),
          new PdfBrush(PdfColor.fromRgb(240, 240, 240)),
          new PdfRect(ex, ey, ew, eh)
        );
      }
    }

    // Label below frame
    if (data[i].label) {
      const lf = new PdfFont(PdfFont.FONT_NAME, 7.5);
      page.drawString(
        `${data[i].label} (${count})`,
        lf,
        PdfBrush.Black,
        null,
        new PdfRect(fx, frameY + frameH + 1, cellW, labelH),
        ETextAlign.Center
      );
    }
  }
}

// ─── Tally Chart ──────────────────────────────────────────────────────────────

function drawTallyChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const n = data.length;
  if (n === 0) return;

  const labelW = Math.min(40, rect.width * 0.3);
  const countW = 14;
  const tallyAreaX = rect.left + labelW + 2;
  const tallyAreaW = rect.width - labelW - countW - 4;
  const rowH = rect.height / n;
  const markH = Math.min(rowH * 0.65, 9);
  const groupW = markH * 3.5; // width of one tally group of 5
  const markGap = markH * 0.5;

  const labelFont = new PdfFont(PdfFont.FONT_NAME, Math.min(8.5, rowH * 0.55));
  const countFont = new PdfFont(PdfFont.FONT_NAME, Math.min(7.5, rowH * 0.5));
  const markPen = new PdfPen(PdfColor.fromRgb(30, 30, 30), 0.9);

  for (let i = 0; i < n; i++) {
    const count = Math.max(0, Math.round(data[i].value));
    const rowY = rect.top + i * rowH;
    const cy = rowY + rowH / 2;
    const markY = cy - markH / 2;
    const { r, g, b } = hexToRgb(colors[i % colors.length]);
    const colorPen = new PdfPen(PdfColor.fromRgb(r, g, b), 0.9);

    // Category label
    page.drawString(
      data[i].label,
      labelFont,
      PdfBrush.Black,
      null,
      new PdfRect(rect.left, rowY, labelW, rowH),
      ETextAlign.CenterRight
    );

    // Tally marks
    const fullGroups = Math.floor(count / 5);
    const remainder = count % 5;
    let px = tallyAreaX;

    for (let g = 0; g < fullGroups; g++) {
      // 4 vertical marks
      for (let k = 0; k < 4; k++) {
        const mx = px + k * (groupW / 4);
        page.drawLine(colorPen, new PdfPoint(mx, markY), new PdfPoint(mx, markY + markH));
      }
      // Diagonal slash across the group
      page.drawLine(
        colorPen,
        new PdfPoint(px - markGap * 0.3, markY + markH),
        new PdfPoint(px + groupW - markGap * 0.3, markY)
      );
      px += groupW + markGap;
    }
    for (let k = 0; k < remainder; k++) {
      const mx = px + k * (groupW / 4);
      page.drawLine(colorPen, new PdfPoint(mx, markY), new PdfPoint(mx, markY + markH));
    }

    // Count label on the right
    page.drawString(
      String(count),
      countFont,
      new PdfBrush(PdfColor.fromRgb(80, 80, 80)),
      null,
      new PdfRect(rect.right - countW, rowY, countW, rowH),
      ETextAlign.CenterLeft
    );
  }
}

// ─── Place Value ──────────────────────────────────────────────────────────────

function drawPlaceValueChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const n = data.length;
  if (n === 0) return;

  // Determine max place (how many columns needed)
  const maxNum = Math.max(...data.map((d) => Math.abs(Math.round(d.value))));
  let places: string[];
  if (maxNum >= 1000) places = ['Th', 'H', 'T', 'O'];
  else if (maxNum >= 100) places = ['H', 'T', 'O'];
  else places = ['T', 'O'];
  const nCols = places.length;

  const labelW = Math.min(35, rect.width * 0.25);
  const colAreaW = rect.width - labelW;
  const colW = colAreaW / nCols;
  const headerH = 12;
  const rowH = (rect.height - headerH) / n;

  const headerFont = new PdfFont(PdfFont.FONT_NAME, 7.5);
  const labelFont = new PdfFont(PdfFont.FONT_NAME, Math.min(8.5, rowH * 0.55));
  const digitFont = new PdfFont(PdfFont.FONT_NAME, Math.min(9, rowH * 0.6));

  const colColors = [
    PdfColor.fromRgb(255, 235, 200),
    PdfColor.fromRgb(200, 235, 255),
    PdfColor.fromRgb(220, 255, 200),
    PdfColor.fromRgb(255, 215, 215),
  ];
  const headerColor = PdfColor.fromRgb(100, 100, 100);
  const borderPen = new PdfPen(PdfColor.fromRgb(160, 160, 160), 0.5);

  // Column headers
  for (let c = 0; c < nCols; c++) {
    const cx = rect.left + labelW + c * colW;
    page.drawRectangle(
      borderPen,
      new PdfBrush(PdfColor.fromRgb(220, 220, 220)),
      new PdfRect(cx, rect.top, colW, headerH)
    );
    page.drawString(
      places[c],
      headerFont,
      new PdfBrush(headerColor),
      null,
      new PdfRect(cx, rect.top, colW, headerH),
      ETextAlign.Center
    );
  }

  for (let i = 0; i < n; i++) {
    const num = Math.abs(Math.round(data[i].value));
    const rowY = rect.top + headerH + i * rowH;

    // Row label
    page.drawString(
      data[i].label,
      labelFont,
      PdfBrush.Black,
      null,
      new PdfRect(rect.left, rowY, labelW - 2, rowH),
      ETextAlign.CenterRight
    );

    // Decompose number
    const digits: number[] = [];
    let remaining = num;
    for (let p = places.length - 1; p >= 0; p--) {
      const place = 10**p;
      digits[places.length - 1 - p] = Math.floor(remaining / place);
      remaining = remaining % place;
    }

    for (let c = 0; c < nCols; c++) {
      const cx = rect.left + labelW + c * colW;
      const bg = colColors[c % colColors.length];
      page.drawRectangle(borderPen, new PdfBrush(bg), new PdfRect(cx, rowY, colW, rowH));
      if (digits[c] > 0 || c === nCols - 1) {
        page.drawString(
          String(digits[c]),
          digitFont,
          PdfBrush.Black,
          null,
          new PdfRect(cx, rowY, colW, rowH),
          ETextAlign.Center
        );
      }
    }
  }
}

// ─── Fraction Circles ─────────────────────────────────────────────────────────

function drawFractionCirclesChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);
  const n = data.length;
  if (n === 0) return;

  const labelH = 12;
  const cols = Math.min(n, 4);
  const rows = Math.ceil(n / cols);
  const cellW = rect.width / cols;
  const cellH = rect.height / rows;

  const fractionRegex = /^(\d+)\/(\d+)$/;
  const labelFont = new PdfFont(PdfFont.FONT_NAME, 7.5);
  const outlineColor = palette.outlineColor ? PdfColor.fromHex(palette.outlineColor) : null;

  for (let i = 0; i < n; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const cx = rect.left + col * cellW + cellW / 2;
    const cy = rect.top + row * cellH + (cellH - labelH) / 2;
    const radius = Math.min(cellW, cellH - labelH) / 2 - 4;

    // Parse fraction from label
    const match = fractionRegex.exec(data[i].label.trim());
    let num: number, den: number;
    if (match) {
      num = parseInt(match[1]);
      den = parseInt(match[2]);
    } else {
      // Fallback: value = numerator, attrs.den = denominator
      num = Math.round(data[i].value);
      den = parseInt(attrs.den || '4');
    }
    den = Math.max(1, den);
    num = Math.max(0, Math.min(num, den));

    const { r, g, b } = hexToRgb(colors[i % colors.length]);
    const filledBrush = new PdfBrush(PdfColor.fromRgb(r, g, b, 0.85));
    const emptyBrush = new PdfBrush(PdfColor.fromRgb(235, 235, 235));
    const sectorPen = outlineColor
      ? new PdfPen(outlineColor, 0.5)
      : new PdfPen(PdfColor.fromRgb(120, 120, 120), 0.5);

    for (let s = 0; s < den; s++) {
      const startAngle = (s / den) * 360 - 90;
      const endAngle = ((s + 1) / den) * 360 - 90;
      const arcPts = getArcPoints(cx, cy, radius, startAngle, endAngle);
      const brush = s < num ? filledBrush : emptyBrush;
      page.drawPolygon(sectorPen, brush, [new PdfPoint(cx, cy), ...arcPts]);
    }

    // Fraction label
    const labelStr = match ? data[i].label : `${num}/${den}`;
    page.drawString(
      labelStr,
      labelFont,
      PdfBrush.Black,
      null,
      new PdfRect(rect.left + col * cellW, rect.top + row * cellH + cellH - labelH, cellW, labelH),
      ETextAlign.Center
    );
  }
}

// ─── Multiplication Grid ──────────────────────────────────────────────────────

function drawMultiplicationGridChart(
  page: PageGen,
  rect: PdfRect,
  data: DataPoint[],
  attrs: Record<string, string>,
  palette: Palette
): void {
  const colors = getPaletteColors(palette.name);

  // Axis ranges — xMin/xMax for columns, yMin/yMax for rows; fall back to from/to
  const xMin = parseInt(attrs.xMin ?? attrs.from ?? '1');
  const xMax = parseInt(attrs.xMax ?? attrs.to ?? '10');
  const yMin = parseInt(attrs.yMin ?? attrs.from ?? '1');
  const yMax = parseInt(attrs.yMax ?? attrs.to ?? '10');
  const nCols = xMax - xMin + 1;
  const nRows = yMax - yMin + 1;
  if (nCols <= 0 || nRows <= 0) return;

  // Cell value function — default x*y; override via fn attr e.g. "x + y"
  const fnStr = attrs.fn || 'x * y';
  let cellFn: (x: number, y: number) => number;
  try {
    // eslint-disable-next-line no-new-func
    cellFn = new Function('x', 'y', `return (${fnStr});`) as (x: number, y: number) => number;
  } catch {
    cellFn = (x, y) => x * y;
  }

  const topLeft = attrs.topLeft ?? fnStr;

  // Grid dimensions: +1 col and +1 row for the headers
  const gridW = nCols + 1;
  const gridH = nRows + 1;
  const cellW = rect.width / gridW;
  const cellH = rect.height / gridH;

  // Pre-compute all body values to size the font appropriately
  const allValues: string[] = [];
  for (let r = 0; r < nRows; r++) {
    for (let c = 0; c < nCols; c++) {
      allValues.push(String(cellFn(xMin + c, yMin + r)));
    }
  }
  const longestValue = allValues.reduce((a, b) => (a.length >= b.length ? a : b), '0');
  const headerFont = new PdfFont(PdfFont.FONT_NAME, Math.min(8, cellH * 0.55));
  const bodyFont = page.adjustFont(cellW - 2, [longestValue], headerFont);

  const headerBg = PdfColor.fromRgb(220, 220, 220);
  const diagBg = PdfColor.fromRgb(255, 240, 180);
  const bodyBg = PdfColor.fromRgb(248, 248, 248);
  const { r: pr, g: pg, b: pb } = hexToRgb(colors[0]);
  const accentBg = PdfColor.fromRgb(pr, pg, pb, 0.15);
  const borderPen = new PdfPen(PdfColor.fromRgb(180, 180, 180), 0.4);

  for (let row = 0; row < gridH; row++) {
    for (let col = 0; col < gridW; col++) {
      const cx = rect.left + col * cellW;
      const cy = rect.top + row * cellH;
      const cellRect = new PdfRect(cx, cy, cellW, cellH);

      let bg: PdfColor;
      let text: string;
      let brush: PdfBrush;

      if (row === 0 && col === 0) {
        bg = headerBg;
        text = topLeft;
        brush = PdfBrush.Black;
      } else if (row === 0) {
        bg = headerBg;
        text = String(xMin + col - 1);
        brush = PdfBrush.Black;
      } else if (col === 0) {
        bg = headerBg;
        text = String(yMin + row - 1);
        brush = PdfBrush.Black;
      } else {
        const x = xMin + col - 1;
        const y = yMin + row - 1;
        text = String(cellFn(x, y));
        bg = x === y ? diagBg : (row + col) % 2 === 0 ? bodyBg : accentBg;
        brush = PdfBrush.Black;
      }

      page.drawRectangle(borderPen, new PdfBrush(bg), cellRect);
      page.drawString(text, bodyFont, brush, null, cellRect, ETextAlign.Center);
    }
  }
}
